import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, Treemap,
} from 'recharts';
import {
  Package, Truck, TrendingUp, TrendingDown, Users, Search, Upload, FileSpreadsheet,
  RotateCcw, AlertTriangle, CheckCircle, Clock, ArrowUpDown, MapPin, ShoppingBag,
  ChevronUp, ChevronDown, IndianRupee, Download, Plus, Settings, Activity, Eye,
  Pencil, Trash2, Save, ChevronLeft, ChevronRight, ShieldCheck, ShieldOff, FileText, Tag, Store,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API, errorText, rollbackImportBatch } from '@/utils/api';
import CustomerReportDialog from '@/components/CustomerReportDialog';
import GoodsReturnReportDialog from '@/components/GoodsReturnReportDialog';
import ImportRetailerTagsDialog from '@/components/ImportRetailerTagsDialog';

const DIVISIONS = ["Arvind Tresca DTR", "Arvind Tresca WS", "GG DTR", "GG WS", "TAS"];
const GR_REASONS = [
  { value: "wrong_dispatch", label: "Wrong Dispatch" },
  { value: "defective", label: "Defective" },
  { value: "late_dispatch", label: "Late Dispatch" },
  { value: "didnt_like", label: "Didn't Like" },
  { value: "refused_delivery", label: "Refused at Delivery" },
  { value: "other", label: "Other" },
];
const GR_TYPES = [
  { value: "post_delivery", label: "Post Delivery Return" },
  { value: "refused_at_delivery", label: "Refused at Delivery" },
];
const PIE_COLORS = ['#818CF8', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#60A5FA', '#F472B6', '#2DD4BF'];
// Wider palette for the product chart, so 30 qualities stay tellable apart.
const PRODUCT_COLORS = [
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#EC4899', '#14B8A6',
  '#84CC16', '#F97316', '#06B6D4', '#A855F7', '#22C55E', '#EAB308', '#DC2626', '#0EA5E9',
  '#D946EF', '#65A30D', '#FB7185', '#2563EB', '#CA8A04', '#059669', '#7C3AED', '#E11D48',
];
const PRODUCT_CHART_TYPES = [
  { key: 'pie', label: 'Pie' }, { key: 'donut', label: 'Donut' }, { key: 'bars', label: 'Bars' }, { key: 'treemap', label: 'Treemap' },
];
const PRODUCT_TOP_CHOICES = [
  { key: 'auto', label: 'Auto' }, { key: '12', label: 'Top 12' }, { key: '20', label: 'Top 20' }, { key: '30', label: 'Top 30' }, { key: 'all', label: 'All' },
];
const readPref = (k, fallback) => { try { return localStorage.getItem(k) || fallback; } catch { return fallback; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode etc. */ } };

const fmtLakhs = (v) => {
  if (v == null || isNaN(v)) return '0';
  const lakhs = v / 100000;
  if (lakhs >= 100) return `${(lakhs / 100).toFixed(1)} Cr`;
  if (lakhs >= 1) return `${lakhs.toFixed(1)} L`;
  return `${Math.round(v).toLocaleString('en-IN')}`;
};
const fmtNum = (v) => (v != null ? Math.round(v).toLocaleString('en-IN') : '0');

// A line's quality name. When a merge in Settings → Fabric Rules files it
// under another name, the merged name shows and the name it was uploaded with
// is a hover hint (the server sends it as trade_name_uploaded).
const TradeName = ({ row, fallback = '-' }) => (row?.trade_name_uploaded ? (
  <span className="underline decoration-dotted decoration-slate-400 underline-offset-2 cursor-help"
    title={`Uploaded as: ${row.trade_name_uploaded}\nShown as ${row.trade_name} — merged in Settings → Fabric Rules`}>
    {row.trade_name || fallback}
  </span>
) : <>{row?.trade_name || fallback}</>);
const fmtDec = (v) => (v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 }) : '0');

// Effective discount % from the truth source (so_price and net_rate).
// Arvind SAP stores trade + other discounts as sequential multipliers,
// not additive — so summing the two percentages (25% + 9.33%) gives
// 34.3% which is wrong. The real effective discount is 32% as shown
// by (1 - net_rate / so_price) × 100 = (1 - 203.32/299) × 100.
//
// Returns null (display as '-') when:
//   - so_price is missing/zero, OR
//   - net_rate is missing/zero (Arvind often rolls the discount + net
//     value onto ONE line of a multi-line dispatch and leaves sister
//     lines with net_rate=0 — those aren't 100% discounted, they're
//     just unpriced rows for which the discount lives on a sibling), OR
//   - net_rate > so_price (no usable discount info).
const effectiveDiscPct = (so_price, net_rate) => {
  const so = Number(so_price || 0);
  const nr = Number(net_rate || 0);
  if (so <= 0) return null;
  if (nr <= 0) return null;  // unpriced row — discount lives on a sibling line
  if (nr > so) return null;
  return (1 - nr / so) * 100;
};

// "For" badge — who the wholesaler placed this line for. Order rows carry the
// tag themselves (end_customer_*); dispatch rows get it derived server-side
// from their matched order line(s), which is why the extra "shared" and
// "none" kinds exist only on dispatches. The vocabulary is fixed by the
// owner: Retailer name | Own shop | Split (N) | Shared | Not tagged | No order copy.
const ForBadge = ({ kind, name, split }) => {
  const pill = 'inline-flex items-center text-[9px] leading-none font-medium rounded px-1.5 py-0.5 whitespace-nowrap border';
  if (kind === 'retailer') {
    return <span className={`${pill} bg-sky-50 text-sky-700 border-sky-300`} title={name || ''}>{name || 'Retailer'}</span>;
  }
  if (kind === 'own') {
    return <span className={`${pill} bg-amber-50 text-amber-700 border-amber-300`}>Own shop</span>;
  }
  if (kind === 'split') {
    const parts = (split || []).map(s => `${s.name || (s.kind === 'own' ? 'Own shop' : 'Retailer')}: ${s.units}`).join(' · ');
    return <span className={`${pill} bg-violet-50 text-violet-700 border-violet-300`} title={parts}>{name || `Split (${(split || []).length})`}</span>;
  }
  if (kind === 'shared') {
    return <span className={`${pill} bg-white text-violet-700 border-violet-400`} title="This invoice line was consumed by order lines tagged to different retailers">Shared</span>;
  }
  if (kind === 'none') {
    return <span className="text-[10px] text-slate-400" title="Not matched to an order copy, so there is no retailer to follow">No order copy</span>;
  }
  return <span className="text-[10px] text-slate-400">Not tagged</span>;
};

// Wholesale divisions are the only ones with retailer ("For") tagging — the
// same rule the server uses (_customer_type_for_division).
const isWsDivision = (d) => {
  const u = String(d || '').toUpperCase();
  return u.includes(' WS') || u.endsWith('WS');
};
// Distinct store names behind a customer's lines, for the group header.
// Where the customer is, next to the legal name: each line's own location
// (store or SAP code, server-derived), else the code's label from the
// locations map (rows such as goods returns carry no label of their own).
const normCodeKey = (c) => String(c || '').trim().replace(/\.0$/, '').replace(/^0+/, '');
const locationSummary = (lines, locMap) => {
  const names = [];
  (lines || []).forEach(l => {
    const lbl = l.location
      || (locMap && locMap[`${l.division || ''}|${normCodeKey(l.customer_code)}`])
      || '';
    if (lbl && !names.includes(lbl)) names.push(lbl);
  });
  if (!names.length) return '';
  return names.length <= 3 ? names.join(' · ') : `${names.slice(0, 3).join(' · ')} +${names.length - 3}`;
};

export default function OrdersDispatch() {
  const { user, selectedDivision } = useOutletContext();
  // "division|code" -> location label, for rows that carry none of their own.
  const [locMap, setLocMap] = useState({});
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/customers/locations`, { withCredentials: true })
      .then(res => { if (alive) setLocMap(res.data?.by_code || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Dispatches dated after their customer was blocked, labelled on every row.
  const [blockedDispatchIds, setBlockedDispatchIds] = useState(() => new Map());
  useEffect(() => {
    axios.get(`${API}/analytics/blocked-dispatch-alerts`, { withCredentials: true })
      .then(res => setBlockedDispatchIds(new Map(Object.entries(res.data?.flagged || {}).map(
        ([id, v]) => [id, `Dispatched after this customer was blocked on ${v.blocked_since}${v.reason ? ` — ${v.reason}` : ''}`]))))
      .catch(() => setBlockedDispatchIds(new Map()));
  }, []);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Filters
  const [filterDivision, setFilterDivision] = useState(selectedDivision || 'all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  // "For" filter on the Orders / Dispatches tabs — only meaningful once a
  // customer is picked, because retailers hang off one wholesaler profile.
  // Declared here (not with the tag-dialog state further down) because the
  // pagination-reset effect below reads it in its deps array on first render.
  const [filterEndCustomer, setFilterEndCustomer] = useState('all'); // 'all' | 'own' | 'untagged' | retailer_id
  const [filterCustomerIsWs, setFilterCustomerIsWs] = useState(false);  // "For" filter only for a wholesaler
  // "For" column: wholesale divisions only (a TAS store never tags retailers).
  const showForCol = !filterDivision || filterDivision === 'all' || isWsDivision(filterDivision);
  const [filterRetailers, setFilterRetailers] = useState([]);
  // "Store" filter — ship-to locations under the selected customer's SAP
  // code. Same rules as "For": needs one customer, and declared here for
  // the same TDZ reason. Values: 'all' | 'head' | 'none' | ship_to_code.
  const [filterStore, setFilterStore] = useState('all');
  const [filterStores, setFilterStores] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const fetchIdRef = useRef(0); // prevents stale API responses from overwriting newer ones
  const pieChartRef = useRef(null); // for scrolling to pie chart on state selection

  // Debounce search - only trigger API after user stops typing for 800ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(globalSearch), 800);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  // Reset list pagination whenever a dropdown filter changes — otherwise being
  // on (say) page 4 then switching to a division with fewer rows lands on an
  // empty page that reads as a false "No records found".
  useEffect(() => {
    setOrdersPage(0);
    setDispatchesPage(0);
  }, [filterDivision, filterMonth, filterState, filterCustomer, filterEndCustomer, filterStore]);

  // Import state
  const [importType, setImportType] = useState('dispatches_gg_pdf');
  const [importDivision, setImportDivision] = useState('');
  const [importFile, setImportFile] = useState(null);  // For backward compat
  const [importFiles, setImportFiles] = useState([]);
  // Order-upload metadata (order imports only): who took the order + where.
  const [importTaker, setImportTaker] = useState('');
  // "For" picker during an order import: { data, fileName, resolve } while open.
  const [retailerPrompt, setRetailerPrompt] = useState(null);
  const [importPlace, setImportPlace] = useState('');
  const [orderTakers, setOrderTakers] = useState([]);
  const [orderPlaces, setOrderPlaces] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Format-mismatch diff surfaced when an upload is blocked by the format lock
  const [formatMismatch, setFormatMismatch] = useState(null);
  // Duplicate-order guard: when an uploaded file contains order numbers that
  // already exist, the server imports NOTHING and returns the clash list —
  // this holds it (plus the file + upload settings) for the decision dialog.
  const [dupConflict, setDupConflict] = useState(null);

  // GR Dialog
  const [grDialogOpen, setGrDialogOpen] = useState(false);
  const [grDispatch, setGrDispatch] = useState(null);
  const [grUnits, setGrUnits] = useState(1);
  const [grReason, setGrReason] = useState('');
  const [grType, setGrType] = useState('post_delivery');
  const [grRemarks, setGrRemarks] = useState('');
  const [grSubmitting, setGrSubmitting] = useState(false);

  // Certify Missing-Order Dispatch Dialog
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [certifyTarget, setCertifyTarget] = useState(null); // single-row target — null when in bulk mode
  const [certifyReason, setCertifyReason] = useState('');
  const [certifyNote, setCertifyNote] = useState('');
  const [certifySubmitting, setCertifySubmitting] = useState(false);
  // Customer Report dialog (period-scoped PDF/Excel for one customer)
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { customer_code, name, dateKey }

  // Bulk selection for certify — Set of dispatch_ids that are checked
  const [selectedForCertify, setSelectedForCertify] = useState(() => new Set());
  const [certifyBulkMode, setCertifyBulkMode] = useState(false); // true when dialog opened from "Certify Selected"
  const [bulkCertifyTargets, setBulkCertifyTargets] = useState([]); // snapshot of dispatch rows being certified in bulk

  // Retailer tagging ("For") on order lines — Set of order_line_ids that are
  // checked on the Orders tab. Every role may select; the server decides who
  // may actually tag, so no role gate here.
  const [selectedForTag, setSelectedForTag] = useState(() => new Set());
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagKind, setTagKind] = useState('own'); // 'own' | 'retailer' | 'clear'
  const [tagRetailerId, setTagRetailerId] = useState('');
  const [tagRetailers, setTagRetailers] = useState([]); // active retailers of the selected customer
  const [tagRetailersLoading, setTagRetailersLoading] = useState(false);
  const [tagNewRetailerOpen, setTagNewRetailerOpen] = useState(false);
  const [tagNewRetailerName, setTagNewRetailerName] = useState('');
  const [tagAddingRetailer, setTagAddingRetailer] = useState(false);
  const [tagSplitOn, setTagSplitOn] = useState(false);
  const [tagSplitRows, setTagSplitRows] = useState([]); // [{ who: 'own' | retailer_id, units: '' }]
  const [tagSubmitting, setTagSubmitting] = useState(false);
  // Editing a tag by clicking a line's "For" cell: the dialog opens on the
  // whole order (or just that line) without touching the checkbox selection.
  const [tagFromCell, setTagFromCell] = useState(null); // { lineId, orderNo, division, orderIds }
  const [tagScope, setTagScope] = useState('order');     // 'order' | 'line'
  const [pendingTagOpen, setPendingTagOpen] = useState(false);

  // GR Status Update Dialog
  const [grStatusDialogOpen, setGrStatusDialogOpen] = useState(false);
  const [grStatusItem, setGrStatusItem] = useState(null);
  const [grNewStatus, setGrNewStatus] = useState('');
  const [grStatusRemarks, setGrStatusRemarks] = useState('');
  const [grCreditNoteNumber, setGrCreditNoteNumber] = useState('');
  const [grCreditNoteAmount, setGrCreditNoteAmount] = useState(0);

  // Table sort (customers tab)
  const [sortField, setSortField] = useState('total_value');
  const [sortDir, setSortDir] = useState('desc');

  // Product tab sort/search/view
  const [productSort, setProductSort] = useState('trade_name');
  const [productSortDir, setProductSortDir] = useState('asc');
  const [productSearch, setProductSearch] = useState('');
  const [productView, setProductView] = useState('fy'); // 'all' | 'fy' | 'region'
  const [expandedRegionProduct, setExpandedRegionProduct] = useState(null); // state name or null
  const [pieRegion, setPieRegion] = useState(null); // selected region for pie chart in region view
  const [piePeriod, setPiePeriod] = useState('fy'); // 'fy' | 'q1' | 'q2' | 'q3' | 'q4' | '2024-04' etc.
  // Product chart preferences — remembered per browser.
  const [productChartType, setProductChartType] = useState(() => readPref('od_product_chart', 'pie'));
  const [productTopChoice, setProductTopChoice] = useState(() => readPref('od_product_top', 'auto'));
  const chooseProductChart = (t) => { setProductChartType(t); writePref('od_product_chart', t); };
  const chooseProductTop = (t) => { setProductTopChoice(t); writePref('od_product_top', t); };
  const [expandedFYs, setExpandedFYs] = useState({});

  // Customers tab view
  const [expandedCustFYs, setExpandedCustFYs] = useState({});
  const [expandedMonthlyCustProduct, setExpandedMonthlyCustProduct] = useState(null); // "month|customer_code" or null

  // Dispatches tab view
  const [dispatchView, setDispatchView] = useState('all'); // 'all' | 'fy'
  const [expandedDispFYs, setExpandedDispFYs] = useState({});
  const [expandedDispMonths, setExpandedDispMonths] = useState({}); // monthKey → { loading, data }

  // Orders tab view
  const [orderView, setOrderView] = useState('all'); // 'all' | 'fy'
  const [expandedOrdFYs, setExpandedOrdFYs] = useState({});
  const [expandedOrdMonths, setExpandedOrdMonths] = useState({}); // monthKey → { loading, data }

  // GR list
  const [grList, setGrList] = useState([]);
  const [loadingGr, setLoadingGr] = useState(false);
  const [grSearch, setGrSearch] = useState('');
  const [collapsedReturnDates, setCollapsedReturnDates] = useState({});
  const [grReportOpen, setGrReportOpen] = useState(false);
  const [grReportTarget, setGrReportTarget] = useState(null); // { customer_code, name, dateKey }

  // Manual order form
  const [manualOrderOpen, setManualOrderOpen] = useState(false);
  // "For" on a manually entered wholesaler order: '' (not tagged) | 'own' | 'ret:<id>' | 'new'
  const [manualFor, setManualFor] = useState('');
  const [manualForName, setManualForName] = useState('');
  const [manualRetailers, setManualRetailers] = useState([]);
  // "Store" on a manually entered TAS order: '' (not specified) | ship_to_code.
  // Options come from the customer's store master once the code resolves.
  const [manualShipTo, setManualShipTo] = useState('');
  const [manualStores, setManualStores] = useState([]);
  const [manualOrder, setManualOrder] = useState({
    order_no: '', division: '', customer_code: '', shop_name: '', trade_name: '',
    product_code: '', units_ordered: 1, order_date: new Date().toISOString().slice(0, 10),
    state: '', city: '', category: '', ex_mill_price: 0,
    discount_pct: 0, remark: '',
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [editOrderMode, setEditOrderMode] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);

  // Bulk discount state
  const [bulkDiscountOpen, setBulkDiscountOpen] = useState(false);
  const [bulkDiscountOrderNo, setBulkDiscountOrderNo] = useState('');
  const [bulkDiscountDivision, setBulkDiscountDivision] = useState('');
  const [bulkDiscountPct, setBulkDiscountPct] = useState(0);
  const [bulkDiscountSubmitting, setBulkDiscountSubmitting] = useState(false);
  const [bulkDiscountBaseInfo, setBulkDiscountBaseInfo] = useState({ base_discount_pct: 0, customer_code: '', shop_name: '' });
  // Customer-level discount picker: one button per customer opens this, listing
  // that customer's orders so the user selects which get the discount.
  const [custDiscount, setCustDiscount] = useState(null); // { name, code, division, orders:[{order_no, units, lines}] } | null
  const [custDiscountSel, setCustDiscountSel] = useState({}); // order_no -> bool
  const [custDiscountPct, setCustDiscountPct] = useState(0);
  const [custDiscountBase, setCustDiscountBase] = useState(0);
  const [custDiscountBusy, setCustDiscountBusy] = useState(false);

  // Orders table state
  const [ordersTableData, setOrdersTableData] = useState([]);
  const [ordersTableTotal, setOrdersTableTotal] = useState(0);
  const [ordersTableLoading, setOrdersTableLoading] = useState(false);
  // ordersSearch removed — using debouncedSearch for all API calls
  const [ordersPage, setOrdersPage] = useState(0);
  const ordersPageSize = 500;

  // Dispatches table state
  const [dispatchesTableData, setDispatchesTableData] = useState([]);
  const [dispatchesTableTotal, setDispatchesTableTotal] = useState(0);
  const [dispatchesTableLoading, setDispatchesTableLoading] = useState(false);
  // dispatchesSearch removed — using debouncedSearch for all API calls
  const [dispatchesPage, setDispatchesPage] = useState(0);
  const dispatchesPageSize = 500;
  // Filter toggle for "Missing Orders" — show only dispatches that have no
  // matching order copy on file (order_match_status === "unmatched").
  const [showMissingOrdersOnly, setShowMissingOrdersOnly] = useState(false);

  // A tag selection only makes sense against the rows on screen — drop it
  // whenever the tab, page or any filter swaps those rows out from under it.
  useEffect(() => {
    setSelectedForTag(new Set());
  }, [activeTab, ordersPage, filterDivision, filterMonth, filterState, filterCustomer, filterEndCustomer, filterStore, debouncedSearch]);

  // Retailers and stores belong to one customer, so the "For" and "Store"
  // filters reset (and their option lists reload) whenever the customer
  // filter changes.
  useEffect(() => {
    setFilterEndCustomer('all');
    setFilterStore('all');
    setFilterCustomerIsWs(false);
    if (!filterCustomer || filterCustomer === 'all' || filterCustomer.startsWith('__name__')) {
      setFilterRetailers([]);
      setFilterStores([]);
      return;
    }
    let cancelled = false;
    const params = {};
    if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
    axios.get(`${API}/customers/${encodeURIComponent(filterCustomer)}/retailers`, { params, withCredentials: true })
      .then(res => {
        if (cancelled) return;
        setFilterRetailers((res.data?.retailers || []).filter(r => r.active !== false));
        setFilterCustomerIsWs(!!res.data?.is_wholesaler);
      })
      .catch(() => { if (!cancelled) { setFilterRetailers([]); setFilterCustomerIsWs(false); } });
    axios.get(`${API}/customers/${encodeURIComponent(filterCustomer)}/stores`, { params, withCredentials: true })
      .then(res => { if (!cancelled) setFilterStores(res.data?.stores || []); })
      .catch(() => { if (!cancelled) setFilterStores([]); });
    return () => { cancelled = true; };
  }, [filterCustomer, filterDivision]);

  // Manual dispatch form
  const [manualDispatchOpen, setManualDispatchOpen] = useState(false);
  const [editDispatchMode, setEditDispatchMode] = useState(false);
  const [editDispatchId, setEditDispatchId] = useState(null);
  const [manualDispatch, setManualDispatch] = useState({
    division: '', plant_invoice: '', delivery_date: new Date().toISOString().slice(0, 10),
    customer_code: '', customer_name: '', po_no: '', po_base: '',
    trade_name: '', product_code: '', customer_reference: '', batch_no: '',
    batch_qty_metres: 0, no_of_pieces: 1, so_price: 0, net_rate: 0,
    net_value: 0, trade_discount_pct: 0, other_discount_pct: 0,
    state: '', city: '', remarks: '',
  });
  const [dispatchSubmitting, setDispatchSubmitting] = useState(false);

  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'order'|'dispatch', item: {...} }
  const [deleting, setDeleting] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);
  // Dispatches-tab export: include goods-return credit notes (ECR/RCR) as
  // negative lines? Default OFF — clean sales, matching the on-screen list.
  const [exportIncludeCN, setExportIncludeCN] = useState(false);

  // Collapsed date groups state
  const [collapsedDates, setCollapsedDates] = useState({});
  const [collapsedOrderDates, setCollapsedOrderDates] = useState({});

  // Import history
  const [importHistory, setImportHistory] = useState([]);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);

  // Rollback state
  const [rollbackLoading, setRollbackLoading] = useState(null); // batch_id being rolled back
  const [reconciling, setReconciling] = useState(false); // orders ↔ dispatches reconcile

  // Mix authorisations (informal bale orders from email/WhatsApp)
  const [mixAuths, setMixAuths] = useState([]);
  const [mixSummary, setMixSummary] = useState({ total: 0, open_bales: 0, fulfilled: 0, cancelled: 0, expired: 0 });
  const [mixLoading, setMixLoading] = useState(false);
  const [mixStatusFilter, setMixStatusFilter] = useState('all');
  const [mixDialogOpen, setMixDialogOpen] = useState(false);
  const [savingMixAuth, setSavingMixAuth] = useState(false);
  const [mixForm, setMixForm] = useState({
    division: '', customer_code: '', fabric_type: 'shirting',
    bales_authorised: 1, flat_rate: 0,
    authorised_on: new Date().toISOString().slice(0, 10),
    expires_on: '', // auto-derived from authorised_on when blank
    source: 'whatsapp', reference_text: '',
  });

  // Mix dispatches that the auto-reconciler couldn't attribute to a fabric
  // type (Arvind's DTR template often says just "MIX DISPATCH" with no
  // shirting/suiting keyword, and the customer may have no open auth or
  // auths of multiple fabric types simultaneously). Admin/accounts pick
  // the fabric manually so the row flows into Mix Bale Shirting / Suiting
  // / Combo Pack revenue buckets.
  const [mixNeedsFabric, setMixNeedsFabric] = useState([]);
  const [mixFabricDialogOpen, setMixFabricDialogOpen] = useState(false);
  const [mixFabricChoices, setMixFabricChoices] = useState({}); // {dispatch_id: "shirting" | "suiting" | "combo_pack"}
  const [mixFabricSaving, setMixFabricSaving] = useState(false);

  // Customer directory for manual order/dispatch entry — locks the link
  // to customer_code so typos in the name can't create phantom customers.
  const [customerOptions, setCustomerOptions] = useState([]);
  // Distinct categories from existing orders — locks manual-order Category
  // entry so caps / spelling variants don't create parallel categories.
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Purge dispatches dialog
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeDivision, setPurgeDivision] = useState('all');
  const [purgeCount, setPurgeCount] = useState(0);
  const [purging, setPurging] = useState(false);

  // Reconciliation state
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconVarianceFilter, setReconVarianceFilter] = useState('all');
  const [reconSeverityFilter, setReconSeverityFilter] = useState('all');
  const [reconSearchTerm, setReconSearchTerm] = useState('');

  // Variance detail dialog
  const [varianceDialogOpen, setVarianceDialogOpen] = useState(false);
  const [selectedVariance, setSelectedVariance] = useState(null);

  // Tolerance settings dialog
  const [toleranceDialogOpen, setToleranceDialogOpen] = useState(false);
  const [toleranceForm, setToleranceForm] = useState({
    quantity_tolerance_pct: 5.0,
    price_tolerance_pct: 2.0,
    aging_warning_days: 30,
    aging_critical_days: 60,
    delivery_grace_days: 7,
    store_aware_matching: true,
  });
  const [toleranceSaving, setToleranceSaving] = useState(false);
  const [reconRunning, setReconRunning] = useState(false);

  const fetchData = useCallback(async () => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterState && filterState !== 'all') params.state = filterState;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const res = await axios.get(`${API}/analytics/orders-dispatch`, {
        params,
        withCredentials: true,
      });
      // Only update if this is still the latest request (prevents stale "g" overwriting "geeta")
      if (myId === fetchIdRef.current) {
        setData(res.data);
      }
    } catch (err) {
      if (myId === fetchIdRef.current) {
        console.error('Failed to fetch analytics:', err);
        toast.error('Failed to load analytics');
      }
    } finally {
      if (myId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [filterDivision, filterMonth, filterState, filterCustomer, debouncedSearch]);

  const grFetchIdRef = useRef(0); // stale-response guard (same pattern as fetchIdRef)
  const fetchGrList = useCallback(async () => {
    const myId = ++grFetchIdRef.current;
    setLoadingGr(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      const res = await axios.get(`${API}/goods-returns`, { params, withCredentials: true });
      if (myId === grFetchIdRef.current) setGrList(res.data.goods_returns || []);
    } catch (err) {
      console.error('Failed to fetch GR list:', err);
      if (myId === grFetchIdRef.current) toast.error('Could not load goods returns.');
    } finally {
      if (myId === grFetchIdRef.current) setLoadingGr(false);
    }
  }, [filterDivision, filterCustomer, filterMonth]);

  useEffect(() => {
    setFilterDivision(selectedDivision || 'all');
  }, [selectedDivision]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Order-upload dropdown options (order taker + place of order). Fetched
  // INDEPENDENTLY so a failure in one never blanks the other.
  useEffect(() => {
    axios.get(`${API}/order-takers`, { withCredentials: true })
      .then(t => setOrderTakers([...(t.data?.order_takers || []), ...(t.data?.company_takers || [])]))
      .catch(e => { console.error('Failed to load order takers', e); toast.error('Could not load the order-taker list.'); });
    axios.get(`${API}/config/order-places`, { withCredentials: true })
      .then(p => setOrderPlaces(p.data?.order_places || []))
      .catch(e => { console.error('Failed to load order places', e); toast.error('Could not load the order-place list.'); });
  }, []);

  // Reconciliation data fetcher (must be declared before the useEffect that references it)
  const reconFetchIdRef = useRef(0); // stale-response guard
  const [reconError, setReconError] = useState('');
  const fetchReconData = useCallback(async () => {
    const myId = ++reconFetchIdRef.current;
    setReconLoading(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterState && filterState !== 'all') params.state = filterState;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (reconVarianceFilter !== 'all') params.variance_type = reconVarianceFilter;
      if (reconSeverityFilter !== 'all') params.severity = reconSeverityFilter;
      const res = await axios.get(`${API}/analytics/reconciliation`, { params, withCredentials: true });
      if (myId !== reconFetchIdRef.current) return; // a newer fetch superseded this one
      setReconError('');
      setReconData(res.data);
      if ((res.data.section_errors || []).length) {
        toast.warning(`Some reconciliation sections failed: ${res.data.section_errors.join(' · ')}`);
      }
      if (res.data.tolerance_settings) {
        setToleranceForm(res.data.tolerance_settings);
      }
    } catch (err) {
      console.error('Failed to fetch reconciliation data:', err);
      if (myId === reconFetchIdRef.current) {
        const msg = errorText(err) || err?.message || 'unknown error';
        setReconError(msg);
        toast.error(`Failed to load reconciliation data: ${msg}`);
      }
    } finally {
      if (myId === reconFetchIdRef.current) setReconLoading(false);
    }
  }, [filterDivision, filterMonth, filterState, filterCustomer, reconVarianceFilter, reconSeverityFilter]);

  useEffect(() => {
    if (activeTab === 'returns') fetchGrList();
    if (activeTab === 'reconciliation') fetchReconData();
  }, [activeTab, fetchGrList, fetchReconData]);

  // Save tolerance settings
  const handleSaveTolerances = async () => {
    setToleranceSaving(true);
    try {
      await axios.post(`${API}/config/reconciliation-settings`, toleranceForm, { withCredentials: true });
      toast.success('Tolerance settings saved');
      setToleranceDialogOpen(false);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save settings');
    } finally {
      setToleranceSaving(false);
    }
  };

  // Run reconciliation
  const handleRunRecon = async () => {
    setReconRunning(true);
    try {
      const body = {};
      if (filterDivision && filterDivision !== 'all') body.division = filterDivision;
      const res = await axios.post(`${API}/reconciliation/run`, body, { withCredentials: true });
      const jobId = res.data.job_id;
      if (!jobId) {
        toast.success(res.data.message);
      } else {
        // Runs in the background on the server (a few minutes on all
        // divisions); poll until it finishes instead of holding a request open.
        toast.message(res.data.message || 'Reconciliation started');
        const started = Date.now();
        let job = null;
        while (Date.now() - started < 45 * 60 * 1000) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            job = (await axios.get(`${API}/reconciliation/run/${jobId}`, { withCredentials: true })).data;
          } catch { continue; } // a blip while polling is not a failure
          if (job.status !== 'running') break;
        }
        if (job?.status === 'done') toast.success(job.result?.message || 'Reconciliation complete');
        else if (job?.status === 'failed') toast.error(`Reconciliation failed: ${job.error || 'unknown error'}`);
        else toast.warning('Reconciliation is still running on the server — check back in a few minutes.');
      }
      fetchReconData();
      fetchData();
    } catch (err) {
      toast.error(`Reconciliation failed: ${errorText(err) || err?.message || 'unknown error'}`);
    } finally {
      setReconRunning(false);
    }
  };

  // Order imports ask "who is each wholesaler order for?" before anything is
  // written: the server answers status 'retailer_tags_needed' with those
  // orders, the picker collects the answer, and the same file is re-sent with
  // it. Cancelling the picker cancels that file's import.
  const askRetailerTags = (data, fileName) => new Promise(resolve => setRetailerPrompt({ data, fileName, resolve }));
  const finishRetailerPrompt = (tags) => {
    const p = retailerPrompt;
    setRetailerPrompt(null);
    if (p) p.resolve(tags);
  };
  const postOrderImport = async (endpoint, makeForm, fileName) => {
    const send = (extra) => {
      const fd = makeForm();
      fd.append('ask_retailers', '1');
      Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v));
      return axios.post(`${API}${endpoint}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
    };
    let res = await send();
    if (res.data?.status === 'retailer_tags_needed') {
      const tags = await askRetailerTags(res.data, fileName);
      if (!tags) return { data: { status: 'cancelled', message: `${fileName}: import cancelled — nothing was imported` } };
      res = await send({ retailer_tags: JSON.stringify(tags) });
    }
    return res;
  };

  // Import handler - supports multiple files
  const handleImport = async () => {
    const files = importFiles.length > 0 ? importFiles : (importFile ? [importFile] : []);
    if (files.length === 0 || !importDivision) {
      toast.error('Select file(s) and division');
      return;
    }
    setUploading(true);
    const endpoint = importType === 'orders' ? '/import/orders' : importType === 'orders_gg' ? '/import/orders-gg' : importType === 'dispatches_gg_pdf' ? '/import/dispatches-gg-pdf' : '/import/dispatches';
    let totalSuccess = 0;
    let totalErrors = 0;
    let lastResult = null;
    try {
      const isOrderImport = importType === 'orders' || importType === 'orders_gg';
      for (let i = 0; i < files.length; i++) {
        const makeForm = () => {
          const formData = new FormData();
          formData.append('file', files[i]);
          formData.append('division', importDivision);
          // Order imports carry taker + place (stamped on every line server-side).
          if (isOrderImport) {
            if (importTaker) {
              formData.append('order_taker_id', importTaker);
              const tk = orderTakers.find(t => t.employee_id === importTaker);
              formData.append('order_taker_name', tk?.name || '');
            }
            if (importPlace) formData.append('order_place', importPlace);
          }
          return formData;
        };
        try {
          const res = isOrderImport
            ? await postOrderImport(endpoint, makeForm, files[i].name)
            : await axios.post(`${API}${endpoint}`, makeForm(), {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
              });
          if (res.data?.status === 'cancelled') {
            toast(res.data.message + (files.length - i - 1 > 0 ? ` (${files.length - i - 1} more file(s) not uploaded)` : ''));
            break;
          }
          if (res.data?.status === 'duplicate_order_nos') {
            // Server wrote nothing — surface the override/skip decision dialog
            // with existing vs incoming quantities per clashing order.
            setDupConflict({
              fileName: files[i].name,
              file: files[i],
              division: importDivision,
              takerId: importTaker || '',
              takerName: (orderTakers.find(t => t.employee_id === importTaker)?.name) || '',
              place: importPlace || '',
              conflicts: res.data.conflicts || [],
              message: res.data.message,
              remaining: files.length - i - 1,
            });
            break; // resolve this file first; any remaining files re-upload after
          }
          lastResult = res.data;
          totalSuccess++;
          if (files.length > 1) {
            toast.success(`(${i + 1}/${files.length}) ${files[i].name}: ${res.data.message}`);
          }
        } catch (err) {
          totalErrors++;
          // Format-lock mismatch → surface a detailed diff dialog instead of toast
          const detail = err?.response?.data?.detail;
          const isMismatch = err?.response?.status === 422 && detail && typeof detail === 'object' && detail.error === 'format_mismatch';
          if (isMismatch) {
            setFormatMismatch({ ...detail, file_name: files[i].name });
            // Stop the batch on first format failure — all files of this type
            // likely share the same problem, no point re-uploading them all.
            break;
          }
          toast.error(`${files[i].name}: ${typeof detail === 'string' ? detail : (detail?.message || 'Import failed')}`);
        }
      }
      if (files.length === 1 && lastResult) {
        setImportResult(lastResult);
        toast.success(lastResult.message);
      } else if (files.length > 1 && lastResult) {
        setImportResult({ message: `Uploaded ${totalSuccess} of ${files.length} files (${totalErrors} errors)` });
        toast.success(`Uploaded ${totalSuccess} of ${files.length} files`);
      }
      setImportFile(null);
      setImportFiles([]);
      const fi = document.getElementById('od-file-input');
      if (fi) fi.value = '';
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  // Resolve a duplicate-order clash: re-send the SAME file with the user's
  // explicit decision — 'override' replaces the stored lines with the file's,
  // 'skip' imports only order numbers that don't exist yet.
  const resolveDupConflict = async (action) => {
    if (!dupConflict) return;
    setUploading(true);
    try {
      const makeForm = () => {
        const formData = new FormData();
        formData.append('file', dupConflict.file);
        formData.append('division', dupConflict.division);
        if (dupConflict.takerId) {
          formData.append('order_taker_id', dupConflict.takerId);
          formData.append('order_taker_name', dupConflict.takerName);
        }
        if (dupConflict.place) formData.append('order_place', dupConflict.place);
        formData.append('conflict_action', action);
        return formData;
      };
      const res = await postOrderImport('/import/orders', makeForm, dupConflict.fileName);
      if (res.data?.status === 'cancelled') {
        toast(res.data.message);
        setDupConflict(null);
        return;
      }
      setImportResult(res.data);
      toast.success(res.data.message);
      if (dupConflict.remaining > 0) {
        toast(`${dupConflict.remaining} more file(s) from that selection weren't uploaded — please re-upload them.`);
      }
      setDupConflict(null);
      fetchData();
      if (activeTab === 'orders') fetchOrdersTable();
    } catch (err) {
      toast.error(errorText(err) || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  // GR create
  const handleCreateGr = async () => {
    if (!grReason) { toast.error('Select a reason'); return; }
    setGrSubmitting(true);
    try {
      await axios.post(`${API}/goods-returns`, {
        dispatch_id: grDispatch.dispatch_id,
        return_units: grUnits,
        reason: grReason,
        return_type: grType,
        remarks: grRemarks,
      }, { withCredentials: true });
      toast.success(`GR created for ${grUnits} unit(s)`);
      setGrDialogOpen(false);
      setGrDispatch(null);
      fetchData();
      if (activeTab === 'returns') fetchGrList();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to create GR');
    } finally {
      setGrSubmitting(false);
    }
  };

  // Open certify dialog for a single missing-order dispatch
  const openCertifyDialog = (dispatch) => {
    setCertifyTarget(dispatch);
    setBulkCertifyTargets([]);
    setCertifyBulkMode(false);
    setCertifyReason('');
    setCertifyNote('');
    setCertifyOpen(true);
  };

  // Toggle a dispatch into / out of the bulk selection set
  const toggleCertifySelection = (dispatchId) => {
    setSelectedForCertify(prev => {
      const next = new Set(prev);
      if (next.has(dispatchId)) next.delete(dispatchId);
      else next.add(dispatchId);
      return next;
    });
  };

  // Group-level helpers — take a list of dispatch lines and flip all their
  // unmatched rows on or off together
  const groupSelectionState = (lines) => {
    const unmatched = (lines || []).filter(d => d.order_match_status === 'unmatched');
    if (unmatched.length === 0) return { total: 0, selected: 0, allSelected: false, anySelected: false };
    const selected = unmatched.filter(d => selectedForCertify.has(d.dispatch_id)).length;
    return {
      total: unmatched.length,
      selected,
      allSelected: selected === unmatched.length,
      anySelected: selected > 0,
    };
  };
  const toggleGroupSelection = (lines) => {
    const unmatched = (lines || []).filter(d => d.order_match_status === 'unmatched');
    if (unmatched.length === 0) return;
    const { allSelected } = groupSelectionState(lines);
    setSelectedForCertify(prev => {
      const next = new Set(prev);
      if (allSelected) {
        unmatched.forEach(d => next.delete(d.dispatch_id));
      } else {
        unmatched.forEach(d => next.add(d.dispatch_id));
      }
      return next;
    });
  };
  const clearCertifySelection = () => setSelectedForCertify(new Set());

  // ---- Retailer tagging ("For") on order lines ----
  const toggleTagSelection = (orderLineId) => {
    if (!orderLineId) return;
    setSelectedForTag(prev => {
      const next = new Set(prev);
      if (next.has(orderLineId)) next.delete(orderLineId);
      else next.add(orderLineId);
      return next;
    });
  };
  const tagGroupSelectionState = (lines) => {
    // Only wholesale-division lines can be tagged.
    const ids = (lines || []).filter(o => isWsDivision(o.division)).map(o => o.order_line_id).filter(Boolean);
    if (ids.length === 0) return { total: 0, selected: 0, allSelected: false, anySelected: false };
    const selected = ids.filter(id => selectedForTag.has(id)).length;
    return { total: ids.length, selected, allSelected: selected === ids.length, anySelected: selected > 0 };
  };
  const toggleTagGroupSelection = (lines) => {
    const ids = (lines || []).filter(o => isWsDivision(o.division)).map(o => o.order_line_id).filter(Boolean);
    if (ids.length === 0) return;
    const { allSelected } = tagGroupSelectionState(lines);
    setSelectedForTag(prev => {
      const next = new Set(prev);
      ids.forEach(id => { if (allSelected) next.delete(id); else next.add(id); });
      return next;
    });
  };
  const clearTagSelection = () => setSelectedForTag(new Set());

  // Rows behind the current selection, plus the facts the dialog needs:
  // which customer(s) they belong to and whether that is exactly one.
  const tagSelection = useMemo(() => {
    const lines = (ordersTableData || []).filter(o => selectedForTag.has(o.order_line_id));
    // Compare codes the way the server does: '0004033605', '4033605' and
    // '4033605.0' are one customer.
    const normCode = (c) => (String(c || '').trim().replace(/\.0$/, '').replace(/^0+/, '') || '0');
    const codes = Array.from(new Set(lines.map(o => normCode(o.customer_code))));
    const divisions = Array.from(new Set(lines.map(o => o.division || '')));
    const orderNos = Array.from(new Set(lines.map(o => o.order_no).filter(Boolean)));
    return {
      lines, codes, divisions, orderNos,
      customerCode: codes.length === 1 ? (lines[0]?.customer_code || codes[0]) : '',
      customerName: lines[0]?.shop_name || lines[0]?.customer_name || '',
      division: divisions.length === 1 ? divisions[0] : (lines[0]?.division || ''),
      single: lines.length === 1 ? lines[0] : null,
    };
  }, [ordersTableData, selectedForTag]);

  const fetchTagRetailers = async (customerCode, division) => {
    if (!customerCode) { setTagRetailers([]); return; }
    setTagRetailersLoading(true);
    try {
      const params = {};
      if (division) params.division = division;
      const res = await axios.get(`${API}/customers/${encodeURIComponent(customerCode)}/retailers`, { params, withCredentials: true });
      setTagRetailers((res.data?.retailers || []).filter(r => r.active !== false));
    } catch (err) {
      setTagRetailers([]);
      toast.error(errorText(err) || 'Could not load retailers for this customer');
    } finally {
      setTagRetailersLoading(false);
    }
  };

  const openTagDialog = () => {
    if (selectedForTag.size === 0) return;
    // Open on what is stored when every selected line carries the same tag,
    // so an edit starts from the current answer.
    const cur = tagSelection.lines;
    const k0 = cur[0]?.end_customer_kind || '';
    const id0 = cur[0]?.end_customer_id || '';
    const same = cur.length > 0 && cur.every(x => (x.end_customer_kind || '') === k0 && (x.end_customer_id || '') === id0);
    setTagKind(same && k0 === 'retailer' ? 'retailer' : 'own');
    setTagRetailerId(same && k0 === 'retailer' ? id0 : '');
    setTagNewRetailerOpen(false);
    setTagNewRetailerName('');
    setTagSplitOn(false);
    setTagSplitRows([{ who: 'own', units: '' }, { who: '', units: '' }]);
    setTagDialogOpen(true);
    if (tagSelection.customerCode) fetchTagRetailers(tagSelection.customerCode, tagSelection.division);
  };

  // Click on a line's "For" cell: edit the tag of its whole order (default)
  // or of that line alone. "Whole order" sends division + order_no, so the
  // server tags every live line of the order — including lines on other
  // pages or hidden by a filter. The checkbox selection is put back after.
  const openTagDialogFor = (o) => {
    if (!o?.order_line_id) return;
    const nc = (c) => (String(c || '').trim().replace(/\.0$/, '').replace(/^0+/, '') || '0');
    const orderIds = (ordersTableData || [])
      .filter(x => x.order_line_id && x.order_no === o.order_no && x.division === o.division && nc(x.customer_code) === nc(o.customer_code))
      .map(x => x.order_line_id);
    if (!orderIds.includes(o.order_line_id)) orderIds.push(o.order_line_id);
    setTagFromCell({ lineId: o.order_line_id, orderNo: o.order_no, division: o.division, orderIds,
                     prevSelection: new Set(selectedForTag) });
    setTagScope('order');
    setSelectedForTag(new Set(orderIds));
    setPendingTagOpen(true);
  };
  useEffect(() => {
    if (!pendingTagOpen || tagSelection.lines.length === 0) return;
    setPendingTagOpen(false);
    openTagDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTagOpen, tagSelection]);
  const changeTagScope = (scope) => {
    if (!tagFromCell) return;
    setTagScope(scope);
    setTagSplitOn(false);
    setSelectedForTag(new Set(scope === 'order' ? tagFromCell.orderIds : [tagFromCell.lineId]));
  };
  const closeTagDialog = () => {
    setTagDialogOpen(false);
    if (tagFromCell) { setSelectedForTag(new Set(tagFromCell.prevSelection || [])); setTagFromCell(null); }
  };

  // Adds a retailer to the wholesaler's list and selects it. The same list
  // feeds the "For" filter, so refresh that too when it is showing this customer.
  const addTagRetailer = async () => {
    const name = tagNewRetailerName.trim().replace(/\s+/g, ' ');
    if (!name || !tagSelection.customerCode) return;
    setTagAddingRetailer(true);
    try {
      const params = {};
      if (tagSelection.division) params.division = tagSelection.division;
      const res = await axios.post(`${API}/customers/${encodeURIComponent(tagSelection.customerCode)}/retailers`, { name }, { params, withCredentials: true });
      const created = res.data?.retailer || res.data;
      if (created?.retailer_id) {
        setTagRetailers(prev => [...prev.filter(r => r.retailer_id !== created.retailer_id), created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setTagRetailerId(created.retailer_id);
        if (filterCustomer === tagSelection.customerCode) {
          setFilterRetailers(prev => [...prev.filter(r => r.retailer_id !== created.retailer_id), created]);
        }
      }
      setTagNewRetailerOpen(false);
      setTagNewRetailerName('');
      toast.success(`Retailer added — ${created?.name || name}`);
    } catch (err) {
      toast.error(errorText(err) || 'Could not add this retailer');
    } finally {
      setTagAddingRetailer(false);
    }
  };

  const tagSplitTotal = tagSplitRows.reduce((s, r) => s + (parseInt(r.units, 10) || 0), 0);
  const tagSplitValid = tagSplitOn && !!tagSelection.single
    && tagSplitRows.length >= 2
    && tagSplitRows.every(r => r.who && (parseInt(r.units, 10) || 0) > 0)
    && tagSplitTotal === Number(tagSelection.single.units_ordered || 0);

  const tagCanSubmit = (() => {
    if (tagSubmitting || tagSelection.lines.length === 0 || tagSelection.codes.length !== 1) return false;
    if (tagSplitOn) return tagSplitValid;
    if (tagKind === 'retailer') return !!tagRetailerId;
    return tagKind === 'own' || tagKind === 'clear';
  })();

  const submitTag = async () => {
    if (!tagCanSubmit) return;
    const order_line_ids = tagSelection.lines.map(o => o.order_line_id);
    const body = { order_line_ids };
    if (tagFromCell && tagScope === 'order' && !tagSplitOn) {
      body.division = tagFromCell.division;
      body.order_no = tagFromCell.orderNo;
    }
    if (tagSplitOn) {
      body.kind = 'split';
      body.split = tagSplitRows.map(r => ({
        kind: r.who === 'own' ? 'own' : 'retailer',
        retailer_id: r.who === 'own' ? null : r.who,
        units: parseInt(r.units, 10) || 0,
      }));
    } else {
      body.kind = tagKind;
      if (tagKind === 'retailer') body.retailer_id = tagRetailerId;
    }
    setTagSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders/tag-end-customer`, body, { withCredentials: true });
      toast.success(res.data?.message || (body.kind === 'clear' ? 'Tag cleared'
        : `Tagged ${res.data?.lines_tagged ?? ''} line(s) — ${res.data?.name || ''}`.replace(/ — $/, '')));
      setTagDialogOpen(false);
      if (tagFromCell) setSelectedForTag(new Set(tagFromCell.prevSelection || []));
      else clearTagSelection();
      setTagFromCell(null);
      fetchOrdersTable();
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Could not tag these lines');
    } finally {
      setTagSubmitting(false);
    }
  };

  // Open dialog with all currently-selected dispatches
  const openBulkCertifyDialog = () => {
    if (selectedForCertify.size === 0) return;
    const idSet = selectedForCertify;
    // Pull matching rows from dispatchesTableData (already in scope below — flatten later)
    const targets = (dispatchesTableData || []).filter(d => idSet.has(d.dispatch_id));
    setBulkCertifyTargets(targets);
    setCertifyTarget(null);
    setCertifyBulkMode(true);
    setCertifyReason('');
    setCertifyNote('');
    setCertifyOpen(true);
  };

  // Certify missing-order dispatch(es). Routes to single or bulk endpoint.
  const submitCertify = async () => {
    const reason = certifyReason.trim();
    if (!reason) {
      toast.error('Reason is required');
      return;
    }
    setCertifySubmitting(true);
    try {
      if (certifyBulkMode) {
        const ids = Array.from(selectedForCertify);
        if (ids.length === 0) {
          toast.error('No dispatches selected');
          setCertifySubmitting(false);
          return;
        }
        const res = await axios.post(
          `${API}/dispatches/bulk-certify-direct`,
          { dispatch_ids: ids, reason, note: certifyNote.trim() },
          { withCredentials: true }
        );
        const certified = res.data?.certified || 0;
        const skipped = (res.data?.skipped || []).length;
        if (skipped > 0) {
          toast.success(`Certified ${certified} dispatch(es) · ${skipped} skipped`);
        } else {
          toast.success(`Certified ${certified} dispatch(es)`);
        }
        setSelectedForCertify(new Set());
      } else {
        if (!certifyTarget) {
          toast.error('No dispatch selected');
          setCertifySubmitting(false);
          return;
        }
        await axios.post(
          `${API}/dispatches/${certifyTarget.dispatch_id}/certify-direct`,
          { reason, note: certifyNote.trim() },
          { withCredentials: true }
        );
        toast.success('Dispatch certified as direct');
      }
      setCertifyOpen(false);
      setCertifyTarget(null);
      setBulkCertifyTargets([]);
      setCertifyBulkMode(false);
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to certify dispatch(es)');
    } finally {
      setCertifySubmitting(false);
    }
  };

  // Un-certify (admin only) — revert to missing-order status
  const uncertifyDispatch = async (dispatch) => {
    if (!dispatch?.dispatch_id) return;
    if (!window.confirm(`Un-certify this dispatch (${dispatch.customer_name || ''} / ${dispatch.trade_name || ''})? It will go back to the Missing Orders list.`)) {
      return;
    }
    try {
      await axios.post(
        `${API}/dispatches/${dispatch.dispatch_id}/uncertify`,
        {},
        { withCredentials: true }
      );
      toast.success('Certification reversed');
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to un-certify dispatch');
    }
  };

  // GR status update
  const handleUpdateGrStatus = async () => {
    if (!grNewStatus) return;
    try {
      await axios.put(`${API}/goods-returns/${grStatusItem.gr_id}/status`, {
        status: grNewStatus,
        remarks: grStatusRemarks,
        credit_note_number: grCreditNoteNumber,
        credit_note_amount: grCreditNoteAmount,
      }, { withCredentials: true });
      toast.success(`GR status updated to ${grNewStatus.replace(/_/g, ' ')}`);
      setGrStatusDialogOpen(false);
      fetchGrList();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to update status');
    }
  };

  // Wholesale (WS) division: the manual form offers "For" on a new order line.
  const manualIsWs = (() => {
    const d = (manualOrder.division || '').toUpperCase();
    return d.includes(' WS') || d.endsWith('WS');
  })();
  const manualForShown = manualIsWs && !editOrderMode;
  useEffect(() => {
    if (manualOrderOpen) { setManualFor(''); setManualForName(''); }
  }, [manualOrderOpen]);
  useEffect(() => {
    if (!manualOrderOpen || !manualForShown || !manualOrder.customer_code || !manualOrder.shop_name) {
      setManualRetailers([]);
      return undefined;
    }
    let alive = true;
    axios.get(`${API}/customers/${encodeURIComponent(manualOrder.customer_code)}/retailers`,
      { params: { division: manualOrder.division }, withCredentials: true })
      .then(r => { if (alive) setManualRetailers((r.data?.retailers || []).filter(x => x.active !== false)); })
      .catch(() => { if (alive) setManualRetailers([]); });
    return () => { alive = false; };
  }, [manualOrderOpen, manualForShown, manualOrder.customer_code, manualOrder.shop_name, manualOrder.division]);
  // Store picker — TAS only (the only division whose files carry a ship-to),
  // and only once the code resolves to a known customer with stores.
  const manualStoreShown = (manualOrder.division || '').toUpperCase() === 'TAS' && !editOrderMode;
  useEffect(() => {
    if (manualOrderOpen) setManualShipTo('');
  }, [manualOrderOpen]);
  useEffect(() => {
    // A new customer code means a new store list: never carry the previous
    // customer's store into this order (the server would reject it anyway).
    setManualShipTo('');
    if (!manualOrderOpen || !manualStoreShown || !manualOrder.customer_code || !manualOrder.shop_name) {
      setManualStores([]);
      return undefined;
    }
    let alive = true;
    axios.get(`${API}/customers/${encodeURIComponent(manualOrder.customer_code)}/stores`,
      { params: { division: manualOrder.division }, withCredentials: true })
      .then(r => { if (alive) setManualStores((r.data?.stores || []).filter(x => x.active !== false)); })
      .catch(() => { if (alive) setManualStores([]); });
    return () => { alive = false; };
  }, [manualOrderOpen, manualStoreShown, manualOrder.customer_code, manualOrder.shop_name, manualOrder.division]);

  // Manual order submit (create or edit)
  const handleManualOrderSubmit = async () => {
    const { order_no, division, customer_code, trade_name, units_ordered } = manualOrder;
    if (!order_no || !division || !customer_code || !trade_name || units_ordered <= 0) {
      toast.error('Order No, Division, Customer Code, Trade Name, and Units are required');
      return;
    }
    let endCustomer = null;
    if (manualForShown && manualFor) {
      if (manualFor === 'own') endCustomer = { kind: 'own' };
      else if (manualFor === 'new') {
        const nm = manualForName.trim().replace(/\s+/g, ' ');
        if (!nm) { toast.error('Type the new retailer name (or number), or choose another option under For'); return; }
        endCustomer = { kind: 'retailer', new_name: nm };
      } else if (manualFor.startsWith('ret:')) endCustomer = { kind: 'retailer', retailer_id: manualFor.slice(4) };
    }
    let manualPayload = endCustomer ? { ...manualOrder, end_customer: endCustomer } : manualOrder;
    if (manualStoreShown && manualShipTo) manualPayload = { ...manualPayload, ship_to_code: manualShipTo };
    setManualSubmitting(true);
    try {
      if (editOrderMode && editOrderId) {
        await axios.put(`${API}/orders/${editOrderId}`, manualOrder, { withCredentials: true });
        toast.success('Order updated');
      } else {
        let res = await axios.post(`${API}/orders/manual`, manualPayload, { withCredentials: true });
        if (res.data?.status === 'exists') {
          // Same (order no + product + division) already stored — saving would
          // replace it. Show existing qty and ask before overriding.
          const ok = window.confirm(`${res.data.message}\n\nOverride the existing order line?`);
          if (!ok) {
            toast('Left the existing order untouched');
            return; // keep the dialog open so the user can adjust
          }
          res = await axios.post(`${API}/orders/manual`, { ...manualPayload, override: true }, { withCredentials: true });
        }
        toast.success(res.data.message);
      }
      setManualOrderOpen(false);
      setManualOrder({
        order_no: '', division: '', customer_code: '', shop_name: '', trade_name: '',
        product_code: '', units_ordered: 1, order_date: new Date().toISOString().slice(0, 10),
        state: '', city: '', category: '', ex_mill_price: 0,
        discount_pct: 0, remark: '',
      });
      setEditOrderMode(false);
      setEditOrderId(null);
      fetchData();
      if (activeTab === 'orders') fetchOrdersTable();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save order');
    } finally {
      setManualSubmitting(false);
    }
  };

  // Bulk discount apply to entire order
  const handleBulkDiscount = async () => {
    if (!bulkDiscountOrderNo || !bulkDiscountDivision) {
      toast.error('Order No and Division are required');
      return;
    }
    setBulkDiscountSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-discount`, {
        order_no: bulkDiscountOrderNo,
        division: bulkDiscountDivision,
        discount_pct: bulkDiscountPct,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setBulkDiscountOpen(false);
      setBulkDiscountPct(0);
      fetchData();
      if (activeTab === 'orders') fetchOrdersTable();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to apply discount');
    } finally {
      setBulkDiscountSubmitting(false);
    }
  };

  // Open the customer-level discount picker: group the customer's lines by
  // order_no (with unit + line counts) so the user picks which orders to
  // discount. All selected by default. Fetches the customer's base discount.
  const openCustomerDiscount = async (cust, division) => {
    const byOrder = {};
    for (const l of cust.lines) {
      const o = byOrder[l.order_no] || (byOrder[l.order_no] = { order_no: l.order_no, units: 0, lines: 0 });
      o.units += (l.units_ordered || 0);
      o.lines += 1;
    }
    const orders = Object.values(byOrder).sort((a, b) => String(a.order_no).localeCompare(String(b.order_no)));
    setCustDiscount({ name: cust.name, code: cust.code, division, orders });
    setCustDiscountSel(Object.fromEntries(orders.map(o => [o.order_no, true])));
    setCustDiscountPct(0);
    setCustDiscountBase(0);
    try {
      const res = await axios.get(`${API}/orders/base-discount`, {
        params: { order_no: orders[0]?.order_no, division }, withCredentials: true,
      });
      setCustDiscountBase(res.data?.base_discount_pct || 0);
    } catch { /* ignore */ }
  };

  const handleCustomerDiscount = async () => {
    if (!custDiscount) return;
    const chosen = custDiscount.orders.filter(o => custDiscountSel[o.order_no]).map(o => o.order_no);
    if (chosen.length === 0) { toast.error('Select at least one order'); return; }
    setCustDiscountBusy(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-discount`, {
        order_nos: chosen,
        division: custDiscount.division,
        discount_pct: custDiscountPct,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setCustDiscount(null);
      fetchData();
      if (activeTab === 'orders') fetchOrdersTable();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to apply discount');
    } finally {
      setCustDiscountBusy(false);
    }
  };

  // Open bulk discount dialog for a specific order — also fetch base discount
  const openBulkDiscount = async (orderNo, division) => {
    setBulkDiscountOrderNo(orderNo);
    setBulkDiscountDivision(division);
    setBulkDiscountPct(0);
    setBulkDiscountBaseInfo({ base_discount_pct: 0, customer_code: '', shop_name: '' });
    setBulkDiscountOpen(true);
    try {
      const res = await axios.get(`${API}/orders/base-discount`, {
        params: { order_no: orderNo, division },
        withCredentials: true,
      });
      setBulkDiscountBaseInfo(res.data);
    } catch { /* ignore */ }
  };

  // Export handlers
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterState && filterState !== 'all') params.state = filterState;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;

      const res = await axios.get(`${API}/orders-dispatch/export/${format}`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `Orders_Dispatch_${filterDivision || 'All'}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Fetch orders table data
  const ordersTableFetchIdRef = useRef(0); // stale-response guard
  const fetchOrdersTable = useCallback(async () => {
    const myId = ++ordersTableFetchIdRef.current;
    setOrdersTableLoading(true);
    try {
      const params = { skip: ordersPage * ordersPageSize, limit: ordersPageSize };
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterState && filterState !== 'all') params.state = filterState;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (filterEndCustomer && filterEndCustomer !== 'all' && filterCustomer !== 'all') params.end_customer = filterEndCustomer;
      if (filterStore && filterStore !== 'all' && filterCustomer !== 'all') params.store = filterStore;
      const res = await axios.get(`${API}/orders`, { params, withCredentials: true });
      if (myId !== ordersTableFetchIdRef.current) return; // superseded by a newer fetch
      setOrdersTableData(res.data.orders || []);
      setOrdersTableTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      if (myId === ordersTableFetchIdRef.current) toast.error('Failed to load orders');
    } finally {
      if (myId === ordersTableFetchIdRef.current) setOrdersTableLoading(false);
    }
  }, [filterDivision, filterMonth, filterState, filterCustomer, filterEndCustomer, filterStore, debouncedSearch, ordersPage]);

  // Fetch dispatches table data
  const dispatchesTableFetchIdRef = useRef(0); // stale-response guard
  const fetchDispatchesTable = useCallback(async () => {
    const myId = ++dispatchesTableFetchIdRef.current;
    setDispatchesTableLoading(true);
    try {
      const params = { skip: dispatchesPage * dispatchesPageSize, limit: dispatchesPageSize };
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterMonth && filterMonth !== 'all') params.month = filterMonth;
      if (filterState && filterState !== 'all') params.state = filterState;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (filterEndCustomer && filterEndCustomer !== 'all' && filterCustomer !== 'all') params.end_customer = filterEndCustomer;
      if (filterStore && filterStore !== 'all' && filterCustomer !== 'all') params.store = filterStore;
      const res = await axios.get(`${API}/dispatches`, { params, withCredentials: true });
      if (myId !== dispatchesTableFetchIdRef.current) return; // superseded by a newer fetch
      setDispatchesTableData(res.data.dispatches || []);
      setDispatchesTableTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch dispatches:', err);
      if (myId === dispatchesTableFetchIdRef.current) toast.error('Failed to load dispatches');
    } finally {
      if (myId === dispatchesTableFetchIdRef.current) setDispatchesTableLoading(false);
    }
  }, [filterDivision, filterMonth, filterState, filterCustomer, filterEndCustomer, filterStore, debouncedSearch, dispatchesPage]);

  // Fetch import history
  const fetchImportHistory = useCallback(async () => {
    setImportHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/import/history`, { withCredentials: true });
      setImportHistory(res.data.imports || []);
    } catch (err) {
      console.error('Failed to fetch import history:', err);
    } finally {
      setImportHistoryLoading(false);
    }
  }, []);

  // Rollback an import batch
  const handleRollback = async (imp) => {
    const batchId = imp.batch_id;
    setRollbackLoading(batchId);
    try {
      const res = await rollbackImportBatch(axios, batchId, imp.original_name || imp.filename);
      if (res) {
        toast.success(res.message || 'Rollback complete');
        fetchImportHistory();
        fetchData();
      }
    } catch (err) {
      toast.error(errorText(err) || 'Rollback failed');
    } finally {
      setRollbackLoading(null);
    }
  };

  // Fetch dispatch count for purge dialog
  const fetchPurgeCount = useCallback(async (div) => {
    try {
      const params = {};
      if (div && div !== 'all') params.division = div;
      const res = await axios.get(`${API}/dispatches/count`, { params, withCredentials: true });
      setPurgeCount(res.data.count || 0);
    } catch (err) { setPurgeCount(0); }
  }, []);

  // Purge dispatches handler
  const handlePurge = async () => {
    if (purgeConfirmText !== 'DELETE') return;
    // The server refuses an all-division purge (audit F-50) — one division at a time.
    if (!purgeDivision || purgeDivision === 'all') { toast.error('Pick one division to purge'); return; }
    setPurging(true);
    try {
      const params = { division: purgeDivision, confirm: true };
      const res = await axios.delete(`${API}/dispatches/purge`, { params, withCredentials: true });
      toast.success(res?.data?.message || `Removed ${purgeCount} dispatches`);
      setPurgeDialogOpen(false);
      setPurgeConfirmText('');
      fetchData();
      fetchDispatchesTable();
      fetchImportHistory();
    } catch (err) {
      toast.error(errorText(err) || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  // Count of dispatches on the current page that are missing their order copy.
  // Drives the filter-toggle label — so the user knows how many to chase.
  const missingOrdersCount = useMemo(
    () => dispatchesTableData.filter(d => d.order_match_status === 'unmatched').length,
    [dispatchesTableData]
  );

  // Group dispatches by date -> customer
  const groupedDispatches = useMemo(() => {
    if (!dispatchesTableData.length) return [];
    const source = showMissingOrdersOnly
      ? dispatchesTableData.filter(d => d.order_match_status === 'unmatched')
      : dispatchesTableData;
    const dateMap = {};
    for (const d of source) {
      const dateKey = (d.delivery_date || '').slice(0, 10) || 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = {};
      const custKey = `${d.customer_code || 'N/A'}|||${d.customer_name || 'Unknown'}`;
      if (!dateMap[dateKey][custKey]) dateMap[dateKey][custKey] = [];
      dateMap[dateKey][custKey].push(d);
    }
    // Sort dates descending
    const sortedDates = Object.keys(dateMap).sort((a, b) => b.localeCompare(a));
    return sortedDates.map((dateKey, dateIdx) => {
      const customers = dateMap[dateKey];
      const custEntries = Object.entries(customers).map(([custKey, lines]) => {
        const [code, name] = custKey.split('|||');
        return {
          code, name, lines,
          totalPieces: lines.reduce((s, l) => s + (l.no_of_pieces || 0), 0),
          totalMetres: lines.reduce((s, l) => s + (l.batch_qty_metres || 0), 0),
          totalNetValue: lines.reduce((s, l) => s + (l.net_value || 0), 0),  // net, ex-GST (audit F-28)
        };
      });
      const dayPieces = custEntries.reduce((s, c) => s + c.totalPieces, 0);
      const dayMetres = custEntries.reduce((s, c) => s + c.totalMetres, 0);
      const dayNetValue = custEntries.reduce((s, c) => s + c.totalNetValue, 0);
      const dayLines = custEntries.reduce((s, c) => s + c.lines.length, 0);
      const dateObj = dateKey !== 'Unknown' ? new Date(dateKey + 'T00:00:00') : null;
      const dayName = dateObj ? dateObj.toLocaleDateString('en-IN', { weekday: 'long' }) : '';
      const formattedDate = dateObj ? dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Unknown';
      return {
        dateKey, formattedDate, dayName, customers: custEntries,
        dayLines, dayPieces, dayMetres, dayNetValue,
        defaultCollapsed: dateIdx > 0, // collapse all except most recent
      };
    });
  }, [dispatchesTableData, showMissingOrdersOnly]);

  // Group orders by date -> customer
  const groupedOrders = useMemo(() => {
    if (!ordersTableData.length) return [];
    const dateMap = {};
    for (const o of ordersTableData) {
      const dateKey = (o.order_date || '').slice(0, 10) || 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = {};
      const custKey = `${o.customer_code || 'N/A'}|||${o.shop_name || 'Unknown'}`;
      if (!dateMap[dateKey][custKey]) dateMap[dateKey][custKey] = [];
      dateMap[dateKey][custKey].push(o);
    }
    const sortedDates = Object.keys(dateMap).sort((a, b) => b.localeCompare(a));
    return sortedDates.map((dateKey, dateIdx) => {
      const customers = dateMap[dateKey];
      const custEntries = Object.entries(customers).map(([custKey, lines]) => {
        const [code, name] = custKey.split('|||');
        return {
          code, name, lines,
          totalOrdered: lines.reduce((s, l) => s + (l.units_ordered || 0), 0),
          totalDispatched: lines.reduce((s, l) => s + (l.units_dispatched || 0), 0),
        };
      });
      const dayOrdered = custEntries.reduce((s, c) => s + c.totalOrdered, 0);
      const dayDispatched = custEntries.reduce((s, c) => s + c.totalDispatched, 0);
      const dayLines = custEntries.reduce((s, c) => s + c.lines.length, 0);
      const dateObj = dateKey !== 'Unknown' ? new Date(dateKey + 'T00:00:00') : null;
      const dayName = dateObj ? dateObj.toLocaleDateString('en-IN', { weekday: 'long' }) : '';
      const formattedDate = dateObj ? dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Unknown';
      return {
        dateKey, formattedDate, dayName, customers: custEntries,
        dayLines, dayOrdered, dayDispatched,
        defaultCollapsed: dateIdx > 0,
      };
    });
  }, [ordersTableData]);

  // Initialize collapsed states when grouped data changes
  useEffect(() => {
    if (groupedDispatches.length && Object.keys(collapsedDates).length === 0) {
      const initial = {};
      groupedDispatches.forEach(g => { initial[g.dateKey] = g.defaultCollapsed; });
      setCollapsedDates(initial);
    }
  }, [groupedDispatches]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (groupedOrders.length && Object.keys(collapsedOrderDates).length === 0) {
      const initial = {};
      groupedOrders.forEach(g => { initial[g.dateKey] = g.defaultCollapsed; });
      setCollapsedOrderDates(initial);
    }
  }, [groupedOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDateCollapse = (dateKey) => {
    setCollapsedDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };
  const toggleOrderDateCollapse = (dateKey) => {
    setCollapsedOrderDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };
  const toggleReturnDateCollapse = (dateKey) => {
    setCollapsedReturnDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  // Goods returns grouped by date -> customer (mirrors the dispatches view).
  // Applies the in-tab search (customer name / code) client-side.
  const groupedReturns = useMemo(() => {
    if (!grList.length) return [];
    const s = grSearch.trim().toLowerCase();
    const source = s
      ? grList.filter(g =>
          (g.customer_name || '').toLowerCase().includes(s) ||
          (g.customer_code || '').toLowerCase().includes(s))
      : grList;
    const dateMap = {};
    for (const g of source) {
      const dateKey = (g.created_at || '').slice(0, 10) || 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = {};
      const custKey = `${g.customer_code || 'N/A'}|||${g.customer_name || 'Unknown'}`;
      if (!dateMap[dateKey][custKey]) dateMap[dateKey][custKey] = [];
      dateMap[dateKey][custKey].push(g);
    }
    const sortedDates = Object.keys(dateMap).sort((a, b) => b.localeCompare(a));
    return sortedDates.map((dateKey, dateIdx) => {
      const customers = dateMap[dateKey];
      const custEntries = Object.entries(customers).map(([custKey, lines]) => {
        const [code, name] = custKey.split('|||');
        return {
          code, name, lines,
          totalUnits: lines.reduce((sum, l) => sum + (l.return_units || 0), 0),
          totalAmount: lines.reduce((sum, l) => sum + (l.credit_note_amount || 0), 0),
        };
      });
      const dayUnits = custEntries.reduce((sum, c) => sum + c.totalUnits, 0);
      const dayAmount = custEntries.reduce((sum, c) => sum + c.totalAmount, 0);
      const dayLines = custEntries.reduce((sum, c) => sum + c.lines.length, 0);
      const dateObj = dateKey !== 'Unknown' ? new Date(dateKey + 'T00:00:00') : null;
      const dayName = dateObj ? dateObj.toLocaleDateString('en-IN', { weekday: 'long' }) : '';
      const formattedDate = dateObj ? dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Unknown';
      return {
        dateKey, formattedDate, dayName, customers: custEntries,
        dayLines, dayUnits, dayAmount,
        defaultCollapsed: dateIdx > 0,
      };
    });
  }, [grList, grSearch]);

  useEffect(() => {
    if (groupedReturns.length && Object.keys(collapsedReturnDates).length === 0) {
      const initial = {};
      groupedReturns.forEach(g => { initial[g.dateKey] = g.defaultCollapsed; });
      setCollapsedReturnDates(initial);
    }
  }, [groupedReturns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mix authorisations — must be declared BEFORE the tab-change effect that
  // references them in its dependency array.
  const mixFetchIdRef = useRef(0); // stale-response guard
  const fetchMixAuths = useCallback(async () => {
    const myId = ++mixFetchIdRef.current;
    setMixLoading(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (mixStatusFilter !== 'all') params.status = mixStatusFilter;
      const res = await axios.get(`${API}/mix-authorisations`, { params, withCredentials: true });
      if (myId !== mixFetchIdRef.current) return; // superseded by a newer fetch
      setMixAuths(res.data?.authorisations || []);
      setMixSummary(res.data?.summary || {});
    } catch (err) {
      console.error('Failed to load mix authorisations', err);
      if (myId === mixFetchIdRef.current) toast.error('Could not load mix authorisations.');
    } finally {
      if (myId === mixFetchIdRef.current) setMixLoading(false);
    }
  }, [filterDivision, mixStatusFilter]);

  const openMixDialog = () => {
    const today = new Date();
    const plus60 = new Date(today.getTime() + 60 * 24 * 3600 * 1000);
    setMixForm({
      division: (filterDivision && filterDivision !== 'all') ? filterDivision : '',
      customer_code: '',
      fabric_type: 'shirting',
      bales_authorised: 1,
      flat_rate: 0,
      authorised_on: today.toISOString().slice(0, 10),
      expires_on: plus60.toISOString().slice(0, 10),
      source: 'whatsapp',
      reference_text: '',
    });
    setMixDialogOpen(true);
  };

  const saveMixAuth = async () => {
    if (!mixForm.division || !mixForm.customer_code || !mixForm.fabric_type) {
      toast.error('Division, Customer Code and Fabric Type are required');
      return;
    }
    if (!Number(mixForm.bales_authorised) || Number(mixForm.bales_authorised) < 1) {
      toast.error('Bales authorised must be at least 1');
      return;
    }
    setSavingMixAuth(true);
    try {
      const res = await axios.post(`${API}/mix-authorisations`, mixForm, { withCredentials: true });
      const consumed = res.data?.reconcile?.dispatches_consumed || 0;
      toast.success(consumed > 0
        ? `Authorisation saved — ${consumed} existing mix dispatch(es) auto-matched`
        : 'Mix authorisation saved');
      setMixDialogOpen(false);
      fetchMixAuths();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save authorisation');
    } finally {
      setSavingMixAuth(false);
    }
  };

  const cancelMixAuth = async (auth) => {
    const remark = window.prompt(`Cancel mix authorisation for ${auth.customer_name || auth.customer_code} (${auth.bales_authorised} ${auth.fabric_type} bales)?\n\nOptional remark (e.g. "cancelled via WhatsApp to Arvind 23 Apr"):`, '');
    if (remark === null) return;
    try {
      await axios.patch(`${API}/mix-authorisations/${auth.auth_id}/cancel`, { cancel_remark: remark }, { withCredentials: true });
      toast.success('Authorisation cancelled');
      fetchMixAuths();
    } catch (err) {
      toast.error(errorText(err) || 'Cancel failed');
    }
  };

  const reconcileMixAuths = async () => {
    try {
      const res = await axios.post(`${API}/mix-authorisations/reconcile`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'Reconciled');
      fetchMixAuths();
      fetchMixNeedsFabric();
    } catch (err) {
      toast.error('Reconcile failed');
    }
  };

  const fetchMixNeedsFabric = useCallback(async () => {
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      const res = await axios.get(`${API}/dispatches/mix-needs-fabric`, { params, withCredentials: true });
      setMixNeedsFabric(res.data?.dispatches || []);
    } catch (err) {
      // Silent — banner just won't show
      console.debug('mix-needs-fabric fetch failed', err);
    }
  }, [filterDivision]);

  const openMixFabricDialog = () => {
    // Prefill suggested fabric where the backend hinted one
    const seed = {};
    mixNeedsFabric.forEach(d => {
      if (d.suggested_fabric) seed[d.dispatch_id] = d.suggested_fabric;
    });
    setMixFabricChoices(seed);
    setMixFabricDialogOpen(true);
  };

  const assignMixFabrics = async () => {
    const entries = Object.entries(mixFabricChoices).filter(([, f]) => f);
    if (entries.length === 0) {
      toast.error('Pick a fabric for at least one row');
      return;
    }
    setMixFabricSaving(true);
    let ok = 0;
    let fail = 0;
    for (const [dispatch_id, fabric_type] of entries) {
      try {
        await axios.post(`${API}/dispatches/${dispatch_id}/assign-fabric`, { fabric_type }, { withCredentials: true });
        ok += 1;
      } catch (err) {
        fail += 1;
      }
    }
    setMixFabricSaving(false);
    if (ok) toast.success(`Fabric assigned on ${ok} dispatch(es)${fail ? `, ${fail} failed` : ''}`);
    else toast.error('Assignment failed');
    setMixFabricDialogOpen(false);
    fetchMixAuths();
    fetchMixNeedsFabric();
  };

  // Fetch tables when tab changes
  useEffect(() => {
    if (activeTab === 'orders') fetchOrdersTable();
    if (activeTab === 'dispatches') fetchDispatchesTable();
    if (activeTab === 'import') fetchImportHistory();
    if (activeTab === 'mix_auths') {
      fetchMixAuths();
      fetchMixNeedsFabric();
    }
  }, [activeTab, fetchOrdersTable, fetchDispatchesTable, fetchImportHistory, fetchMixAuths, fetchMixNeedsFabric]);

  // Load distinct categories whenever the manual-order dialog is open,
  // scoped to the chosen division. Gives operators a dropdown so they
  // can't accidentally create a new category through a typo.
  useEffect(() => {
    if (!manualOrderOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (manualOrder.division) params.division = manualOrder.division;
        const res = await axios.get(`${API}/orders/categories`, { params, withCredentials: true });
        if (cancelled) return;
        setCategoryOptions(res.data?.categories || []);
      } catch (err) {
        console.error('Failed to load category list', err);
      }
    })();
    return () => { cancelled = true; };
  }, [manualOrderOpen, manualOrder.division]);

  // Load customers scoped to the selected division whenever either manual
  // entry dialog is open. Flattens divisions[] so each row exposes the
  // per-division customer_code. Same directory powers both the Add Order
  // and Add Dispatch dialogs so there's one source of truth.
  useEffect(() => {
    const activeDivision = manualOrderOpen ? manualOrder.division
      : manualDispatchOpen ? manualDispatch.division
      : mixDialogOpen ? mixForm.division
      : '';
    if (!activeDivision) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/customers`, {
          params: { division: activeDivision },
          withCredentials: true,
        });
        if (cancelled) return;
        const rows = (res.data || []).map(c => {
          const div = (c.divisions || []).find(d => d.division === activeDivision) || {};
          return {
            customer_id: c.customer_id,
            customer_code: div.customer_code || c.customer_code || '',
            name: c.name || '',
            region: c.region || '',
            state: c.state || c.region || '',
            city: c.city || '',
          };
        }).filter(r => r.customer_code);
        setCustomerOptions(rows);
      } catch (err) {
        console.error('Failed to load customer directory', err);
      }
    })();
    return () => { cancelled = true; };
  }, [manualOrderOpen, manualOrder.division, manualDispatchOpen, manualDispatch.division, mixDialogOpen, mixForm.division]);

  // Per-table export handlers
  // Export dialog for the Orders / Dispatches tabs. Firing the export straight
  // from the button meant an unfiltered click tried to render the whole
  // dataset and only failed afterwards (413 "too many rows"). The dialog asks
  // for the scope up front — duration, region, customer — seeded from the
  // page's active filters.
  const [odExportKind, setOdExportKind] = useState(null); // 'orders' | 'dispatches' | null
  const [odExMonth, setOdExMonth] = useState('all');
  const [odExState, setOdExState] = useState('all');
  const [odExCustomer, setOdExCustomer] = useState('all');

  const openOdExport = (kind) => {
    setOdExMonth(filterMonth || 'all');
    setOdExState(filterState || 'all');
    setOdExCustomer(filterCustomer || 'all');
    setOdExportKind(kind);
  };

  const odExportScoped = () => odExMonth !== 'all' || odExState !== 'all' || odExCustomer !== 'all';

  const handleOrdersExport = async (format) => {
    setExporting(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (odExMonth && odExMonth !== 'all') params.month = odExMonth;
      if (odExState && odExState !== 'all') params.state = odExState;
      if (odExCustomer && odExCustomer !== 'all') params.customer_code = odExCustomer;
      const res = await axios.get(`${API}/orders/export/${format}`, {
        params, withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const scope = [filterDivision && filterDivision !== 'all' ? filterDivision : 'All',
        odExMonth !== 'all' ? odExMonth : null,
        odExState !== 'all' ? odExState : null].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]+/g, '');
      link.setAttribute('download', `Orders_${scope}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Orders ${format.toUpperCase()} downloaded`);
      setOdExportKind(null);
    } catch (err) {
      // responseType is 'blob', so error bodies arrive as a Blob — read it to
      // surface the server's real reason (e.g. the 413 row-limit message).
      let msg = err.message || 'Unknown error';
      try {
        const t = await (err.response?.data?.text?.() || Promise.resolve(''));
        if (t) { try { msg = JSON.parse(t).detail || msg; } catch { msg = t.slice(0, 200); } }
      } catch {}
      toast.error(`Export failed${err.response?.status ? ` (${err.response.status})` : ''}: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  // One-click reconcile: re-sync dispatch counts for every order in the
  // current division. Fixes cases where the order copy was uploaded AFTER
  // the dispatches, which left units_dispatched = 0 and status = "pending"
  // even though the goods had already shipped.
  const handleReconcileOrders = async () => {
    if (!window.confirm('Re-scan all orders against existing dispatches to recalculate dispatch counts? This only re-reads data — nothing is deleted.')) return;
    setReconciling(true);
    try {
      const body = {};
      if (filterDivision && filterDivision !== 'all') body.division = filterDivision;
      const res = await axios.post(`${API}/orders/sync-dispatches`, body, { withCredentials: true });
      toast.success(res.data.message || 'Reconciliation complete');
      fetchOrdersTable();
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  // Download an Excel sheet of dispatches currently marked Missing Order so
  // admin can forward it to the marketing team or hunt for the order copies.
  const handleMissingOrdersExport = () => {
    const missing = (dispatchesTableData || []).filter(d => d.order_match_status === 'unmatched');
    if (!missing.length) {
      toast.info('No missing orders to export on the current page');
      return;
    }
    const rows = missing.map(d => ({
      'Customer Code': d.customer_code || '',
      'Customer Name': d.customer_name || '',
      'State': d.state || '',
      'Division': d.division || '',
      'Invoice No': d.plant_invoice || '',
      'Invoice Date': (d.delivery_date || '').slice(0, 10),
      'Delivery Month': d.delivery_month || '',
      'Trade Name': d.trade_name || '',
      'Sort / Product Code': d.product_code || d.customer_reference || '',
      'Pieces': d.no_of_pieces || 0,
      'Metres': d.batch_qty_metres || 0,
      'Net Value (\u20B9)': d.net_value || 0,
      'Order No (if on dispatch)': d.order_no || '',
      'PO Base': d.po_base || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 14 }, { wch: 32 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
      { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 20 }, { wch: 10 },
      { wch: 10 }, { wch: 14 }, { wch: 20 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Missing Orders');
    const today = new Date().toISOString().slice(0, 10);
    const divTag = (filterDivision && filterDivision !== 'all') ? `_${filterDivision.replace(/\s+/g, '')}` : '';
    XLSX.writeFile(wb, `Missing_Orders${divTag}_${today}.xlsx`);
    toast.success(`Exported ${missing.length} missing order line(s)`);
  };

  const handleDispatchesExport = async (format) => {
    setExporting(true);
    try {
      const params = {};
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (odExMonth && odExMonth !== 'all') params.month = odExMonth;
      if (odExState && odExState !== 'all') params.state = odExState;
      if (odExCustomer && odExCustomer !== 'all') params.customer_code = odExCustomer;
      if (exportIncludeCN) params.include_credit_notes = true;
      const res = await axios.get(`${API}/dispatches/export/${format}`, {
        params, withCredentials: true, responseType: 'blob',
        // Long timeout — PDF generation can take 30-90s on large
        // dispatches sets. Without this axios cuts the connection at
        // its default and the response body is lost.
        timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const scope = [filterDivision && filterDivision !== 'all' ? filterDivision : 'All',
        odExMonth !== 'all' ? odExMonth : null,
        odExState !== 'all' ? odExState : null].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]+/g, '');
      link.setAttribute('download', `Dispatches_${scope}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Dispatches ${format.toUpperCase()} downloaded`);
      setOdExportKind(null);
    } catch (err) {
      // axios is configured with responseType: 'blob', so even error
      // responses arrive as a Blob. Read the Blob text and try to
      // extract the JSON `detail` so the toast shows the real cause.
      // If err.response is missing entirely it's a timeout / network
      // error — Railway proxy closes the connection mid-stream when
      // PDF generation takes too long.
      let msg = err.message || 'Unknown error';
      const status = err.response?.status;
      try {
        const blobText = await (err.response?.data?.text?.() || Promise.resolve(''));
        if (blobText) {
          try {
            const parsed = JSON.parse(blobText);
            if (parsed?.detail) msg = parsed.detail;
          } catch { msg = blobText.slice(0, 300); }
        } else if (err.code === 'ECONNABORTED' || !err.response) {
          msg = 'Server timed out while building the PDF — the dataset is likely too large. Narrow filters (pick a customer / state) or use Excel export.';
        }
      } catch {}
      toast.error(`Export failed${status ? ` (${status})` : ''}: ${msg}`, { duration: 8000 });
    } finally {
      setExporting(false);
    }
  };

  // Edit order - populate dialog and open
  const handleEditOrder = (order) => {
    setManualOrder({
      order_no: order.order_no || '',
      division: order.division || '',
      customer_code: order.customer_code || '',
      shop_name: order.shop_name || '',
      trade_name: order.trade_name_uploaded || order.trade_name || '',   // edit what is stored
      product_code: order.product_code || '',
      units_ordered: order.units_ordered || 1,
      order_date: (order.order_date || '').slice(0, 10),
      state: order.state || '',
      city: order.city || '',
      category: order.category || '',
      ex_mill_price: order.ex_mill_price || 0,
      discount_pct: order.discount_pct || 0,
      remark: order.remark || '',
    });
    setEditOrderMode(true);
    setEditOrderId(order.order_line_id);
    setManualOrderOpen(true);
  };

  // Edit dispatch - populate dialog and open
  const handleEditDispatch = (dispatch) => {
    setManualDispatch({
      division: dispatch.division || '',
      plant_invoice: dispatch.plant_invoice || '',
      delivery_date: (dispatch.delivery_date || '').slice(0, 10),
      customer_code: dispatch.customer_code || '',
      customer_name: dispatch.customer_name || '',
      po_no: dispatch.po_no || '',
      po_base: dispatch.po_base || '',
      trade_name: dispatch.trade_name_uploaded || dispatch.trade_name || '',   // edit what is stored
      product_code: dispatch.product_code || '',
      customer_reference: dispatch.customer_reference || '',
      batch_no: dispatch.batch_no || '',
      batch_qty_metres: dispatch.batch_qty_metres || 0,
      no_of_pieces: dispatch.no_of_pieces || 1,
      so_price: dispatch.so_price || 0,
      net_rate: dispatch.net_rate || 0,
      net_value: dispatch.net_value || 0,
      trade_discount_pct: dispatch.trade_discount_pct || 0,
      other_discount_pct: dispatch.other_discount_pct || 0,
      state: dispatch.state || '',
      city: dispatch.city || '',
      remarks: dispatch.remarks || '',
    });
    setEditDispatchMode(true);
    setEditDispatchId(dispatch.dispatch_id);
    setManualDispatchOpen(true);
  };

  // Delete handler — supports single-line or whole-record deletion. When
  // `entire` is true: for order rows every line sharing the same order_no is
  // soft-deleted (by-order-no); for dispatch rows every batch line sharing the
  // same plant_invoice is soft-deleted (by-invoice). Both scope by division.
  const handleDeleteConfirm = async (entire = false) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { type, item } = deleteTarget;
      if (type === 'order') {
        if (entire) {
          const res = await axios.delete(
            `${API}/orders/by-order-no/${encodeURIComponent(item.order_no)}`,
            { params: { division: item.division }, withCredentials: true }
          );
          const linesDeleted = res?.data?.lines_deleted || 0;
          toast.success(`Deleted ${linesDeleted} line(s) of order ${item.order_no}`);
        } else {
          await axios.delete(`${API}/orders/${item.order_line_id}`, { withCredentials: true });
          toast.success('Order line deleted');
        }
        fetchOrdersTable();
      } else {
        if (entire) {
          const res = await axios.delete(
            `${API}/dispatches/by-invoice/${encodeURIComponent(item.plant_invoice)}`,
            { params: { division: item.division }, withCredentials: true }
          );
          const linesDeleted = res?.data?.lines_deleted || 0;
          toast.success(`Deleted ${linesDeleted} line(s) of invoice ${item.plant_invoice}`);
        } else {
          await axios.delete(`${API}/dispatches/${item.dispatch_id}`, { withCredentials: true });
          toast.success('Dispatch deleted');
        }
        fetchDispatchesTable();
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Manual dispatch submit
  const handleManualDispatchSubmit = async () => {
    const { division, customer_code, customer_name, trade_name, delivery_date, no_of_pieces } = manualDispatch;
    if (!division || !customer_code || !customer_name || !trade_name || !delivery_date || no_of_pieces <= 0) {
      toast.error('Division, Customer Code, Customer Name, Trade Name, Delivery Date, and Pieces are required');
      return;
    }
    setDispatchSubmitting(true);
    try {
      if (editDispatchMode && editDispatchId) {
        await axios.put(`${API}/dispatches/${editDispatchId}`, manualDispatch, { withCredentials: true });
        toast.success('Dispatch updated');
      } else {
        await axios.post(`${API}/dispatches/manual`, manualDispatch, { withCredentials: true });
        toast.success('Dispatch created');
      }
      setManualDispatchOpen(false);
      resetDispatchForm();
      fetchDispatchesTable();
      fetchData();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save dispatch');
    } finally {
      setDispatchSubmitting(false);
    }
  };

  const resetDispatchForm = () => {
    setManualDispatch({
      division: '', plant_invoice: '', delivery_date: new Date().toISOString().slice(0, 10),
      customer_code: '', customer_name: '', po_no: '', po_base: '',
      trade_name: '', product_code: '', customer_reference: '', batch_no: '',
      batch_qty_metres: 0, no_of_pieces: 1, so_price: 0, net_rate: 0,
      net_value: 0, trade_discount_pct: 0, other_discount_pct: 0,
      state: '', city: '', remarks: '',
    });
    setEditDispatchMode(false);
    setEditDispatchId(null);
  };

  const updateDispatchField = (field, value) => {
    setManualDispatch(prev => ({ ...prev, [field]: value }));
  };

  const updateManualField = (field, value) => {
    setManualOrder(prev => ({ ...prev, [field]: value }));
  };

  // Sort helper
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 text-gray-400" />;
    return sortDir === 'asc' ? <ChevronUp size={14} className="ml-1 text-indigo-600" /> : <ChevronDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Filtered + sorted customer data — uses globalSearch for local filter too
  const filteredCustomers = useMemo(() => {
    if (!data?.customer_breakdown) return [];
    let list = [...data.customer_breakdown];
    if (globalSearch) {
      const term = globalSearch.toLowerCase();
      list = list.filter(c =>
        (c.customer_name || '').toLowerCase().includes(term) ||
        (c.customer_code || '').toLowerCase().includes(term)
      );
    }
    list.sort((a, b) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    return list;
  }, [data?.customer_breakdown, globalSearch, sortField, sortDir]);

  // Group monthly trend by FY (Apr-Mar) — must be before early returns (hooks rule)
  const MONTH_LABELS_SHORT = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'};
  const fyGroupedTrend = useMemo(() => {
    const trend = data?.monthly_trend || [];
    if (!trend.length) return [];
    const groups = {};
    trend.forEach(m => {
      const [y, mm] = (m.month || '').split('-').map(Number);
      if (!y || !mm) return;
      const fy = mm >= 4 ? y : y - 1;
      const fyLabel = `FY ${fy}-${String(fy + 1).slice(-2)}`;
      if (!groups[fyLabel]) groups[fyLabel] = { fy, fyLabel, months: [], totalPieces: 0, totalMetres: 0, totalNetValue: 0, totalGrossValue: 0, totalCustomers: new Set() };
      groups[fyLabel].months.push({ ...m, monthNum: mm, year: y, label: `${MONTH_LABELS_SHORT[mm]} ${y}` });
      groups[fyLabel].totalPieces += m.pieces || 0;
      groups[fyLabel].totalMetres += m.metres || 0;
      groups[fyLabel].totalNetValue += m.net_value || 0;
      groups[fyLabel].totalGrossValue += m.gross_value || 0;
    });
    // Sort FYs descending, months within FY by month order (Apr=4 first)
    return Object.values(groups)
      .sort((a, b) => b.fy - a.fy)
      .map(g => ({
        ...g,
        totalMetres: Math.round(g.totalMetres * 100) / 100,
        totalNetValue: Math.round(g.totalNetValue * 100) / 100,
        months: g.months.sort((a, b) => {
          const aOrder = a.monthNum >= 4 ? a.monthNum - 4 : a.monthNum + 8;
          const bOrder = b.monthNum >= 4 ? b.monthNum - 4 : b.monthNum + 8;
          return aOrder - bOrder;
        }),
      }));
  }, [data?.monthly_trend]);

  // Filtered + sorted products for Products tab
  const filteredProducts = useMemo(() => {
    if (!data?.product_breakdown) return [];
    let list = [...data.product_breakdown];
    if (productSearch) {
      const term = productSearch.toLowerCase();
      list = list.filter(p => (p.trade_name || '').toLowerCase().includes(term));
    }
    list.sort((a, b) => {
      const key = productSort;
      let aVal = a[key], bVal = b[key];
      if (key === 'trade_name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return productSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return productSortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return list;
  }, [data?.product_breakdown, productSearch, productSort, productSortDir]);

  // Chart data: club all surplus into one "Surplus Dispatch" bucket, keep Mixed Dispatch
  const chartProducts = useMemo(() => {
    if (!filteredProducts.length) return [];
    const surplusBucket = { trade_name: 'Surplus Dispatch', total_value: 0 };
    const rest = [];
    filteredProducts.forEach(p => {
      if (p.is_surplus) {
        surplusBucket.total_value += p.total_value || 0;
      } else {
        rest.push({ trade_name: p.trade_name, total_value: p.total_value || 0 });
      }
    });
    if (surplusBucket.total_value > 0) rest.push(surplusBucket);
    return rest.sort((a, b) => b.total_value - a.total_value);
  }, [filteredProducts]);

  // Quarter mapping for Indian FY: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
  const QUARTER_MONTHS = { q1: [4,5,6], q2: [7,8,9], q3: [10,11,12], q4: [1,2,3] };

  // Dynamic pie chart data based on selected period
  const pieData = useMemo(() => {
    const raw = data?.monthly_product_breakdown || [];
    if (!raw.length) return chartProducts; // fallback to overall

    // Filter raw data by selected period
    let filtered = raw;
    if (piePeriod !== 'fy') {
      if (piePeriod.startsWith('q')) {
        const qMonths = QUARTER_MONTHS[piePeriod] || [];
        filtered = raw.filter(item => {
          const mm = parseInt((item.month || '').split('-')[1], 10);
          return qMonths.includes(mm);
        });
      } else {
        // Specific month like '2024-04'
        filtered = raw.filter(item => item.month === piePeriod);
      }
    }

    // Same product search as the tables, so the card never disagrees with them
    if (productSearch) {
      const q = productSearch.toLowerCase();
      filtered = filtered.filter(item => (item.trade_name || '').toLowerCase().includes(q));
    }

    // Aggregate by trade_name
    const map = {};
    filtered.forEach(item => {
      const tn = item.trade_name || 'Unknown';
      if (!map[tn]) map[tn] = { trade_name: tn, total_value: 0, total_pieces: 0, total_metres: 0 };
      map[tn].total_value += item.total_value || 0;
      map[tn].total_pieces += item.total_pieces || 0;
      map[tn].total_metres += item.total_metres || 0;
    });
    return Object.values(map).sort((a, b) => b.total_value - a.total_value);
  }, [data?.monthly_product_breakdown, piePeriod, chartProducts, productSearch]);

  // Available months for period selector — only months that have data AND up to current month
  const availableMonths = useMemo(() => {
    const raw = data?.monthly_product_breakdown || [];
    const months = new Set();
    raw.forEach(item => { if (item.month) months.add(item.month); });
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return [...months].filter(m => m <= currentMonth).sort();
  }, [data?.monthly_product_breakdown]);

  // Region-filtered product pie data for Region × Product view (with period filtering)
  const regionPieData = useMemo(() => {
    const rpData = data?.region_product_breakdown || [];
    if (!rpData.length) return [];
    let filtered = rpData;
    // Filter by region
    if (pieRegion) filtered = filtered.filter(rp => (rp.state || '').toUpperCase() === pieRegion.toUpperCase());
    // Filter by period
    if (piePeriod !== 'fy') {
      if (piePeriod.startsWith('q')) {
        const qMonths = QUARTER_MONTHS[piePeriod] || [];
        filtered = filtered.filter(rp => {
          const mm = parseInt((rp.month || '').split('-')[1], 10);
          return qMonths.includes(mm);
        });
      } else {
        filtered = filtered.filter(rp => rp.month === piePeriod);
      }
    }
    if (productSearch) {
      const q = productSearch.toLowerCase();
      filtered = filtered.filter(rp => (rp.trade_name || '').toLowerCase().includes(q));
    }
    const map = {};
    filtered.forEach(rp => {
      const tn = rp.trade_name || 'Unknown';
      if (!map[tn]) map[tn] = { trade_name: tn, total_value: 0, total_pieces: 0, total_metres: 0 };
      map[tn].total_value += rp.total_value || 0;
      map[tn].total_pieces += rp.total_pieces || 0;
      map[tn].total_metres += rp.total_metres || 0;
    });
    return Object.values(map).sort((a, b) => b.total_value - a.total_value);
  }, [data?.region_product_breakdown, pieRegion, piePeriod, productSearch]);

  // Chart slices: the top N qualities + one "Others" slice, so the chart and
  // the table below cover the same total. "Auto" picks the smallest N that
  // keeps Others under a quarter of the total — one big grey area says
  // nothing. Memoised: Recharts 3 restarts the entrance animation (labels
  // hidden, sectors at zero) whenever its data identity changes.
  const OTHERS_COLOR = '#CBD5E1';
  const activePie = productView === 'region' ? regionPieData : pieData;
  const { pieSlices, pieTop } = useMemo(() => {
    const positive = activePie.filter(p => (p.total_value || 0) > 0);  // a chart can't draw a net return
    const total = positive.reduce((t, p) => t + p.total_value, 0);
    let top;
    if (productTopChoice === 'all') top = positive.length;
    else if (productTopChoice === 'auto') {
      top = positive.length;
      for (const n of [12, 20, 30, 50]) {
        const others = positive.slice(n).reduce((t, p) => t + p.total_value, 0);
        if (total > 0 && others / total <= 0.25) { top = n; break; }
      }
    } else top = parseInt(productTopChoice, 10) || 12;
    if (positive.length <= top) return { pieSlices: positive, pieTop: positive.length };
    const others = positive.slice(top).reduce((o, p) => ({
      ...o,
      total_value: o.total_value + (p.total_value || 0),
      total_pieces: o.total_pieces + (p.total_pieces || 0),
      total_metres: o.total_metres + (p.total_metres || 0),
    }), { trade_name: `Others (${positive.length - top})`, total_value: 0, total_pieces: 0, total_metres: 0, _others: true });
    return { pieSlices: [...positive.slice(0, top), others], pieTop: top };
  }, [activePie, productTopChoice]);
  const sliceColor = (i) => i < pieTop ? PRODUCT_COLORS[i % PRODUCT_COLORS.length] : OTHERS_COLOR;

  // The chart itself — pie, donut, horizontal bars or treemap over the same
  // slices. Not a hook; called inside the card's render.
  const renderProductChart = (totalVal) => {
    const tip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const d = payload[0].payload;
      const pct = totalVal > 0 ? ((d.total_value / totalVal) * 100).toFixed(1) : 0;
      return (
        <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
          <p className="font-bold text-sm mb-1">{d.trade_name}</p>
          <p>Value: <strong>{fmtLakhs(d.total_value)}</strong> ({pct}%)</p>
          {d.total_metres > 0 && <p>Metres: <strong>{fmtDec(d.total_metres)}</strong></p>}
          {d.total_pieces > 0 && <p>Pieces: <strong>{fmtNum(d.total_pieces)}</strong></p>}
        </div>
      );
    };
    if (productChartType === 'bars') {
      const h = Math.max(320, pieSlices.length * 26 + 40);
      return (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={pieSlices} layout="vertical" margin={{ top: 4, right: 70, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => fmtLakhs(v)} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="trade_name" width={170} tick={{ fontSize: 11 }} interval={0} />
            <Tooltip content={tip} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
            <Bar dataKey="total_value" isAnimationActive={false} radius={[0, 4, 4, 0]}
              label={{ position: 'right', fontSize: 10, formatter: (v) => totalVal > 0 ? `${((v / totalVal) * 100).toFixed(1)}%` : '' }}>
              {pieSlices.map((_, i) => <Cell key={i} fill={sliceColor(i)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (productChartType === 'treemap') {
      const tData = pieSlices.map((p, i) => ({ ...p, name: p.trade_name, size: p.total_value, fill: sliceColor(i) }));
      const TreeCell = ({ x, y, width, height, name, size, fill }) => {
        if (width == null || height == null) return null;
        const pct = totalVal > 0 && size ? ((size / totalVal) * 100).toFixed(1) : null;
        const roomy = width > 70 && height > 34;
        return (
          <g>
            <rect x={x} y={y} width={width} height={height} rx={3} fill={fill} stroke="#fff" strokeWidth={2} />
            {roomy && (
              <>
                <text x={x + 6} y={y + 15} fontSize={11} fontWeight={600} fill="#fff">{(name || '').length > Math.floor(width / 7) ? `${(name || '').slice(0, Math.max(3, Math.floor(width / 7) - 1))}…` : name}</text>
                {pct && height > 48 && <text x={x + 6} y={y + 30} fontSize={10} fill="rgba(255,255,255,0.9)">{pct}%</text>}
              </>
            )}
          </g>
        );
      };
      return (
        <ResponsiveContainer width="100%" height={380}>
          <Treemap data={tData} dataKey="size" nameKey="name" aspectRatio={4 / 3} isAnimationActive={false} content={<TreeCell />}>
            <Tooltip content={tip} />
          </Treemap>
        </ResponsiveContainer>
      );
    }
    const donut = productChartType === 'donut';
    return (
      <ResponsiveContainer width="100%" height={380}>
        <PieChart>
          <Pie
            data={pieSlices}
            isAnimationActive={false}
            dataKey="total_value"
            nameKey="trade_name"
            cx="50%"
            cy="50%"
            innerRadius={donut ? 72 : 0}
            outerRadius={130}
            paddingAngle={donut ? 1 : 0}
            label={({ trade_name, percent }) => percent >= 0.025 ? `${trade_name} (${(percent * 100).toFixed(0)}%)` : ''}
            labelLine={({ percent }) => percent >= 0.025}
          >
            {pieSlices.map((_, i) => <Cell key={i} fill={sliceColor(i)} />)}
          </Pie>
          {donut && (
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize={13} fontWeight={700} fill="#1F2937">
              {fmtLakhs(totalVal)}
            </text>
          )}
          <Tooltip content={tip} />
          {pieSlices.length <= 16 && <Legend />}
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Available quarters — only show quarters that have at least one available month
  const availableQuarters = useMemo(() => {
    const monthNums = new Set(availableMonths.map(m => parseInt(m.split('-')[1], 10)));
    const quarters = [];
    if ([4,5,6].some(m => monthNums.has(m))) quarters.push('q1');
    if ([7,8,9].some(m => monthNums.has(m))) quarters.push('q2');
    if ([10,11,12].some(m => monthNums.has(m))) quarters.push('q3');
    if ([1,2,3].some(m => monthNums.has(m))) quarters.push('q4');
    return quarters;
  }, [availableMonths]);

  const handleProductSort = (field) => {
    if (productSort === field) {
      setProductSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setProductSort(field);
      setProductSortDir(field === 'trade_name' ? 'asc' : 'desc');
    }
  };

  const ProductSortIcon = ({ field }) => {
    if (productSort !== field) return <ArrowUpDown size={12} className="inline ml-1 opacity-30" />;
    return productSortDir === 'asc'
      ? <ChevronUp size={12} className="inline ml-1 text-indigo-600" />
      : <ChevronDown size={12} className="inline ml-1 text-indigo-600" />;
  };

  // Exports the view on screen — All-Products list, FY/month-wise rows, or
  // the Region × Product composition for the selected period/region — and
  // states the scope on the document (it used to dump the whole-FY list
  // whatever was showing, with no scope line).
  const exportProducts = (format) => {
    const periodLabel = piePeriod === 'fy' ? 'Full FY'
      : piePeriod.startsWith('q') ? piePeriod.toUpperCase()
      : (() => { const [y, m] = piePeriod.split('-'); return `${MONTH_NAMES[parseInt(m, 10)]} ${y}`; })();
    const scope = [
      `Division: ${filterDivision && filterDivision !== 'all' ? filterDivision : 'All divisions'}`,
      `Scope: ${data?.scope_label || ''}`,
      `View: ${productView === 'all' ? 'All products' : productView === 'fy' ? 'FY / month-wise' : 'Region × product'}`,
      ...(productView !== 'all' ? [`Period: ${periodLabel}`] : []),
      ...(productView === 'region' ? [`Region: ${pieRegion || 'All regions'}`] : []),
      ...(productSearch ? [`Search: "${productSearch}"`] : []),
      'Values net of goods returns; free samples excluded',
    ];
    let rows, sheet;
    if (productView === 'fy') {
      sheet = 'FY month-wise';
      rows = [];
      fyProductData.forEach(g => g.months.forEach(mo => mo.products.forEach(p => rows.push({
        'FY': g.fyLabel, 'Month': mo.monthLabel, 'Trade Name': p.trade_name,
        'Pieces': p.total_pieces || 0, 'Metres': Number((p.total_metres || 0).toFixed(1)),
        'Avg SO Price': Number((p.avg_so_price || 0).toFixed(2)), 'Avg Net Rate': Number((p.avg_net_rate || 0).toFixed(2)),
        'Eff. Disc %': p.effective_discount_pct || 0, 'Net Value': Number((p.total_value || 0).toFixed(2)),
        'Full Rate': p.full_rate_lines || 0, 'Discounted': p.discounted_lines || 0,
      }))));
    } else if (productView === 'region') {
      sheet = 'Region x Product';
      const totalVal = regionPieData.reduce((t, p) => t + (p.total_value || 0), 0);
      rows = regionPieData.map((p, i) => ({
        '#': i + 1, 'Region': pieRegion || 'All regions', 'Trade Name': p.trade_name,
        'Pieces': p.total_pieces || 0, 'Metres': Number((p.total_metres || 0).toFixed(1)),
        'Net Value': Number((p.total_value || 0).toFixed(2)),
        '% of Total': totalVal > 0 ? Number(((p.total_value || 0) / totalVal * 100).toFixed(1)) : 0,
      }));
    } else {
      sheet = 'Products';
      rows = filteredProducts.map((p, i) => ({
        '#': i + 1,
        'Trade Name': p.trade_name,
        'Pieces': p.total_pieces || 0,
        'Metres': Number((p.total_metres || 0).toFixed(1)),
        'Avg SO Price': Number((p.avg_so_price || 0).toFixed(2)),
        'Avg Net Rate': Number((p.avg_net_rate || 0).toFixed(2)),
        'Eff. Disc %': p.effective_discount_pct || 0,
        'Net Value': Number((p.total_value || 0).toFixed(2)),
        'Full Rate': p.full_rate_lines || 0,
        'Discounted': p.discounted_lines || 0,
        'Customers': p.customer_count || 0,
      }));
    }
    if (!rows.length) { toast.error('Nothing to export for this view'); return; }
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === 'excel') {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheet);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scope.map(l => [l])), 'Scope');
      XLSX.writeFile(wb, `Product_Breakdown_${stamp}.xlsx`);
      toast.success('Excel downloaded');
    } else {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Product Breakdown', 14, 15);
      doc.setFontSize(9);
      doc.text(`${scope.join('  ·  ')}`, 14, 22);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 27);
      autoTable(doc, {
        startY: 32,
        head: [Object.keys(rows[0])],
        body: rows.map(r => Object.values(r)),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [99, 102, 241] },
      });
      doc.save(`Product_Breakdown_${stamp}.pdf`);
      toast.success('PDF downloaded');
    }
  };

  // FY-wise product data grouped from monthly_product_breakdown
  const MONTH_NAMES = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'};
  const fyProductData = useMemo(() => {
    const raw = data?.monthly_product_breakdown || [];
    if (!raw.length) return [];
    const search = productSearch.toLowerCase();

    // Group: FY → month → products
    const fyMap = {};
    raw.forEach(item => {
      const [y, mm] = (item.month || '').split('-').map(Number);
      if (!y || !mm) return;
      if (search && !(item.trade_name || '').toLowerCase().includes(search)) return;
      const fy = mm >= 4 ? y : y - 1;
      const fyLabel = `FY ${fy}-${String(fy + 1).slice(-2)}`;
      if (!fyMap[fyLabel]) fyMap[fyLabel] = { fy, fyLabel, months: {}, totals: {} };
      const monthKey = item.month;
      const monthLabel = `${MONTH_NAMES[mm]} ${y}`;
      if (!fyMap[fyLabel].months[monthKey]) {
        fyMap[fyLabel].months[monthKey] = { monthKey, monthLabel, monthNum: mm, year: y, products: [],
          totalPieces: 0, totalMetres: 0, totalValue: 0, discounted: 0, fullRate: 0 };
      }
      const mo = fyMap[fyLabel].months[monthKey];
      mo.products.push(item);
      mo.totalPieces += item.total_pieces || 0;
      mo.totalMetres += item.total_metres || 0;
      mo.totalValue += item.total_value || 0;
      mo.discounted += item.discounted_lines || 0;
      mo.fullRate += item.full_rate_lines || 0;

      // FY totals by product
      const tn = item.trade_name;
      if (!fyMap[fyLabel].totals[tn]) {
        fyMap[fyLabel].totals[tn] = { trade_name: tn, total_pieces: 0, total_metres: 0, total_value: 0,
          discounted_lines: 0, full_rate_lines: 0, so_sum: 0, so_ct: 0, nr_sum: 0, nr_ct: 0 };
      }
      const t = fyMap[fyLabel].totals[tn];
      t.total_pieces += item.total_pieces || 0;
      t.total_metres += item.total_metres || 0;
      t.total_value += item.total_value || 0;
      t.discounted_lines += item.discounted_lines || 0;
      t.full_rate_lines += item.full_rate_lines || 0;
      if (item.avg_so_price > 0) { t.so_sum += item.avg_so_price * (item.total_pieces || 1); t.so_ct += item.total_pieces || 1; }
      if (item.avg_net_rate > 0) { t.nr_sum += item.avg_net_rate * (item.total_pieces || 1); t.nr_ct += item.total_pieces || 1; }
    });

    return Object.values(fyMap)
      .sort((a, b) => b.fy - a.fy)
      .map(g => {
        const months = Object.values(g.months).sort((a, b) => {
          const aOrd = a.monthNum >= 4 ? a.monthNum - 4 : a.monthNum + 8;
          const bOrd = b.monthNum >= 4 ? b.monthNum - 4 : b.monthNum + 8;
          return aOrd - bOrd;
        });
        months.forEach(mo => {
          mo.products.sort((a, b) => (a.trade_name || '').localeCompare(b.trade_name || ''));
        });
        const fyTotals = Object.values(g.totals).map(t => ({
          ...t,
          total_metres: Math.round(t.total_metres * 100) / 100,
          total_value: Math.round(t.total_value * 100) / 100,
          avg_so_price: t.so_ct > 0 ? Math.round(t.so_sum / t.so_ct * 100) / 100 : 0,
          avg_net_rate: t.nr_ct > 0 ? Math.round(t.nr_sum / t.nr_ct * 100) / 100 : 0,
          effective_discount_pct: t.so_ct > 0 && t.nr_ct > 0
            ? Math.round((1 - (t.nr_sum / t.nr_ct) / (t.so_sum / t.so_ct)) * 1000) / 10
            : 0,
        })).sort((a, b) => (a.trade_name || '').localeCompare(b.trade_name || ''));
        return { ...g, months, fyTotals };
      });
  }, [data?.monthly_product_breakdown, productSearch]);

  const toggleFY = (fyLabel) => setExpandedFYs(prev => ({ ...prev, [fyLabel]: !prev[fyLabel] }));
  const toggleCustFY = (fyLabel) => setExpandedCustFYs(prev => ({ ...prev, [fyLabel]: !prev[fyLabel] }));
  const toggleDispFY = (fyLabel) => setExpandedDispFYs(prev => ({ ...prev, [fyLabel]: !prev[fyLabel] }));
  const toggleOrdFY = (fyLabel) => setExpandedOrdFYs(prev => ({ ...prev, [fyLabel]: !prev[fyLabel] }));

  // Drill-down: fetch and group dispatch detail for a month
  const groupDispatchRecords = (records) => {
    const dateMap = {};
    for (const d of records) {
      const dateKey = (d.delivery_date || '').slice(0, 10) || 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = {};
      const custKey = `${d.customer_code || 'N/A'}|||${d.customer_name || 'Unknown'}`;
      if (!dateMap[dateKey][custKey]) dateMap[dateKey][custKey] = [];
      dateMap[dateKey][custKey].push(d);
    }
    return Object.keys(dateMap).sort((a, b) => b.localeCompare(a)).map(dateKey => {
      const custEntries = Object.entries(dateMap[dateKey]).map(([ck, lines]) => {
        const [code, name] = ck.split('|||');
        return { code, name, lines,
          totalPieces: lines.reduce((s, l) => s + (l.no_of_pieces || 0), 0),
          totalMetres: lines.reduce((s, l) => s + (l.batch_qty_metres || 0), 0),
          totalNetValue: lines.reduce((s, l) => s + (l.net_value || 0), 0),  // net, ex-GST (audit F-28)
        };
      });
      const dateObj = dateKey !== 'Unknown' ? new Date(dateKey + 'T00:00:00') : null;
      return {
        dateKey,
        formattedDate: dateObj ? dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Unknown',
        dayName: dateObj ? dateObj.toLocaleDateString('en-IN', { weekday: 'long' }) : '',
        customers: custEntries,
        dayPieces: custEntries.reduce((s, c) => s + c.totalPieces, 0),
        dayMetres: custEntries.reduce((s, c) => s + c.totalMetres, 0),
        dayNetValue: custEntries.reduce((s, c) => s + c.totalNetValue, 0),
        dayLines: custEntries.reduce((s, c) => s + c.lines.length, 0),
      };
    });
  };

  const toggleDispMonth = async (monthKey) => {
    if (expandedDispMonths[monthKey]?.data) {
      setExpandedDispMonths(prev => { const n = { ...prev }; delete n[monthKey]; return n; });
      return;
    }
    setExpandedDispMonths(prev => ({ ...prev, [monthKey]: { loading: true, data: null } }));
    try {
      const params = { month: monthKey, limit: 5000 };
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      if (filterState) params.state = filterState;
      const res = await axios.get(`${API}/dispatches`, { params });
      const rows = res.data.dispatches || [];
      // Say so when the month has more lines than one fetch returns (F-44).
      if ((res.data.total || 0) > rows.length) {
        toast.warning(`Showing ${rows.length.toLocaleString('en-IN')} of ${res.data.total.toLocaleString('en-IN')} dispatch lines for this month — narrow by division or customer to see all.`);
      }
      const grouped = groupDispatchRecords(rows);
      setExpandedDispMonths(prev => ({ ...prev, [monthKey]: { loading: false, data: grouped } }));
    } catch { setExpandedDispMonths(prev => ({ ...prev, [monthKey]: { loading: false, data: [] } })); }
  };

  // Drill-down: fetch and group order detail for a month
  const groupOrderRecords = (records) => {
    const dateMap = {};
    for (const o of records) {
      const dateKey = (o.order_date || '').slice(0, 10) || 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = {};
      const custKey = `${o.customer_code || 'N/A'}|||${o.shop_name || 'Unknown'}`;
      if (!dateMap[dateKey][custKey]) dateMap[dateKey][custKey] = [];
      dateMap[dateKey][custKey].push(o);
    }
    return Object.keys(dateMap).sort((a, b) => b.localeCompare(a)).map(dateKey => {
      const custEntries = Object.entries(dateMap[dateKey]).map(([ck, lines]) => {
        const [code, name] = ck.split('|||');
        return { code, name, lines,
          totalOrdered: lines.reduce((s, l) => s + (l.units_ordered || 0), 0),
          totalDispatched: lines.reduce((s, l) => s + (l.units_dispatched || 0), 0),
        };
      });
      const dateObj = dateKey !== 'Unknown' ? new Date(dateKey + 'T00:00:00') : null;
      return {
        dateKey,
        formattedDate: dateObj ? dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Unknown',
        dayName: dateObj ? dateObj.toLocaleDateString('en-IN', { weekday: 'long' }) : '',
        customers: custEntries,
        dayOrdered: custEntries.reduce((s, c) => s + c.totalOrdered, 0),
        dayDispatched: custEntries.reduce((s, c) => s + c.totalDispatched, 0),
        dayLines: custEntries.reduce((s, c) => s + c.lines.length, 0),
      };
    });
  };

  const toggleOrdMonth = async (monthKey) => {
    if (expandedOrdMonths[monthKey]?.data) {
      setExpandedOrdMonths(prev => { const n = { ...prev }; delete n[monthKey]; return n; });
      return;
    }
    setExpandedOrdMonths(prev => ({ ...prev, [monthKey]: { loading: true, data: null } }));
    try {
      const params = { month: monthKey, limit: 5000 };
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterCustomer && filterCustomer !== 'all') params.customer_code = filterCustomer;
      const res = await axios.get(`${API}/orders`, { params });
      const rows = res.data.orders || [];
      if ((res.data.total || 0) > rows.length) {
        toast.warning(`Showing ${rows.length.toLocaleString('en-IN')} of ${res.data.total.toLocaleString('en-IN')} order lines for this month — narrow by division or customer to see all.`);
      }
      const grouped = groupOrderRecords(rows);
      setExpandedOrdMonths(prev => ({ ...prev, [monthKey]: { loading: false, data: grouped } }));
    } catch { setExpandedOrdMonths(prev => ({ ...prev, [monthKey]: { loading: false, data: [] } })); }
  };

  // Helper: group monthly data by FY
  const groupByFY = (items, monthKey = 'month') => {
    const fyMap = {};
    items.forEach(item => {
      const [y, mm] = (item[monthKey] || '').split('-').map(Number);
      if (!y || !mm) return;
      const fy = mm >= 4 ? y : y - 1;
      const fyLabel = `FY ${fy}-${String(fy + 1).slice(-2)}`;
      if (!fyMap[fyLabel]) fyMap[fyLabel] = { fy, fyLabel, months: {} };
      const mKey = item[monthKey];
      const mLabel = `${MONTH_NAMES[mm]} ${y}`;
      if (!fyMap[fyLabel].months[mKey]) fyMap[fyLabel].months[mKey] = { monthKey: mKey, monthLabel: mLabel, monthNum: mm, year: y, items: [] };
      fyMap[fyLabel].months[mKey].items.push(item);
    });
    return Object.values(fyMap)
      .sort((a, b) => b.fy - a.fy)
      .map(g => ({
        ...g,
        months: Object.values(g.months).sort((a, b) => {
          const aOrd = a.monthNum >= 4 ? a.monthNum - 4 : a.monthNum + 8;
          const bOrd = b.monthNum >= 4 ? b.monthNum - 4 : b.monthNum + 8;
          return aOrd - bOrd;
        }),
      }));
  };

  // FY-grouped customer data
  const fyCustomerData = useMemo(() => {
    const raw = data?.monthly_customer_breakdown || [];
    if (!raw.length) return [];
    const search = globalSearch.toLowerCase();
    const filtered = search ? raw.filter(c => (c.customer_name || '').toLowerCase().includes(search) || (c.customer_code || '').toLowerCase().includes(search)) : raw;
    return groupByFY(filtered);
  }, [data?.monthly_customer_breakdown, globalSearch]);

  // FY-grouped dispatch summary
  const fyDispatchData = useMemo(() => {
    const raw = data?.monthly_dispatch_summary || [];
    if (!raw.length) return [];
    return groupByFY(raw);
  }, [data?.monthly_dispatch_summary]);

  // FY-grouped order summary
  const fyOrderData = useMemo(() => {
    const raw = data?.monthly_order_summary || [];
    if (!raw.length) return [];
    return groupByFY(raw);
  }, [data?.monthly_order_summary]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const ds = data?.dispatch_summary || {};
  const of = data?.order_fulfillment || {};
  const fillRate = data?.fill_rate || 0;
  const coverage = data?.order_coverage || {};
  const scopeLabel = data?.scope_label || 'this FY';
  const fillRateFY = data?.fill_rate_current_fy || 0;
  // One fill rate for the page's period (current FY by default, or the selected
  // month), capped at 100% — over-dispatch lives in its own "Over Disp." tile.
  const periodFill = Math.min(100, filterMonth === 'all' ? fillRateFY : fillRate);
  const currentMonthLabel = data?.current_month_label || '';
  const gr = data?.gr_summary || {};

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Floating bulk-certify action bar — appears when one or more
          missing-order rows are selected. Admin/accounts only. */}
      {selectedForCertify.size > 0 && (user?.role === 'admin' || user?.role === 'accounts') && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border-2 border-emerald-400 rounded-lg shadow-xl px-4 py-3 flex items-center gap-3">
          <ShieldCheck size={18} className="text-emerald-600" />
          <div className="text-sm">
            <span className="font-semibold text-emerald-700">{selectedForCertify.size}</span>
            <span className="text-slate-600"> dispatch{selectedForCertify.size === 1 ? '' : 'es'} selected</span>
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" onClick={openBulkCertifyDialog}>
            Certify Selected
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearCertifySelection}>
            Clear
          </Button>
        </div>
      )}
      {/* Floating "Tag to…" bar — appears when order lines are selected on
          the Orders tab. Shown to every role; the server decides who may tag. */}
      {activeTab === 'orders' && selectedForTag.size > 0 && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border-2 border-indigo-400 rounded-lg shadow-xl px-4 py-3 flex items-center gap-3">
          <Tag size={18} className="text-indigo-600" />
          <div className="text-sm">
            <span className="font-semibold text-indigo-700">{selectedForTag.size}</span>
            <span className="text-slate-600"> order line{selectedForTag.size === 1 ? '' : 's'} selected</span>
            {tagSelection.codes.length === 1 && tagSelection.customerName && (
              <span className="text-slate-500"> · {tagSelection.customerName}</span>
            )}
            {tagSelection.codes.length > 1 && (
              <span className="text-red-600"> · {tagSelection.codes.length} customers</span>
            )}
          </div>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs" onClick={openTagDialog}>
            Tag to…
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearTagSelection}>
            Clear
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Orders & Dispatch
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track orders, dispatches, fill rates, and goods returns
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditOrderMode(false); setEditOrderId(null); setManualOrder({ order_no: '', division: '', customer_code: '', shop_name: '', trade_name: '', product_code: '', units_ordered: 1, order_date: new Date().toISOString().slice(0, 10), state: '', city: '', category: '', ex_mill_price: 0, discount_pct: 0, remark: '' }); setManualOrderOpen(true); }}>
            <Plus size={16} className="mr-1" /> Add Order
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting}>
            <Download size={16} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting}>
            <Download size={16} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl border border-border/60 px-4 py-2.5 shadow-sm">
        <div className="relative flex-1 min-w-[200px] max-w-[280px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Search customer (all tabs)…"
            value={globalSearch}
            onChange={e => { setGlobalSearch(e.target.value); setDispatchesPage(0); setOrdersPage(0); }}
            className="pl-8 h-9 text-sm border-0 bg-gray-50 focus:bg-white"
          />
        </div>
        <div className="h-6 w-px bg-border/60 hidden md:block" />
        <Select value={filterDivision} onValueChange={setFilterDivision}>
          <SelectTrigger className="h-9 text-sm w-auto min-w-[130px] border-0 bg-gray-50 hover:bg-gray-100">
            <SelectValue placeholder="Division" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Divisions</SelectItem>
            {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="h-9 text-sm w-auto min-w-[120px] border-0 bg-gray-50 hover:bg-gray-100">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {(() => {
              const months = (data?.filters?.months || []).filter(m => m);
              const MONTH_LABELS = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'};
              const fyGroups = {};
              months.forEach(m => {
                const [y, mm] = m.split('-').map(Number);
                const fy = mm >= 4 ? y : y - 1;
                const fyLabel = `FY ${fy}-${String(fy + 1).slice(-2)}`;
                if (!fyGroups[fyLabel]) fyGroups[fyLabel] = [];
                fyGroups[fyLabel].push({ value: m, month: mm, year: y, label: `${MONTH_LABELS[mm]} ${y}` });
              });
              const sortedFYs = Object.keys(fyGroups).sort().reverse();
              return sortedFYs.map(fy => (
                <SelectGroup key={fy}>
                  <SelectLabel className="text-xs font-bold text-indigo-600">{fy}</SelectLabel>
                  {fyGroups[fy].sort((a, b) => b.value.localeCompare(a.value)).map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectGroup>
              ));
            })()}
          </SelectContent>
        </Select>
        <Select value={filterState} onValueChange={setFilterState}>
          <SelectTrigger className="h-9 text-sm w-auto min-w-[110px] border-0 bg-gray-50 hover:bg-gray-100">
            <SelectValue placeholder="State" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {(data?.filters?.states || []).filter(s => s).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCustomer} onValueChange={setFilterCustomer}>
          <SelectTrigger className="h-9 text-sm w-auto min-w-[130px] border-0 bg-gray-50 hover:bg-gray-100">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            {(() => {
              const allC = (data?.filters?.all_customers || data?.filters?.customers || []).filter(c => c.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              const groups = [
                { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                { label: 'S — Z', test: n => n >= 'S' },
              ];
              return groups.map(g => {
                const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                if (!items.length) return null;
                return (
                  <SelectGroup key={g.label}>
                    <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                    {items.map((c, i) => (
                      <SelectItem key={c.code || `name-${i}`} value={c.code || `__name__${c.name}`}>
                        {c.name}{c.code ? ` (${c.code})` : ''}{c.location ? ` · ${c.location}` : ''}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              });
            })()}
          </SelectContent>
        </Select>
        {/* "For" filter — only once a customer is picked, because retailers
            are a list on that one wholesaler's profile. Drives the Orders
            and Dispatches tabs (dispatches follow their matched order line). */}
        {filterCustomer !== 'all' && !filterCustomer.startsWith('__name__') && (activeTab === 'orders' || activeTab === 'dispatches')
          && (filterCustomerIsWs || filterRetailers.length > 0) && (
          <Select value={filterEndCustomer} onValueChange={setFilterEndCustomer}>
            <SelectTrigger className="h-9 text-sm w-auto min-w-[120px] border-0 bg-gray-50 hover:bg-gray-100" title="For — who the wholesaler placed the order for">
              <Tag size={12} className="mr-1.5 text-indigo-500" />
              <SelectValue placeholder="For" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">For: All</SelectItem>
              <SelectItem value="own">Own shop</SelectItem>
              <SelectItem value="untagged">Not tagged</SelectItem>
              {filterRetailers.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs font-bold text-indigo-600">Retailers</SelectLabel>
                  {filterRetailers.map(r => (
                    <SelectItem key={r.retailer_id} value={r.retailer_id}>{r.name}{r.city ? ` · ${r.city}` : ''}</SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        )}
        {/* "Store" filter — ship-to locations under the picked customer's SAP
            code. Only shown when the master knows at least one store for it. */}
        {filterCustomer !== 'all' && !filterCustomer.startsWith('__name__') && filterStores.length > 0 && (activeTab === 'orders' || activeTab === 'dispatches') && (
          <Select value={filterStore} onValueChange={setFilterStore}>
            <SelectTrigger className="h-9 text-sm w-auto min-w-[120px] border-0 bg-gray-50 hover:bg-gray-100" title="Store — which ship-to location the line went to">
              <Store size={12} className="mr-1.5 text-amber-600" />
              <SelectValue placeholder="Store" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {filterStores.some(s => !s.is_head) && (
                <SelectGroup>
                  <SelectLabel className="text-xs font-bold text-amber-700">Stores</SelectLabel>
                  {filterStores.filter(s => !s.is_head).map(s => (
                    <SelectItem key={s.store_id || s.ship_to_code} value={s.ship_to_code}>{s.display_name || s.name || s.ship_to_code}{s.city ? ` · ${s.city}` : ''}</SelectItem>
                  ))}
                </SelectGroup>
              )}
              <SelectItem value="head">SAP code itself (store not specified)</SelectItem>
              <SelectItem value="none">No store</SelectItem>
            </SelectContent>
          </Select>
        )}
        {(filterDivision !== 'all' || filterMonth !== 'all' || filterState !== 'all' || filterCustomer !== 'all' || globalSearch) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-red-500"
            onClick={() => { setFilterDivision('all'); setFilterMonth('all'); setFilterState('all'); setFilterCustomer('all'); setFilterEndCustomer('all'); setFilterStore('all'); setGlobalSearch(''); }}>
            Clear All
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="inline-flex h-10 bg-muted/50 rounded-lg p-1 gap-0.5">
          <TabsTrigger value="overview" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Activity size={13} className="mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="customers" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Users size={13} className="mr-1.5" />Customers</TabsTrigger>
          <TabsTrigger value="products" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><ShoppingBag size={13} className="mr-1.5" />Products</TabsTrigger>
          <TabsTrigger value="returns" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><RotateCcw size={13} className="mr-1.5" />Returns</TabsTrigger>
          <TabsTrigger value="reconciliation" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><ArrowUpDown size={13} className="mr-1.5" />Reconciliation</TabsTrigger>
          <TabsTrigger value="orders" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><FileSpreadsheet size={13} className="mr-1.5" />Orders</TabsTrigger>
          <TabsTrigger value="dispatches" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Truck size={13} className="mr-1.5" />Dispatches</TabsTrigger>
          <TabsTrigger value="mix_auths" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Package size={13} className="mr-1.5" />Mix Bales</TabsTrigger>
          <TabsTrigger value="import" className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Upload size={13} className="mr-1.5" />Import</TabsTrigger>
        </TabsList>

        {/* TAB 1: OVERVIEW */}
        <TabsContent value="overview" className="space-y-5 mt-4">
          {/* KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {[
              // Net of goods returns for the period: credit notes are subtracted
              // in the month they were raised, whether or not the original
              // dispatch could be matched. The sub-line shows what was returned.
              { icon: IndianRupee, color: 'text-blue-600 bg-blue-50', label: 'Net Sales', value: fmtLakhs(ds.total_net_value), sub: (ds.returned_value || 0) > 0 ? `excl GST · after ${fmtLakhs(ds.returned_value)} returns` : 'excl GST · no returns' },
              // Realised selling rate (ex-GST, per metre) — the KPI that shows
              // whether revenue moved on VOLUME or on PRICE, which neither
              // gross sales nor a piece count can reveal. Pieces and metres
              // move to the sub-line so no information is lost.
              { icon: Package, color: 'text-emerald-600 bg-emerald-50', label: 'Avg Rate', value: ds.avg_rate_per_m != null ? `₹${fmtNum(Math.round(ds.avg_rate_per_m))}/m` : '—', sub: `${fmtNum(ds.total_pieces)} pcs · ${(ds.total_metres || 0) >= 1000 ? Math.round((ds.total_metres || 0) / 1000) + 'k' : fmtDec(ds.total_metres)} m` },
              { icon: Users, color: 'text-purple-600 bg-purple-50', label: 'Customers', value: fmtNum(ds.total_customers), sub: 'billed' },
              { icon: CheckCircle, color: periodFill >= 80 ? 'text-green-600 bg-green-50' : periodFill >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50', label: 'Fill Rate', value: `${periodFill}%`, sub: filterMonth === 'all' ? 'this FY' : 'order fill' },
              { icon: TrendingUp, color: 'text-teal-600 bg-teal-50', label: 'Avg/Customer', value: fmtLakhs(ds.total_customers > 0 ? (ds.total_net_value || 0) / ds.total_customers : 0), sub: 'net sale' },
              // Order Coverage — the honesty check on Fill Rate. Fill Rate is
              // measured only over orders that exist in the system, so
              // shipments with no order copy are invisible to it.
              { icon: FileText, color: coverage.coverage_pct >= 90 ? 'text-green-600 bg-green-50' : coverage.coverage_pct >= 70 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50', label: 'Order Coverage', value: `${coverage.coverage_pct ?? 0}%`, sub: `${fmtNum(coverage.missing_order?.invoices || 0)} copies missing` },
            ].map(({ icon: Icon, color, label, value, sub }, idx) => (
              <div key={`${label}-${idx}`} className="flex items-center gap-3 bg-white rounded-xl border border-border/40 px-3 py-3 shadow-sm">
                <div className={`p-2 rounded-lg ${color.split(' ').slice(1).join(' ')}`}>
                  <Icon size={16} className={color.split(' ')[0]} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold leading-tight">{value}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{label} &middot; {sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Plain-language explainer. Fill Rate and Order Coverage are easy to
              misread on their own — this states in ordinary words what each one
              counts, what it does NOT count, and what to do about the gap. */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2.5 flex-wrap">
              <p className="text-xs font-semibold text-slate-700">
                How to read these numbers
              </p>
              <span className="text-[10px] text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                Everything on this page covers <b>{scopeLabel}</b> only
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] leading-relaxed text-slate-600">
              <div className="md:col-span-2">
                <p className="font-semibold text-slate-700 mb-0.5">
                  Avg Rate — {ds.avg_rate_per_m != null ? `₹${fmtNum(Math.round(ds.avg_rate_per_m))} per metre` : 'not available'}
                </p>
                <p>
                  The average rate we actually <b>realised per metre</b> in {scopeLabel},
                  after discounts and before GST. Read it next to Net Sales: if sales rose
                  but this fell, the growth came from <b>volume at a lower price</b>, not from
                  better rates.
                  {ds.avg_rate_basis_pct != null && ds.avg_rate_basis_pct < 99 && (
                    <> Based on {ds.avg_rate_basis_pct}% of dispatched value — the rest
                    ({fmtLakhs(ds.value_without_metres || 0)}) is on lines with no metres
                    recorded (e.g. pack-priced combo/RTS), which cannot have a per-metre rate.</>
                  )}
                </p>
              </div>
              <div>
                <p className="font-semibold text-slate-700 mb-0.5">
                  Fill Rate — {periodFill}%
                </p>
                <p>
                  Of everything customers <b>ordered</b> in {scopeLabel}, this much has been
                  dispatched. It only looks at orders that exist in the system, so a shipment
                  sent without an order copy is <b>not counted here at all</b> — neither as
                  ordered nor as dispatched.
                </p>
              </div>
              <div>
                <p className="font-semibold text-slate-700 mb-0.5">
                  Order Coverage — {coverage.coverage_pct ?? 0}%
                </p>
                <p>
                  Of everything we actually <b>shipped</b> in {scopeLabel}, this much can be
                  traced to an order copy, a mix-bale authorisation, or was marked a deliberate direct dispatch.
                  The rest went out with <b>no order record</b>. This is the honesty check on
                  Fill Rate: if coverage is low, Fill Rate is only describing part of the business.
                </p>
              </div>
            </div>
            {(coverage.total_dispatch_pieces || 0) > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]">
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <p className="font-semibold text-green-700">
                    {fmtNum(coverage.matched?.pieces || 0)} pcs matched to an order
                  </p>
                  <p className="text-slate-500">{fmtNum(coverage.matched?.invoices || 0)} invoices &middot; the normal path</p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <p className="font-semibold text-sky-700">
                    {fmtNum(coverage.bale_authorised?.pieces || 0)} pcs mix bales, authorised
                  </p>
                  <p className="text-slate-500">{fmtNum(coverage.bale_authorised?.invoices || 0)} invoices &middot; matched to a bale authorisation</p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                  <p className="font-semibold text-slate-700">
                    {fmtNum(coverage.certified_direct?.pieces || 0)} pcs direct dispatch
                  </p>
                  <p className="text-slate-500">Approved as intentional &middot; not a mistake</p>
                </div>
                <div className="bg-white rounded-lg border border-red-200 px-3 py-2">
                  <p className="font-semibold text-red-700">
                    {fmtNum(coverage.missing_order?.pieces || 0)} pcs with no order copy
                  </p>
                  <p className="text-slate-500">
                    {fmtNum(coverage.missing_order?.invoices || 0)} invoices &middot;{' '}
                    {fmtNum(coverage.missing_order?.customers || 0)} customers &middot; needs uploading
                  </p>
                </div>
              </div>
            )}
            {(coverage.missing_order?.invoices || 0) > 0 && (
              <p className="mt-2.5 text-[11px] text-slate-600">
                <b>{fmtNum(coverage.missing_order.invoices)} order copies</b> are missing for
                shipments already sent in {scopeLabel}. Open the <b>Dispatches</b> tab and use{' '}
                <b>Missing List</b> to see exactly which ones to chase — uploading them will
                also make Fill Rate more accurate. If a shipment was deliberately sent without
                an order, mark it <b>direct dispatch</b> so it stops counting as missing.
              </p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground -mt-2 px-1">
            {filterMonth === 'all'
              ? <>All figures cover the <strong>current financial year</strong> (use the month filter to narrow). All values are <strong>net, before GST</strong>.</>
              : <>All figures cover <strong>{currentMonthLabel || 'the selected month'}</strong>. All values are <strong>net, before GST</strong>.</>}
          </p>

          {/* Fulfillment + Returns — compact row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Order Fulfillment */}
            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                <p className="text-sm font-semibold mb-3">Order Fulfillment</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'pending', label: 'Pending', bg: 'bg-gray-50', text: 'text-gray-700', icon: Clock },
                    { key: 'partial', label: 'Partial', bg: 'bg-amber-50', text: 'text-amber-700', icon: AlertTriangle },
                    { key: 'fulfilled', label: 'Fulfilled', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle },
                  ].map(({ key, label, bg, text, icon: Icon }) => (
                    <div key={key} className={`${bg} rounded-lg p-2.5 text-center`}>
                      <Icon size={14} className={`${text} mx-auto mb-1`} />
                      <p className={`text-lg font-bold ${text}`}>{of[key]?.count || 0}</p>
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Returns Summary */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-semibold mb-3">Returns</p>
                <div className="space-y-2">
                  {[
                    { key: 'at_office', label: 'At Office', dot: 'bg-amber-500' },
                    { key: 'sent_to_mill', label: 'Sent to Mill', dot: 'bg-blue-500' },
                    { key: 'credit_note_issued', label: 'Credit Note', dot: 'bg-green-500' },
                  ].map(({ key, label, dot }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${dot}`} />
                        <span className="text-xs">{label}</span>
                      </div>
                      <span className="text-sm font-bold">{gr[key]?.count || 0} <span className="text-xs font-normal text-muted-foreground">({gr[key]?.units || 0})</span></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Discount Analysis — inline */}
          {data?.discount_summary && data.discount_summary.total_lines > 0 && (
            <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl border border-border/40 px-4 py-3 shadow-sm">
              <span className="text-sm font-semibold text-muted-foreground">Discounts</span>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs">Full Rate: <strong className="text-green-700">{data.discount_summary.full_rate_lines}</strong> ({fmtLakhs(data.discount_summary.full_rate_value)})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs">Discounted: <strong className="text-amber-700">{data.discount_summary.discounted_lines}</strong> ({fmtLakhs(data.discount_summary.discounted_value)})</span>
              </div>
              {data.discount_summary.surplus_lines > 0 && (
                <div className="flex items-center gap-1.5" title="Surplus / stock-lot — non-fresh goods, tracked separately from fresh sales">
                  <div className="w-2 h-2 rounded-full bg-indigo-500" />
                  <span className="text-xs">Surplus: <strong className="text-indigo-700">{data.discount_summary.surplus_lines}</strong> ({fmtLakhs(data.discount_summary.surplus_value)})</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground">Avg Trade: <strong>{data.discount_summary.avg_trade_discount}%</strong></span>
              <span className="text-xs text-muted-foreground">Avg Other: <strong>{data.discount_summary.avg_other_discount}%</strong></span>
            </div>
          )}

          {/* FY-wise Sales Breakdown */}
          {fyGroupedTrend.length > 0 ? fyGroupedTrend.map(fyGroup => (
            <Card key={fyGroup.fyLabel}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">{fyGroup.fyLabel}</CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">Net (ex-GST): <span className="font-bold text-foreground">{fmtLakhs(fyGroup.totalNetValue)}</span></span>
                    <span className="text-muted-foreground">{fmtNum(fyGroup.totalPieces)} pcs</span>
                    <span className="text-muted-foreground">{fmtDec(fyGroup.totalMetres)} m</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Month-wise chart */}
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={fyGroup.months}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => fmtLakhs(v)} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Net Value') return [fmtLakhs(value), name];
                        return [fmtNum(value), name];
                      }}
                    />
                    <Legend />
                    <Bar isAnimationActive={false} yAxisId="left" dataKey="pieces" name="Pieces" fill="#818CF8" radius={[4, 4, 0, 0]} />
                    <Bar isAnimationActive={false} yAxisId="right" dataKey="net_value" name="Net Value" fill="#34D399" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {/* Month-wise table */}
                <div className="overflow-x-auto mt-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Metres</TableHead>
                        <TableHead className="text-right">Pieces</TableHead>
                        <TableHead className="text-right">Net Value</TableHead>
                        <TableHead className="text-right">Customers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fyGroup.months.map(m => (
                        <TableRow key={m.month}>
                          <TableCell className="font-medium">{m.label}</TableCell>
                          <TableCell className="text-right">{fmtNum(m.pieces)}</TableCell>
                          <TableCell className="text-right">{fmtDec(m.metres)}</TableCell>
                          <TableCell className="text-right font-semibold">{fmtLakhs(m.net_value)}</TableCell>
                          <TableCell className="text-right">{m.customers}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell>{fyGroup.fyLabel} Total</TableCell>
                        <TableCell className="text-right">{fmtDec(fyGroup.totalMetres)}</TableCell>
                        <TableCell className="text-right">{fmtNum(fyGroup.totalPieces)}</TableCell>
                        <TableCell className="text-right">{fmtLakhs(fyGroup.totalNetValue)}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">No dispatch data available</p>
              </CardContent>
            </Card>
          )}

          {/* State-wise Breakdown */}
          {(data?.state_breakdown || []).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <MapPin size={18} /> State-wise Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Metres</TableHead>
                        <TableHead className="text-right">Pieces</TableHead>
                        <TableHead className="text-right">Net Value</TableHead>
                        <TableHead className="text-right">Customers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.state_breakdown.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{s.state}</TableCell>
                          <TableCell className="text-right">{fmtDec(s.total_metres)}</TableCell>
                          <TableCell className="text-right">{fmtNum(s.total_pieces)}</TableCell>
                          <TableCell className="text-right">{fmtLakhs(s.total_value)}</TableCell>
                          <TableCell className="text-right">{s.customer_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB 2: CUSTOMERS */}
        <TabsContent value="customers" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">Customer-wise FY Breakdown</p>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{filteredCustomers.length} customers</Badge>
            </div>
          </div>

          {(
            <>
              {fyCustomerData.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-muted-foreground">No period-wise customer data</CardContent></Card>
              ) : fyCustomerData.map(fyG => {
                const fyTotPcs = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, c) => ss + (c.total_pieces || 0), 0), 0);
                const fyTotVal = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, c) => ss + (c.total_value || 0), 0), 0);
                return (
                  <Card key={fyG.fyLabel} className="border-2">
                    <CardHeader className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => toggleCustFY(fyG.fyLabel)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedCustFYs[fyG.fyLabel] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          <CardTitle className="text-lg font-bold text-indigo-700">{fyG.fyLabel}</CardTitle>
                          <Badge variant="outline">{fyG.months.length} months</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span><strong>{fmtNum(fyTotPcs)}</strong> pcs</span>
                          <span><strong>{fmtLakhs(fyTotVal)}</strong></span>
                        </div>
                      </div>
                    </CardHeader>
                    {expandedCustFYs[fyG.fyLabel] && (
                      <CardContent className="pt-0 space-y-4">
                        {fyG.months.map(mo => {
                          const moTotPcs = mo.items.reduce((s, c) => s + (c.total_pieces || 0), 0);
                          const moTotVal = mo.items.reduce((s, c) => s + (c.total_value || 0), 0);
                          return (
                            <div key={mo.monthKey} className="border rounded-lg">
                              <div className="flex items-center justify-between px-4 py-2 bg-muted/30">
                                <p className="text-sm font-semibold">{mo.monthLabel}</p>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                  <span>{mo.items.length} customers</span>
                                  <span>{fmtNum(moTotPcs)} pcs</span>
                                  <span>{fmtLakhs(moTotVal)}</span>
                                </div>
                              </div>
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>#</TableHead>
                                      <TableHead>Customer</TableHead>
                                      <TableHead className="text-right">Metres</TableHead>
                                      <TableHead className="text-right">Pieces</TableHead>
                                      <TableHead className="text-right">Net Value</TableHead>
                                      <TableHead className="text-right">Designs</TableHead>
                                      <TableHead className="text-right text-green-700">Full Rate</TableHead>
                                      <TableHead className="text-right text-amber-700">Discounted</TableHead>
                                      <TableHead className="text-right">Disc %</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {mo.items.map((c, i) => {
                                      const mCustKey = `${mo.monthKey}|${c.customer_code || c.customer_name}`;
                                      const isMCustExp = expandedMonthlyCustProduct === mCustKey;
                                      const mCustProducts = isMCustExp ? (data?.customer_product_breakdown || []).filter(cp =>
                                        cp.month === mo.monthKey && (cp.customer_code === c.customer_code || cp.customer_name === c.customer_name)
                                      ).sort((a, b) => b.total_value - a.total_value) : [];
                                      return (
                                        <React.Fragment key={i}>
                                          <TableRow
                                            className={`cursor-pointer hover:bg-indigo-50/50 ${isMCustExp ? 'bg-indigo-50/50' : ''}`}
                                            onClick={() => setExpandedMonthlyCustProduct(isMCustExp ? null : mCustKey)}
                                          >
                                            <TableCell className="text-xs text-muted-foreground">
                                              <div className="flex items-center gap-1">
                                                {isMCustExp ? <ChevronDown size={11} className="text-indigo-500" /> : <ChevronRight size={11} />}
                                                {i + 1}
                                              </div>
                                            </TableCell>
                                            <TableCell>
                                              <p className="font-medium text-sm text-indigo-700 hover:underline">{c.customer_name}</p>
                                              <p className="text-xs text-muted-foreground">{c.customer_code}</p>
                                            </TableCell>
                                            <TableCell className="text-right">{fmtDec(c.total_metres)}</TableCell>
                                            <TableCell className="text-right">{fmtNum(c.total_pieces)}</TableCell>
                                            <TableCell className="text-right font-semibold">{fmtLakhs(c.total_value)}</TableCell>
                                            <TableCell className="text-right">{c.trade_names_count}</TableCell>
                                            <TableCell className="text-right text-green-700">{c.full_rate_lines || 0}</TableCell>
                                            <TableCell className="text-right text-amber-700">{c.discounted_lines || 0}</TableCell>
                                            <TableCell className="text-right">
                                              <span className={`font-medium ${(c.discount_pct || 0) > 50 ? 'text-red-600' : (c.discount_pct || 0) > 20 ? 'text-amber-600' : 'text-green-600'}`}>
                                                {c.discount_pct || 0}%
                                              </span>
                                            </TableCell>
                                          </TableRow>
                                          {isMCustExp && (
                                            <TableRow>
                                              <TableCell colSpan={9} className="p-0">
                                                <div className="bg-gray-50 border-y px-4 py-2">
                                                  <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1.5">Products — {c.customer_name} ({mo.monthLabel})</p>
                                                  {mCustProducts.length > 0 ? (
                                                    <table className="w-full text-xs">
                                                      <thead>
                                                        <tr className="text-muted-foreground border-b">
                                                          <th className="text-left py-1 font-medium">#</th>
                                                          <th className="text-left py-1 font-medium">Product</th>
                                                          <th className="text-right py-1 font-medium">Pieces</th>
                                                          <th className="text-right py-1 font-medium">Metres</th>
                                                          <th className="text-right py-1 font-medium">Value</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {mCustProducts.map((cp, pi) => (
                                                          <tr key={cp.trade_name} className="border-t border-gray-100">
                                                            <td className="py-1 text-muted-foreground">{pi + 1}</td>
                                                            <td className="py-1 font-medium">{cp.trade_name}</td>
                                                            <td className="py-1 text-right">{fmtNum(cp.total_pieces)}</td>
                                                            <td className="py-1 text-right">{fmtDec(cp.total_metres)}</td>
                                                            <td className="py-1 text-right font-semibold">{fmtLakhs(cp.total_value)}</td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  ) : (
                                                    <p className="text-xs text-muted-foreground">No product data for this month</p>
                                                  )}
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                    <TableRow className="bg-muted/50 font-semibold">
                                      <TableCell></TableCell>
                                      <TableCell>Month Total</TableCell>
                                      <TableCell className="text-right">{fmtNum(moTotPcs)}</TableCell>
                                      <TableCell className="text-right">{fmtDec(mo.items.reduce((s, c) => s + (c.total_metres || 0), 0))}</TableCell>
                                      <TableCell className="text-right">{fmtLakhs(moTotVal)}</TableCell>
                                      <TableCell className="text-right"></TableCell>
                                      <TableCell className="text-right text-green-700">{fmtNum(mo.items.reduce((s, c) => s + (c.full_rate_lines || 0), 0))}</TableCell>
                                      <TableCell className="text-right text-amber-700">{fmtNum(mo.items.reduce((s, c) => s + (c.discounted_lines || 0), 0))}</TableCell>
                                      <TableCell className="text-right"></TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </>
          )}
        </TabsContent>

        {/* TAB 3: PRODUCTS */}
        <TabsContent value="products" className="space-y-4 mt-4">
          {/* View toggle + search + download controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant={productView === 'all' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => { setProductView('all'); setPieRegion(null); }}>
                All Products
              </Button>
              <Button variant={productView === 'fy' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => { setProductView('fy'); setPieRegion(null); }}>
                FY / Month-wise
              </Button>
              <Button variant={productView === 'region' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => { setProductView('region'); setPieRegion(null); }}>
                <MapPin size={12} className="mr-1" /> Region × Product
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="pl-8 h-9 w-48 text-sm"
                />
              </div>
              <Button variant="outline" size="sm" className="h-9 gap-1" onClick={() => exportProducts('excel')}>
                <FileSpreadsheet size={14} /> Excel
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1" onClick={() => exportProducts('pdf')}>
                <Download size={14} /> PDF
              </Button>
            </div>
          </div>

          {/* Product Breakdown by Value — with period selector */}
          <Card ref={pieChartRef}>
            <CardHeader className="pb-2">
              <div className="flex flex-col gap-2">
                <CardTitle className="text-lg font-semibold">
                  Product Breakdown by Value
                  {pieRegion && (
                    <span className="text-sm font-normal text-emerald-600 ml-2">— {pieRegion}</span>
                  )}
                  {piePeriod !== 'fy' && (
                    <span className="text-sm font-normal text-indigo-600 ml-2">
                      {pieRegion ? '' : '— '}{piePeriod.startsWith('q') ? piePeriod.toUpperCase() : (() => { const [y, m] = piePeriod.split('-'); const mn = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'}; return `${mn[parseInt(m)]} ${y}`; })()}
                    </span>
                  )}
                </CardTitle>
                {productView === 'region' && (
                  <div className="flex flex-wrap items-center gap-1 mb-1">
                    <span className="text-[10px] text-muted-foreground font-medium mr-1">Region:</span>
                    <button onClick={() => { setPieRegion(null); setExpandedRegionProduct(null); }}
                      className={`text-[11px] px-2.5 py-1 rounded-md transition-all ${!pieRegion ? 'bg-emerald-600 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                      All Regions
                    </button>
                    {pieRegion && (
                      <span className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-600 text-white font-semibold">{pieRegion}</span>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-1">Click a region card below to filter</span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-[10px] text-muted-foreground font-medium mr-1">Period:</span>
                  <button onClick={() => setPiePeriod('fy')}
                    className={`text-[11px] px-2.5 py-1 rounded-md transition-all ${piePeriod === 'fy' ? 'bg-indigo-600 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                    Full FY
                  </button>
                  {availableQuarters.map(q => (
                    <button key={q} onClick={() => setPiePeriod(q)}
                      className={`text-[11px] px-2.5 py-1 rounded-md transition-all ${piePeriod === q ? 'bg-indigo-600 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                      {q.toUpperCase()}
                    </button>
                  ))}
                  <span className="text-muted-foreground mx-1">|</span>
                  {availableMonths.map(m => {
                    const [y, mm] = m.split('-');
                    const mn = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'};
                    return (
                      <button key={m} onClick={() => setPiePeriod(m)}
                        className={`text-[11px] px-2 py-1 rounded-md transition-all ${piePeriod === m ? 'bg-indigo-600 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                        {mn[parseInt(mm)]}{' '}{y.slice(2)}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] text-muted-foreground font-medium mr-1" title="How many qualities to draw before the rest fold into Others. Auto keeps Others under a quarter of the total.">Show:</span>
                    {PRODUCT_TOP_CHOICES.map(c => (
                      <button key={c.key} onClick={() => chooseProductTop(c.key)}
                        className={`text-[11px] px-2 py-1 rounded-md transition-all ${productTopChoice === c.key ? 'bg-emerald-600 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                        {c.label}{c.key === 'auto' && productTopChoice === 'auto' ? ` (${pieTop})` : ''}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] text-muted-foreground font-medium mr-1">Chart:</span>
                    {PRODUCT_CHART_TYPES.map(c => (
                      <button key={c.key} onClick={() => chooseProductChart(c.key)}
                        className={`text-[11px] px-2 py-1 rounded-md transition-all ${productChartType === c.key ? 'bg-slate-700 text-white font-semibold' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const totalVal = activePie.reduce((s, p) => s + (p.total_value || 0), 0);
                return activePie.length > 0 ? (
                <div>
                  {renderProductChart(totalVal)}
                  {/* Product composition table below pie */}
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left py-1.5 font-medium">#</th>
                          <th className="text-left py-1.5 font-medium">Product</th>
                          <th className="text-right py-1.5 font-medium">Metres</th>
                          <th className="text-right py-1.5 font-medium">Pieces</th>
                          <th className="text-right py-1.5 font-medium">Value</th>
                          <th className="text-right py-1.5 font-medium">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activePie.map((p, i) => (
                          <tr key={p.trade_name} className="border-t border-gray-100">
                            <td className="py-1 text-muted-foreground">{i + 1}</td>
                            <td className="py-1 font-medium">
                              <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: sliceColor(i) }}></span>
                              {p.trade_name}
                            </td>
                            <td className="py-1 text-right font-medium">{fmtDec(p.total_metres || 0)}</td>
                            <td className="py-1 text-right text-muted-foreground">{fmtNum(p.total_pieces || 0)}</td>
                            <td className="py-1 text-right font-semibold">{fmtLakhs(p.total_value)}</td>
                            <td className="py-1 text-right text-indigo-600 font-medium">{totalVal > 0 ? ((p.total_value / totalVal) * 100).toFixed(1) : 0}%</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 font-bold">
                          <td className="py-1.5"></td>
                          <td className="py-1.5">Total</td>
                          <td className="py-1.5 text-right">{fmtDec(activePie.reduce((s, p) => s + (p.total_metres || 0), 0))}</td>
                          <td className="py-1.5 text-right text-muted-foreground">{fmtNum(activePie.reduce((s, p) => s + (p.total_pieces || 0), 0))}</td>
                          <td className="py-1.5 text-right">{fmtLakhs(totalVal)}</td>
                          <td className="py-1.5 text-right">100%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No product data for selected period</p>
              ); })()}
            </CardContent>
          </Card>

          {productView === 'all' && (
            <>
              {/* Full Product Table */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">All Products — Pricing & Discounts</CardTitle>
                    <Badge variant="outline">{filteredProducts.length} products</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleProductSort('trade_name')}>
                            Trade Name <ProductSortIcon field="trade_name" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('total_metres')}>
                            Metres <ProductSortIcon field="total_metres" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('total_pieces')}>
                            Pieces <ProductSortIcon field="total_pieces" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('avg_so_price')}>
                            Avg SO Price <ProductSortIcon field="avg_so_price" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('avg_net_rate')}>
                            Avg Net Rate <ProductSortIcon field="avg_net_rate" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('effective_discount_pct')}>
                            Eff. Disc % <ProductSortIcon field="effective_discount_pct" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('total_value')}>
                            Net Value <ProductSortIcon field="total_value" />
                          </TableHead>
                          <TableHead className="text-right text-green-700 cursor-pointer select-none" onClick={() => handleProductSort('full_rate_lines')}>
                            # Full Rate <ProductSortIcon field="full_rate_lines" />
                          </TableHead>
                          <TableHead className="text-right text-amber-700 cursor-pointer select-none" onClick={() => handleProductSort('discounted_lines')}>
                            # Discounted <ProductSortIcon field="discounted_lines" />
                          </TableHead>
                          <TableHead className="text-right cursor-pointer select-none" onClick={() => handleProductSort('customer_count')}>
                            Customers <ProductSortIcon field="customer_count" />
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                            <TableCell className="font-medium">{p.trade_name}</TableCell>
                            <TableCell className="text-right">{fmtDec(p.total_metres)}</TableCell>
                            <TableCell className="text-right">{fmtNum(p.total_pieces)}</TableCell>
                            <TableCell className="text-right">{fmtDec(p.avg_so_price)}</TableCell>
                            <TableCell className="text-right">{fmtDec(p.avg_net_rate)}</TableCell>
                            <TableCell className="text-right">
                              <span className={`font-medium ${(p.effective_discount_pct || 0) > 10 ? 'text-red-600' : (p.effective_discount_pct || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {p.effective_discount_pct || 0}%
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{fmtLakhs(p.total_value)}</TableCell>
                            <TableCell className="text-right text-green-700">{p.full_rate_lines || 0}</TableCell>
                            <TableCell className="text-right text-amber-700">{p.discounted_lines || 0}</TableCell>
                            <TableCell className="text-right">{p.customer_count}</TableCell>
                          </TableRow>
                        ))}
                        {filteredProducts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                              {productSearch ? 'No products match your search' : 'No product data'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* FY / Month-wise Product View */}
          {productView === 'fy' && (
            <>
              {fyProductData.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No period-wise product data available
                  </CardContent>
                </Card>
              ) : fyProductData.map(fyGroup => (
                <Card key={fyGroup.fyLabel} className="border-2">
                  {/* FY Header — click to expand/collapse */}
                  <CardHeader
                    className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleFY(fyGroup.fyLabel)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedFYs[fyGroup.fyLabel] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        <CardTitle className="text-lg font-bold text-indigo-700">{fyGroup.fyLabel}</CardTitle>
                        <Badge variant="outline">{fyGroup.months.length} months</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span><strong>{fmtDec(fyGroup.fyTotals.reduce((s, t) => s + t.total_metres, 0))}</strong> m</span>
                        <span><strong>{fmtLakhs(fyGroup.fyTotals.reduce((s, t) => s + t.total_value, 0))}</strong></span>
                        <span className="text-green-700">{fmtNum(fyGroup.fyTotals.reduce((s, t) => s + t.full_rate_lines, 0))} full</span>
                        <span className="text-amber-700">{fmtNum(fyGroup.fyTotals.reduce((s, t) => s + t.discounted_lines, 0))} disc</span>
                      </div>
                    </div>
                  </CardHeader>

                  {expandedFYs[fyGroup.fyLabel] && (
                    <CardContent className="pt-0 space-y-4">
                      {/* FY Total Summary Table */}
                      <div className="bg-indigo-50 rounded-lg p-3">
                        <p className="text-sm font-semibold text-indigo-800 mb-2">{fyGroup.fyLabel} — Product Totals</p>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-indigo-100/50">
                                <TableHead>#</TableHead>
                                <TableHead>Trade Name</TableHead>
                                <TableHead className="text-right">Metres</TableHead>
                                <TableHead className="text-right">Pieces</TableHead>
                                <TableHead className="text-right">Avg SO Price</TableHead>
                                <TableHead className="text-right">Avg Net Rate</TableHead>
                                <TableHead className="text-right">Eff. Disc %</TableHead>
                                <TableHead className="text-right">Net Value</TableHead>
                                <TableHead className="text-right text-green-700"># Full Rate</TableHead>
                                <TableHead className="text-right text-amber-700"># Discounted</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {fyGroup.fyTotals.map((t, i) => (
                                <TableRow key={i}>
                                  <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                                  <TableCell className="font-medium">{t.trade_name}</TableCell>
                                  <TableCell className="text-right">{fmtDec(t.total_metres)}</TableCell>
                                  <TableCell className="text-right">{fmtNum(t.total_pieces)}</TableCell>
                                  <TableCell className="text-right">{fmtDec(t.avg_so_price)}</TableCell>
                                  <TableCell className="text-right">{fmtDec(t.avg_net_rate)}</TableCell>
                                  <TableCell className="text-right">
                                    <span className={`font-medium ${(t.effective_discount_pct || 0) > 10 ? 'text-red-600' : (t.effective_discount_pct || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                      {t.effective_discount_pct || 0}%
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{fmtLakhs(t.total_value)}</TableCell>
                                  <TableCell className="text-right text-green-700">{t.full_rate_lines || 0}</TableCell>
                                  <TableCell className="text-right text-amber-700">{t.discounted_lines || 0}</TableCell>
                                </TableRow>
                              ))}
                              {/* FY grand total row */}
                              <TableRow className="bg-indigo-100 font-bold">
                                <TableCell></TableCell>
                                <TableCell>TOTAL</TableCell>
                                <TableCell className="text-right">{fmtDec(fyGroup.fyTotals.reduce((s, t) => s + t.total_metres, 0))}</TableCell>
                                <TableCell className="text-right">{fmtNum(fyGroup.fyTotals.reduce((s, t) => s + t.total_pieces, 0))}</TableCell>
                                <TableCell className="text-right"></TableCell>
                                <TableCell className="text-right"></TableCell>
                                <TableCell className="text-right"></TableCell>
                                <TableCell className="text-right">{fmtLakhs(fyGroup.fyTotals.reduce((s, t) => s + t.total_value, 0))}</TableCell>
                                <TableCell className="text-right text-green-700">{fmtNum(fyGroup.fyTotals.reduce((s, t) => s + t.full_rate_lines, 0))}</TableCell>
                                <TableCell className="text-right text-amber-700">{fmtNum(fyGroup.fyTotals.reduce((s, t) => s + t.discounted_lines, 0))}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Month-wise breakdown */}
                      {fyGroup.months.map(mo => (
                        <div key={mo.monthKey} className="border rounded-lg">
                          <div className="flex items-center justify-between px-4 py-2 bg-muted/30">
                            <p className="text-sm font-semibold">{mo.monthLabel}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{fmtDec(mo.totalMetres)} m</span>
                              <span>{fmtLakhs(mo.totalValue)}</span>
                              <span className="text-green-700">{fmtNum(mo.fullRate)} full</span>
                              <span className="text-amber-700">{fmtNum(mo.discounted)} disc</span>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>#</TableHead>
                                  <TableHead>Trade Name</TableHead>
                                  <TableHead className="text-right">Metres</TableHead>
                                  <TableHead className="text-right">Pieces</TableHead>
                                  <TableHead className="text-right">Avg SO Price</TableHead>
                                  <TableHead className="text-right">Avg Net Rate</TableHead>
                                  <TableHead className="text-right">Eff. Disc %</TableHead>
                                  <TableHead className="text-right">Net Value</TableHead>
                                  <TableHead className="text-right text-green-700"># Full Rate</TableHead>
                                  <TableHead className="text-right text-amber-700"># Discounted</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {mo.products.map((p, i) => (
                                  <TableRow key={i}>
                                    <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                                    <TableCell className="font-medium text-sm">{p.trade_name}</TableCell>
                                    <TableCell className="text-right">{fmtDec(p.total_metres)}</TableCell>
                                    <TableCell className="text-right">{fmtNum(p.total_pieces)}</TableCell>
                                    <TableCell className="text-right">{fmtDec(p.avg_so_price)}</TableCell>
                                    <TableCell className="text-right">{fmtDec(p.avg_net_rate)}</TableCell>
                                    <TableCell className="text-right">
                                      <span className={`font-medium ${(p.effective_discount_pct || 0) > 10 ? 'text-red-600' : (p.effective_discount_pct || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                        {p.effective_discount_pct || 0}%
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right font-semibold">{fmtLakhs(p.total_value)}</TableCell>
                                    <TableCell className="text-right text-green-700">{p.full_rate_lines || 0}</TableCell>
                                    <TableCell className="text-right text-amber-700">{p.discounted_lines || 0}</TableCell>
                                  </TableRow>
                                ))}
                                {/* Month total row */}
                                <TableRow className="bg-muted/50 font-semibold">
                                  <TableCell></TableCell>
                                  <TableCell>Month Total</TableCell>
                                  <TableCell className="text-right">{fmtDec(mo.totalMetres)}</TableCell>
                                  <TableCell className="text-right">{fmtNum(mo.totalPieces)}</TableCell>
                                  <TableCell className="text-right"></TableCell>
                                  <TableCell className="text-right"></TableCell>
                                  <TableCell className="text-right"></TableCell>
                                  <TableCell className="text-right">{fmtLakhs(mo.totalValue)}</TableCell>
                                  <TableCell className="text-right text-green-700">{fmtNum(mo.fullRate)}</TableCell>
                                  <TableCell className="text-right text-amber-700">{fmtNum(mo.discounted)}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              ))}
            </>
          )}

          {productView === 'region' && (
            <>
              {(() => {
                const rpData = data?.region_product_breakdown || [];
                // Apply SAME period filter as the pie chart so numbers always align
                let filtered = rpData;
                if (piePeriod !== 'fy') {
                  if (piePeriod.startsWith('q')) {
                    const qMonths = QUARTER_MONTHS[piePeriod] || [];
                    filtered = filtered.filter(rp => {
                      const mm = parseInt((rp.month || '').split('-')[1], 10);
                      return qMonths.includes(mm);
                    });
                  } else {
                    filtered = filtered.filter(rp => rp.month === piePeriod);
                  }
                }
                if (productSearch) {
                  const q = productSearch.toLowerCase();
                  filtered = filtered.filter(rp => (rp.trade_name || '').toLowerCase().includes(q));
                }
                // Group by state, then aggregate by trade_name within each state (raw data is per-month)
                const regionMap = {};
                filtered.forEach(rp => {
                  if (!regionMap[rp.state]) regionMap[rp.state] = { productsByTrade: {}, totalValue: 0, totalPieces: 0, totalMetres: 0 };
                  const bucket = regionMap[rp.state];
                  bucket.totalValue += rp.total_value || 0;
                  bucket.totalPieces += rp.total_pieces || 0;
                  bucket.totalMetres += rp.total_metres || 0;
                  const tn = rp.trade_name || 'Unknown';
                  if (!bucket.productsByTrade[tn]) {
                    bucket.productsByTrade[tn] = { trade_name: tn, total_value: 0, total_pieces: 0, total_metres: 0, customer_count: 0 };
                  }
                  const p = bucket.productsByTrade[tn];
                  p.total_value += rp.total_value || 0;
                  p.total_pieces += rp.total_pieces || 0;
                  p.total_metres += rp.total_metres || 0;
                  // customer_count is a distinct count per month — take max as a reasonable approximation
                  p.customer_count = Math.max(p.customer_count, rp.customer_count || 0);
                });
                Object.values(regionMap).forEach(r => {
                  r.products = Object.values(r.productsByTrade);
                });
                const sortedRegions = Object.entries(regionMap).sort((a, b) => b[1].totalValue - a[1].totalValue);
                const grandTotal = sortedRegions.reduce((s, [, r]) => s + r.totalValue, 0);
                const grandMetres = sortedRegions.reduce((s, [, r]) => s + r.totalMetres, 0);
                const unknownEntry = sortedRegions.find(([s]) => !s || s === 'Unknown' || s === 'null');
                const unknownValue = unknownEntry ? unknownEntry[1].totalValue : 0;
                // Human-readable period label
                const periodLabel = piePeriod === 'fy'
                  ? 'Full FY'
                  : piePeriod.startsWith('q')
                    ? piePeriod.toUpperCase()
                    : (() => { const [y, m] = piePeriod.split('-'); const mn = {1:'Jan',2:'Feb',3:'Mar',4:'Apr',5:'May',6:'Jun',7:'Jul',8:'Aug',9:'Sep',10:'Oct',11:'Nov',12:'Dec'}; return `${mn[parseInt(m)]} ${y}`; })();

                return sortedRegions.length === 0 ? (
                  <Card><CardContent className="py-8"><p className="text-center text-muted-foreground">No regional product data available for {periodLabel}</p></CardContent></Card>
                ) : (
                  <div className="space-y-2">
                    {/* Reconciliation strip — always visible so totals are auditable */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-indigo-50 to-emerald-50 border border-indigo-200 rounded-lg px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-4 text-xs">
                        <span className="font-semibold text-indigo-700">All Regions Total</span>
                        <span className="text-muted-foreground">Period: <span className="font-semibold text-emerald-700">{periodLabel}</span></span>
                        <span>{sortedRegions.length} states</span>
                        <span>{fmtDec(grandMetres)} m</span>
                        <span className="font-bold text-indigo-700">{fmtLakhs(grandTotal)}</span>
                      </div>
                      {unknownValue > 0 && (
                        <div className="flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                          <AlertTriangle size={12} />
                          <span><strong>{fmtLakhs(unknownValue)}</strong> in dispatches have no state — shown under "Unknown"</span>
                        </div>
                      )}
                    </div>
                    {sortedRegions.map(([state, rd]) => {
                      const isExp = expandedRegionProduct === state;
                      return (
                        <Card key={state} className={`transition-all ${isExp ? 'shadow-lg ring-2 ring-indigo-400 bg-indigo-50/40' : 'hover:shadow-sm'}`}>
                          <div
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer ${isExp ? 'bg-indigo-100/60 border-b border-indigo-200' : ''}`}
                            onClick={() => {
                              const newState = isExp ? null : state;
                              setExpandedRegionProduct(newState);
                              setPieRegion(newState);
                              if (newState) {
                                setTimeout(() => pieChartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {isExp ? <ChevronDown size={14} className="text-indigo-600" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                              <MapPin size={14} className={isExp ? 'text-indigo-600' : 'text-indigo-500'} />
                              <span className={`text-sm uppercase ${isExp ? 'font-extrabold text-indigo-700' : 'font-bold'}`}>{state}</span>
                              {isExp && (
                                <span className="text-[10px] font-semibold text-white bg-indigo-600 px-2 py-0.5 rounded-full">SELECTED</span>
                              )}
                              <span className="text-[10px] text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">{rd.products.length} products</span>
                              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">{periodLabel}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-muted-foreground">{fmtDec(rd.totalMetres)} m</span>
                              <span className={`font-bold ${isExp ? 'text-indigo-700' : ''}`}>{fmtLakhs(rd.totalValue)}</span>
                              <span className="text-indigo-600 font-medium">{grandTotal > 0 ? `${Math.round(rd.totalValue / grandTotal * 100)}%` : '-'}</span>
                            </div>
                          </div>
                          {isExp && (
                            <div className="px-4 pb-3 pt-2">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-[11px] text-indigo-700 font-semibold">
                                  {state} — Product Composition · <span className="text-emerald-700">{periodLabel}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground">Matches pie chart above</div>
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground border-b">
                                    <th className="text-left py-1.5 font-medium">#</th>
                                    <th className="text-left py-1.5 font-medium">Product / Trade Name</th>
                                    <th className="text-right py-1.5 font-medium">Pieces</th>
                                    <th className="text-right py-1.5 font-medium">Metres</th>
                                    <th className="text-right py-1.5 font-medium">Net Value</th>
                                    <th className="text-right py-1.5 font-medium">Customers</th>
                                    <th className="text-right py-1.5 font-medium">% of Region</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rd.products.sort((a, b) => b.total_value - a.total_value).map((p, pi) => (
                                    <tr key={p.trade_name} className="border-t border-gray-100 hover:bg-white/70">
                                      <td className="py-1.5 text-muted-foreground">{pi + 1}</td>
                                      <td className="py-1.5 font-medium">{p.trade_name}</td>
                                      <td className="py-1.5 text-right">{fmtNum(p.total_pieces)}</td>
                                      <td className="py-1.5 text-right">{fmtDec(p.total_metres)}</td>
                                      <td className="py-1.5 text-right font-semibold">{fmtLakhs(p.total_value)}</td>
                                      <td className="py-1.5 text-right">{p.customer_count}</td>
                                      <td className="py-1.5 text-right text-indigo-600 font-semibold">{rd.totalValue > 0 ? `${Math.round(p.total_value / rd.totalValue * 100)}%` : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </TabsContent>

        {/* TAB 4: RETURNS (GR) */}
        <TabsContent value="returns" className="space-y-4 mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Goods Returns</h2>
              <Badge variant="outline">{grList.length} returns</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  placeholder="Search customer name..."
                  value={grSearch}
                  onChange={e => setGrSearch(e.target.value)}
                  className="pl-8 h-9 w-56 text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setGrReportTarget(null); setGrReportOpen(true); }}
                title="Download a goods-return statement for a day, month, or FY"
              >
                <Download size={14} className="mr-1" /> Download…
              </Button>
            </div>
          </div>
          {loadingGr ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : groupedReturns.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {grList.length === 0
                  ? 'No goods returns recorded. Use the Dispatch tab to create return entries.'
                  : 'No goods returns match your search.'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {groupedReturns.map((dateGroup) => (
                <div key={dateGroup.dateKey} className="border rounded-lg overflow-hidden">
                  {/* Date Header */}
                  <div
                    className="flex items-center justify-between px-4 py-2.5 bg-rose-50 cursor-pointer hover:bg-rose-100 transition-colors"
                    onClick={() => toggleReturnDateCollapse(dateGroup.dateKey)}
                  >
                    <div className="flex items-center gap-3">
                      {collapsedReturnDates[dateGroup.dateKey] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                      <span className="font-semibold text-sm">{dateGroup.formattedDate}</span>
                      <span className="text-xs text-muted-foreground">({dateGroup.dayName})</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span>{dateGroup.dayLines} returns</span>
                      <span className="font-semibold text-red-600">{fmtNum(dateGroup.dayUnits)} pcs</span>
                      <span className="font-semibold text-red-700">₹{fmtNum(dateGroup.dayAmount)}</span>
                    </div>
                  </div>

                  {/* Date Content (collapsible) */}
                  {!collapsedReturnDates[dateGroup.dateKey] && (
                    <div>
                      {dateGroup.customers.map((cust, cIdx) => (
                        <div key={`${cust.code}-${cIdx}`}>
                          {/* Customer Header */}
                          <div className="flex items-center justify-between px-4 py-1.5 bg-slate-50 border-t border-b">
                            <div className="flex items-center gap-2">
                              <Users size={13} className="text-indigo-500" />
                              <span className="text-xs font-semibold">{cust.name}</span>
                              <span className="text-[10px] text-muted-foreground">({cust.code})</span>
                                    {locationSummary(cust.lines, locMap) && <span className="text-[11px] text-sky-700 font-medium" title="Location (edit on the customer profile)">· {locationSummary(cust.lines, locMap)}</span>}
                            </div>
                            <div className="flex items-center gap-3 text-[11px]">
                              <span>{cust.lines.length} lines</span>
                              <span className="font-semibold text-red-600">{fmtNum(cust.totalUnits)} pcs</span>
                              <span className="font-semibold text-red-700">₹{fmtNum(cust.totalAmount)}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px] text-indigo-700 hover:bg-indigo-100"
                                onClick={() => {
                                  setGrReportTarget({ customer_code: cust.code, name: cust.name, dateKey: dateGroup.dateKey });
                                  setGrReportOpen(true);
                                }}
                                title={`Download goods-return statement for ${cust.name} — pick day, month, or FY`}
                              >
                                <FileText size={11} className="mr-1" /> Download…
                              </Button>
                            </div>
                          </div>

                          {/* Return Lines */}
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-white">
                                  <TableHead className="text-[10px] font-medium w-[110px]">Invoice / CN No</TableHead>
                                  <TableHead className="text-[10px] font-medium w-[90px]">Product</TableHead>
                                  <TableHead className="text-[10px] font-medium">Trade Name</TableHead>
                                  <TableHead className="text-[10px] font-medium text-right w-[50px]">Units</TableHead>
                                  <TableHead className="text-[10px] font-medium text-right w-[110px]">Amount (incl. GST)</TableHead>
                                  <TableHead className="text-[10px] font-medium w-[90px]">Type</TableHead>
                                  <TableHead className="text-[10px] font-medium w-[110px]">Status</TableHead>
                                  <TableHead className="text-[10px] font-medium w-[100px]">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {cust.lines.map((g, lIdx) => (
                                  <TableRow key={g.gr_id || lIdx} className="hover:bg-slate-50/50">
                                    <TableCell className="text-xs font-mono py-1.5">{g.plant_invoice || '—'}</TableCell>
                                    <TableCell className="text-xs py-1.5">{g.product_code || '—'}</TableCell>
                                    <TableCell className="text-xs py-1.5">{g.trade_name || '—'}</TableCell>
                                    <TableCell className="text-xs text-right font-semibold text-red-600 py-1.5">{fmtNum(g.return_units)}</TableCell>
                                    <TableCell className="text-xs text-right font-medium text-red-600 py-1.5">
                                      {g.credit_note_amount ? `₹${Number(g.credit_note_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}
                                    </TableCell>
                                    <TableCell className="text-[11px] py-1.5">{g.return_type?.replace(/_/g, ' ')}</TableCell>
                                    <TableCell className="py-1.5">
                                      <Badge className={`text-[10px] ${
                                        g.gr_status === 'at_office' ? 'bg-amber-100 text-amber-800' :
                                        g.gr_status === 'sent_to_mill' ? 'bg-blue-100 text-blue-800' :
                                        'bg-green-100 text-green-800'
                                      }`}>
                                        {g.gr_status?.replace(/_/g, ' ')}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="py-1.5">
                                      {g.gr_status !== 'credit_note_issued' && !g.read_only && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-[10px] h-6"
                                          onClick={() => {
                                            setGrStatusItem(g);
                                            setGrNewStatus(g.gr_status === 'at_office' ? 'sent_to_mill' : 'credit_note_issued');
                                            setGrStatusRemarks('');
                                            setGrCreditNoteNumber('');
                                            setGrCreditNoteAmount(0);
                                            setGrStatusDialogOpen(true);
                                          }}
                                        >
                                          Update Status
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {/* Customer Subtotal */}
                                <TableRow className="bg-slate-50/80 border-t">
                                  <TableCell colSpan={3} className="text-[10px] font-semibold text-right py-1">Subtotal ({cust.name}):</TableCell>
                                  <TableCell className="text-xs text-right font-bold text-red-600 py-1">{fmtNum(cust.totalUnits)}</TableCell>
                                  <TableCell className="text-xs text-right font-bold text-red-700 py-1">₹{fmtNum(cust.totalAmount)}</TableCell>
                                  <TableCell colSpan={3} className="py-1"></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ))}

                      {/* Day Total */}
                      <div className="flex items-center justify-between px-4 py-2 bg-rose-100/70 border-t-2 border-rose-300">
                        <span className="text-xs font-bold text-rose-800">Day Total — {dateGroup.formattedDate}</span>
                        <div className="flex items-center gap-4 text-xs font-bold text-rose-800">
                          <span>{dateGroup.dayLines} returns</span>
                          <span>{fmtNum(dateGroup.dayUnits)} pcs</span>
                          <span>₹{fmtNum(dateGroup.dayAmount)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TAB 5: RECONCILIATION */}
        <TabsContent value="reconciliation" className="space-y-6 mt-4">
          {reconLoading && !reconData ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : reconData ? (
            <>
              {/* Section 1: Health Score + Variance Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className={`border-2 ${(reconData.health_score?.health_pct || 100) >= 80 ? 'border-green-300 bg-green-50' : (reconData.health_score?.health_pct || 100) >= 60 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
                  <CardContent className="p-4 text-center">
                    <Activity size={24} className="mx-auto mb-1 text-indigo-600" />
                    <p className={`text-3xl font-bold ${(reconData.health_score?.health_pct || 100) >= 80 ? 'text-green-600' : (reconData.health_score?.health_pct || 100) >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                      {reconData.health_score?.health_pct ?? 100}%
                    </p>
                    <p className="text-xs text-muted-foreground">Health Score</p>
                    <p className="text-xs mt-1">{(reconData.health_score?.clean_orders || 0).toLocaleString('en-IN')} need no attention / {(reconData.health_score?.total_orders || 0).toLocaleString('en-IN')} order lines</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Counts over-dispatch, price variance, substitution and orders still open after {reconData.health_score?.aging_warning_days ?? 30} days.
                      Fresh open orders, cross-customer and certified are not counted.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-red-200 bg-red-50/50">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle size={24} className="mx-auto mb-1 text-red-500" />
                    <p className="text-3xl font-bold text-red-600">{(reconData.health_score?.variance_orders || 0).toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">Orders Needing Attention</p>
                    {reconData.health_score?.problem_breakdown && (
                      <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                        {(reconData.health_score.problem_breakdown.overdue_unfulfilled || 0).toLocaleString('en-IN')} open &gt; {reconData.health_score?.aging_warning_days ?? 30}d ·{' '}
                        {(reconData.health_score.problem_breakdown.over_dispatched || 0).toLocaleString('en-IN')} over-dispatched ·{' '}
                        {(reconData.health_score.problem_breakdown.price_variance || 0).toLocaleString('en-IN')} price ·{' '}
                        {(reconData.health_score.problem_breakdown.substitution || 0).toLocaleString('en-IN')} substitution
                        {' '}(a line can have more than one)<br />
                        {(reconData.health_score?.in_progress_orders || 0).toLocaleString('en-IN')} open orders still within {reconData.health_score?.aging_warning_days ?? 30} days (in progress)
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card className="border-2 border-indigo-200 bg-indigo-50/50">
                  <CardContent className="p-4 text-center">
                    <TrendingUp size={24} className="mx-auto mb-1 text-indigo-600" />
                    <p className="text-3xl font-bold text-indigo-600">{reconData.health_score?.fill_rate ?? 0}%</p>
                    <p className="text-xs text-muted-foreground">Fill Rate (Own Customer)</p>
                    <p className="text-xs mt-1">
                      {reconData.health_score?.fulfilled || 0} fulfilled / {reconData.health_score?.pending || 0} pending
                    </p>
                    {reconData.health_score?.fill_rate_all_customers > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        All customers: {reconData.health_score?.fill_rate_all_customers}% ({reconData.health_score?.total_dispatched_all_customers || 0} / {reconData.health_score?.total_ordered_units || 0}u)
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Variance Type Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                {[
                  { key: 'order_missing', label: 'No Order Match', color: 'bg-red-100 border-red-300 text-red-700', icon: AlertTriangle },
                  { key: 'excess_dispatch', label: 'Excess Dispatch', color: 'bg-orange-100 border-orange-300 text-orange-700', icon: TrendingUp },
                  { key: 'short_dispatch', label: 'Short Dispatch', color: 'bg-amber-100 border-amber-300 text-amber-700', icon: Clock },
                  { key: 'cross_customer', label: 'Cross Customer (info — not in health)', color: 'bg-slate-100 border-slate-300 text-slate-600', icon: ArrowUpDown },
                  { key: 'store_mismatch', label: 'Store Swap (info — shipped to another store of the customer)', color: 'bg-sky-50 border-sky-300 text-sky-700', icon: ArrowUpDown },
                  { key: 'substitution', label: 'Substitution', color: 'bg-purple-100 border-purple-300 text-purple-700', icon: Package },
                  { key: 'price_variance', label: 'Price Variance', color: 'bg-amber-100 border-amber-400 text-amber-800', icon: IndianRupee },
                  { key: 'direct_dispatch', label: 'Direct/Manual', color: 'bg-blue-100 border-blue-300 text-blue-700', icon: AlertTriangle },
                ].map(({ key, label, color, icon: Icon }) => {
                  const vs = reconData.variance_summary?.[key] || {};
                  const isActive = reconVarianceFilter === key;
                  return (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${isActive ? 'ring-2 ring-indigo-500 ' + color : color} hover:shadow-md`}
                      onClick={() => {
                        setReconVarianceFilter(isActive ? 'all' : key);
                        fetchReconData();
                      }}
                    >
                      <Icon size={16} className="mb-1" />
                      <p className="text-lg font-bold">{vs.count || 0}</p>
                      <p className="text-[10px] leading-tight">{label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Section 2: Aging + Tolerance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Aging Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Order Aging (Unfulfilled)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={reconData.aging_breakdown || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="bucket" width={80} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar isAnimationActive={false} dataKey="count" fill="#818CF8" name="Orders" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Tolerance Settings Card */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-semibold">Tolerance Settings</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleRunRecon} disabled={reconRunning}>
                        <RotateCcw size={14} className={`mr-1 ${reconRunning ? 'animate-spin' : ''}`} />
                        {reconRunning ? 'Running...' : 'Re-Run'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setToleranceDialogOpen(true)}>
                        <Settings size={14} className="mr-1" /> Configure
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-2 bg-slate-50 rounded">
                        <span className="text-muted-foreground">Qty Tolerance:</span>
                        <span className="ml-1 font-semibold">±{reconData.tolerance_settings?.quantity_tolerance_pct ?? 5}%</span>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <span className="text-muted-foreground">Price Tolerance:</span>
                        <span className="ml-1 font-semibold">±{reconData.tolerance_settings?.price_tolerance_pct ?? 2}%</span>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <span className="text-muted-foreground">Aging Warning:</span>
                        <span className="ml-1 font-semibold">{reconData.tolerance_settings?.aging_warning_days ?? 30}d</span>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <span className="text-muted-foreground">Aging Critical:</span>
                        <span className="ml-1 font-semibold">{reconData.tolerance_settings?.aging_critical_days ?? 60}d</span>
                      </div>
                      <div className="p-2 bg-slate-50 rounded col-span-2">
                        <span className="text-muted-foreground">Delivery Grace:</span>
                        <span className="ml-1 font-semibold">{reconData.tolerance_settings?.delivery_grace_days ?? 7} days</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Section 3: Variance Detail Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold">Variance Details</CardTitle>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Select value={reconVarianceFilter} onValueChange={v => { setReconVarianceFilter(v); }}>
                      <SelectTrigger className="h-8 text-xs w-[150px]"><SelectValue placeholder="All Types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {['order_missing', 'excess_dispatch', 'short_dispatch', 'cross_customer', 'store_mismatch', 'substitution', 'price_variance', 'direct_dispatch'].map(t => (
                          <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={reconSeverityFilter} onValueChange={v => { setReconSeverityFilter(v); }}>
                      <SelectTrigger className="h-8 text-xs w-[120px]"><SelectValue placeholder="All Severity" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Severity</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="info">Info</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1 min-w-[160px]">
                      <Search size={14} className="absolute left-2 top-2 text-muted-foreground" />
                      <Input
                        className="h-8 text-xs pl-7"
                        placeholder="Search customer / sort no / invoice..."
                        value={reconSearchTerm}
                        onChange={e => setReconSearchTerm(e.target.value)}
                      />
                    </div>
                    <Button variant="outline" size="sm" className="h-8" onClick={fetchReconData}>
                      <RotateCcw size={14} className="mr-1" /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs w-[100px]">Type</TableHead>
                        <TableHead className="text-xs">Order (Sort No)</TableHead>
                        <TableHead className="text-xs">Dispatch (Sort No)</TableHead>
                        <TableHead className="text-xs">Dispatch Customer</TableHead>
                        <TableHead className="text-xs text-right">Qty Var.</TableHead>
                        <TableHead className="text-xs text-right">Price Var.</TableHead>
                        <TableHead className="text-xs w-[80px]">Severity</TableHead>
                        <TableHead className="text-xs w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reconData.variances || [])
                        .filter(v => {
                          if (!reconSearchTerm) return true;
                          const s = reconSearchTerm.toLowerCase();
                          return (v.order?.order_no || '').toLowerCase().includes(s)
                            || (v.order?.shop_name || '').toLowerCase().includes(s)
                            || (v.order?.product_code || '').toLowerCase().includes(s)
                            || (v.dispatch?.customer_name || '').toLowerCase().includes(s)
                            || (v.dispatch?.customer_reference || '').toLowerCase().includes(s)
                            || (v.dispatch?.plant_invoice || '').toLowerCase().includes(s);
                        })
                        .slice(0, 100)
                        .map((v, idx) => (
                          <TableRow key={idx} className="cursor-pointer hover:bg-slate-50" onClick={() => { setSelectedVariance(v); setVarianceDialogOpen(true); }}>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${
                                v.type === 'order_missing' ? 'border-red-400 text-red-700 bg-red-50' :
                                v.type === 'excess_dispatch' ? 'border-orange-400 text-orange-700 bg-orange-50' :
                                v.type === 'short_dispatch' ? 'border-amber-400 text-amber-700 bg-amber-50' :
                                v.type === 'cross_customer' ? 'border-cyan-400 text-cyan-700 bg-cyan-50' :
                                v.type === 'substitution' ? 'border-purple-400 text-purple-700 bg-purple-50' :
                                v.type === 'price_variance' ? 'border-amber-500 text-amber-800 bg-amber-50' :
                                'border-blue-400 text-blue-700 bg-blue-50'
                              }`}>
                                {v.type.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {v.order ? (
                                <div>
                                  <p className="font-medium">{v.order.product_code || '-'}</p>
                                  <p className="text-muted-foreground">{v.order.trade_name || '-'} ({v.order.units_ordered || 0}u)</p>
                                  <p className="text-[10px] text-muted-foreground">{v.order.shop_name || ''}</p>
                                </div>
                              ) : <span className="text-muted-foreground">-</span>}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div>
                                <p className="font-medium">{v.dispatch?.customer_reference || v.dispatch?.plant_invoice || '-'}</p>
                                <p className="text-muted-foreground">{v.dispatch?.trade_name || '-'} ({v.dispatch?.no_of_pieces || 0}pcs)</p>
                                {v.dispatch?.match_type && (
                                  <p className="text-[10px] text-indigo-500">{v.dispatch.match_type.replace(/_/g, ' ')}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">
                              <p>{v.dispatch?.customer_name || '-'}</p>
                              {v.dispatch?.order_customer && v.dispatch?.order_customer !== v.dispatch?.customer_name && (
                                <p className="text-[10px] text-cyan-600">Order: {v.dispatch.order_customer}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right">
                              {v.quantity_variance !== 0 ? (
                                <span className={v.quantity_variance > 0 ? 'text-orange-600' : 'text-amber-600'}>
                                  {v.quantity_variance > 0 ? '+' : ''}{v.quantity_variance} ({v.quantity_variance_pct > 0 ? '+' : ''}{v.quantity_variance_pct}%)
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-right">
                              {v.price_variance_pct !== 0 ? (
                                <span className={v.price_variance_pct < 0 ? 'text-red-600' : 'text-green-600'}>
                                  {v.price_variance_pct > 0 ? '+' : ''}{v.price_variance_pct}%
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${
                                v.severity === 'critical' ? 'border-red-500 text-red-700 bg-red-100' :
                                v.severity === 'warning' ? 'border-amber-500 text-amber-700 bg-amber-100' :
                                'border-blue-400 text-blue-600 bg-blue-50'
                              }`}>
                                {v.severity}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Eye size={14} className="text-muted-foreground" />
                            </TableCell>
                          </TableRow>
                        ))}
                      {(!reconData.variances || reconData.variances.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                            <CheckCircle size={24} className="mx-auto mb-2 text-green-500" />
                            No variances detected. All orders and dispatches are clean!
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Section 4: Customer & Product Variance Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Customer Variance Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Customer Variance Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                          <TableHead className="text-xs text-right">Short</TableHead>
                          <TableHead className="text-xs text-right">Excess</TableHead>
                          <TableHead className="text-xs text-right">Price</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(reconData.customer_variance_breakdown || []).slice(0, 15).map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs">
                              <p className="font-medium">{c.customer_name}</p>
                              <p className="text-muted-foreground text-[10px]">{c.customer_code}</p>
                            </TableCell>
                            <TableCell className="text-xs text-right font-semibold">{c.total_variances}</TableCell>
                            <TableCell className="text-xs text-right">{c.short_dispatch || 0}</TableCell>
                            <TableCell className="text-xs text-right">{c.excess_dispatch || 0}</TableCell>
                            <TableCell className="text-xs text-right">{c.price_variance || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Product Variance Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Product Variance Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Trade Name</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                          <TableHead className="text-xs text-right">Subst.</TableHead>
                          <TableHead className="text-xs text-right">Price</TableHead>
                          <TableHead className="text-xs text-right">Avg Price Var</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(reconData.product_variance_breakdown || []).slice(0, 15).map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs font-medium">{p.trade_name}</TableCell>
                            <TableCell className="text-xs text-right font-semibold">{p.total_variances}</TableCell>
                            <TableCell className="text-xs text-right">{p.substitution || 0}</TableCell>
                            <TableCell className="text-xs text-right">{p.price_variance || 0}</TableCell>
                            <TableCell className="text-xs text-right">
                              {p.avg_price_variance_pct ? (
                                <span className={p.avg_price_variance_pct < 0 ? 'text-red-600' : 'text-green-600'}>
                                  {p.avg_price_variance_pct > 0 ? '+' : ''}{p.avg_price_variance_pct}%
                                </span>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground space-y-3">
              <p>{reconError
                ? `Reconciliation data could not be loaded: ${reconError}`
                : 'No reconciliation data available. Import orders and dispatches first.'}</p>
              {/* Re-Run must stay reachable when the panel fails to load —
                  it is often the thing that repairs the data. */}
              {(user?.role === 'admin' || user?.role === 'accounts') && (
                <Button variant="outline" size="sm" onClick={handleRunRecon} disabled={reconRunning}>
                  <RotateCcw size={14} className={`mr-1 ${reconRunning ? 'animate-spin' : ''}`} />
                  {reconRunning ? 'Running reconciliation...' : 'Re-Run Reconciliation'}
                </Button>
              )}
            </div>
          )}
        </TabsContent>

        {/* TAB 6: ORDERS DETAIL */}
        <TabsContent value="orders" className="space-y-4 mt-4">
          {/* View toggle */}
          <div className="flex items-center gap-2">
            <Button variant={orderView === 'all' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => setOrderView('all')}>
              All Orders
            </Button>
            <Button variant={orderView === 'fy' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => setOrderView('fy')}>
              FY / Month-wise
            </Button>
          </div>

          {/* FY / Month-wise order summary */}
          {orderView === 'fy' && (
            <>
              {fyOrderData.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-muted-foreground">No period-wise order data</CardContent></Card>
              ) : fyOrderData.map(fyG => {
                const fyTotOrd = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, o) => ss + (o.total_orders || 0), 0), 0);
                const fyTotUnitsOrd = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, o) => ss + (o.units_ordered || 0), 0), 0);
                const fyTotUnitsDisp = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, o) => ss + (o.units_dispatched || 0), 0), 0);
                return (
                  <Card key={fyG.fyLabel} className="border-2">
                    <CardHeader className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => toggleOrdFY(fyG.fyLabel)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedOrdFYs[fyG.fyLabel] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          <CardTitle className="text-lg font-bold text-indigo-700">{fyG.fyLabel}</CardTitle>
                          <Badge variant="outline">{fyG.months.length} months</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span><strong>{fmtNum(fyTotOrd)}</strong> orders</span>
                          <span><strong>{fmtNum(fyTotUnitsOrd)}</strong> ordered</span>
                          <span><strong>{fmtNum(fyTotUnitsDisp)}</strong> dispatched</span>
                          <span className={`font-medium ${fyTotUnitsOrd > 0 && (fyTotUnitsDisp / fyTotUnitsOrd * 100) >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                            {fyTotUnitsOrd > 0 ? Math.round(fyTotUnitsDisp / fyTotUnitsOrd * 100) : 0}% fill
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    {expandedOrdFYs[fyG.fyLabel] && (
                      <CardContent className="pt-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-6"></TableHead>
                                <TableHead>Month</TableHead>
                                <TableHead className="text-right">Orders</TableHead>
                                <TableHead className="text-right">Units Ordered</TableHead>
                                <TableHead className="text-right">Units Dispatched</TableHead>
                                <TableHead className="text-right">Fill Rate</TableHead>
                                <TableHead className="text-right">Customers</TableHead>
                                <TableHead className="text-right">Products</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {fyG.months.map(mo => {
                                const o = mo.items[0] || {};
                                const fr = o.units_ordered > 0 ? Math.round(o.units_dispatched / o.units_ordered * 100) : 0;
                                const moDetail = expandedOrdMonths[mo.monthKey];
                                return (
                                  <React.Fragment key={mo.monthKey}>
                                    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleOrdMonth(mo.monthKey)}>
                                      <TableCell className="px-2">
                                        {moDetail?.data ? <ChevronDown size={14} className="text-indigo-600" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                                      </TableCell>
                                      <TableCell className="font-medium text-indigo-700">{mo.monthLabel}</TableCell>
                                      <TableCell className="text-right">{fmtNum(o.total_orders)}</TableCell>
                                      <TableCell className="text-right">{fmtNum(o.units_ordered)}</TableCell>
                                      <TableCell className="text-right">{fmtNum(o.units_dispatched)}</TableCell>
                                      <TableCell className="text-right">
                                        <span className={`font-medium ${fr >= 80 ? 'text-green-600' : 'text-amber-600'}`}>{fr}%</span>
                                      </TableCell>
                                      <TableCell className="text-right">{o.customers}</TableCell>
                                      <TableCell className="text-right">{o.products}</TableCell>
                                    </TableRow>
                                    {moDetail?.loading && (
                                      <TableRow><TableCell colSpan={8} className="text-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
                                      </TableCell></TableRow>
                                    )}
                                    {moDetail?.data && (
                                      <TableRow><TableCell colSpan={8} className="p-0 bg-gray-50/50">
                                        <div className="px-4 py-3 space-y-2">
                                          {moDetail.data.map(dg => (
                                            <div key={dg.dateKey} className="border rounded-lg overflow-hidden bg-white">
                                              <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50/70">
                                                <span className="text-xs font-semibold">{dg.formattedDate} <span className="text-muted-foreground font-normal">({dg.dayName})</span></span>
                                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                                                  <span>{dg.dayLines} orders</span>
                                                  <span>{fmtNum(dg.dayOrdered)} ordered</span>
                                                  <span>{fmtNum(dg.dayDispatched)} dispatched</span>
                                                </div>
                                              </div>
                                              {dg.customers.map(cust => (
                                                <div key={cust.code} className="border-t">
                                                  <div className="flex items-center justify-between px-3 py-1 bg-muted/20">
                                                    <span className="text-xs font-medium">{cust.name} <span className="text-muted-foreground">({cust.code})</span></span>
                                                    <span className="text-[11px] text-muted-foreground">{fmtNum(cust.totalOrdered)} ordered | {fmtNum(cust.totalDispatched)} dispatched</span>
                                                  </div>
                                                  <Table>
                                                    <TableHeader>
                                                      <TableRow className="text-[11px]">
                                                        <TableHead className="py-1 h-7">Order No</TableHead>
                                                        <TableHead className="py-1 h-7">Division</TableHead>
                                                        <TableHead className="py-1 h-7">Trade Name</TableHead>
                                                        <TableHead className="py-1 h-7">Sort No</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Ord</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Disp</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Status</TableHead>
                                                      </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                      {cust.lines.map((l, li) => (
                                                        <TableRow key={li} className="text-xs">
                                                          <TableCell className="py-1 font-mono">{l.order_no}</TableCell>
                                                          <TableCell className="py-1">{l.division}</TableCell>
                                                          <TableCell className="py-1"><TradeName row={l} /></TableCell>
                                                          <TableCell className="py-1">{l.product_code}</TableCell>
                                                          <TableCell className="py-1 text-right">{l.units_ordered}</TableCell>
                                                          <TableCell className="py-1 text-right">{l.units_dispatched}</TableCell>
                                                          <TableCell className="py-1 text-right">
                                                            <Badge variant="outline" className={`text-[10px] ${l.dispatch_status === 'fulfilled' ? 'bg-green-50 text-green-700' : l.dispatch_status === 'partial' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50'}`}>
                                                              {l.dispatch_status}
                                                            </Badge>
                                                          </TableCell>
                                                        </TableRow>
                                                      ))}
                                                    </TableBody>
                                                  </Table>
                                                </div>
                                              ))}
                                            </div>
                                          ))}
                                          {moDetail.data.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No order records for this month</p>}
                                        </div>
                                      </TableCell></TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                              <TableRow className="bg-indigo-50 font-bold">
                                <TableCell></TableCell>
                                <TableCell>FY Total</TableCell>
                                <TableCell className="text-right">{fmtNum(fyTotOrd)}</TableCell>
                                <TableCell className="text-right">{fmtNum(fyTotUnitsOrd)}</TableCell>
                                <TableCell className="text-right">{fmtNum(fyTotUnitsDisp)}</TableCell>
                                <TableCell className="text-right">
                                  <span className={`font-medium ${fyTotUnitsOrd > 0 && (fyTotUnitsDisp / fyTotUnitsOrd * 100) >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                                    {fyTotUnitsOrd > 0 ? Math.round(fyTotUnitsDisp / fyTotUnitsOrd * 100) : 0}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-right"></TableCell>
                                <TableCell className="text-right"></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </>
          )}

          {orderView === 'all' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg font-semibold">Order Details</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditOrderMode(false); setEditOrderId(null); setManualOrder({ order_no: '', division: '', customer_code: '', shop_name: '', trade_name: '', product_code: '', units_ordered: 1, order_date: new Date().toISOString().slice(0, 10), state: '', city: '', category: '', ex_mill_price: 0, discount_pct: 0, remark: '' }); setManualOrderOpen(true); }}>
                    <Plus size={14} className="mr-1" /> Add Order
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'accounts') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReconcileOrders}
                      disabled={reconciling}
                      title="Re-scan orders against existing dispatches. Useful when an order copy was uploaded AFTER its dispatches (units_dispatched stuck at 0 / status stuck on 'pending')."
                    >
                      <ArrowUpDown size={14} className="mr-1" />
                      {reconciling ? 'Reconciling…' : 'Reconcile'}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openOdExport('orders')} disabled={exporting}
                    title="Download orders — pick duration, region and customer first">
                    <Download size={14} className="mr-1" /> Download…
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(ordersTableTotal)}</p>
                  <p className="text-[11px] text-muted-foreground">Total Orders</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(data?.total_ordered)}</p>
                  <p className="text-[11px] text-muted-foreground">Units Ordered</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(data?.total_dispatched_against_orders)}</p>
                  <p className="text-[11px] text-muted-foreground">Units Dispatched</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className={`text-lg font-bold ${fillRate >= 80 ? 'text-green-600' : 'text-amber-600'}`}>{fillRate}%</p>
                  <p className="text-[11px] text-muted-foreground">Fill Rate</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-red-600">{fmtNum(data?.total_gr_units)}</p>
                  <p className="text-[11px] text-muted-foreground">Returned</p>
                </div>
              </div>

              {/* Orders Table - Grouped by Date -> Customer */}
              {ordersTableLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : ordersTableData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package size={32} className="mx-auto mb-2 opacity-40" />
                  <p>No orders found. Import order files or add manually.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {groupedOrders.map((dateGroup) => (
                      <div key={dateGroup.dateKey} className="border rounded-lg overflow-hidden">
                        {/* Date Header */}
                        <div
                          className="flex items-center justify-between px-4 py-2.5 bg-indigo-50 cursor-pointer hover:bg-indigo-100 transition-colors"
                          onClick={() => toggleOrderDateCollapse(dateGroup.dateKey)}
                        >
                          <div className="flex items-center gap-3">
                            {collapsedOrderDates[dateGroup.dateKey] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                            <span className="font-semibold text-sm">{dateGroup.formattedDate}</span>
                            <span className="text-xs text-muted-foreground">({dateGroup.dayName})</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span>{dateGroup.dayLines} orders</span>
                            <span className="font-semibold">{fmtNum(dateGroup.dayOrdered)} ordered</span>
                            <span>{fmtNum(dateGroup.dayDispatched)} dispatched</span>
                          </div>
                        </div>

                        {/* Date Content (collapsible) */}
                        {!collapsedOrderDates[dateGroup.dateKey] && (
                          <div>
                            {dateGroup.customers.map((cust, cIdx) => (
                              <div key={`${cust.code}-${cIdx}`}>
                                {/* Customer Header */}
                                <div className="flex items-center justify-between px-4 py-1.5 bg-slate-50 border-t border-b">
                                  <div className="flex items-center gap-2">
                                    {(() => {
                                      const tagSel = tagGroupSelectionState(cust.lines);
                                      return tagSel.total > 0 ? (
                                        <input
                                          type="checkbox"
                                          className="h-3 w-3 accent-indigo-600 cursor-pointer"
                                          checked={tagSel.allSelected}
                                          ref={el => { if (el) el.indeterminate = tagSel.anySelected && !tagSel.allSelected; }}
                                          onChange={() => toggleTagGroupSelection(cust.lines)}
                                          title={`Select all ${tagSel.total} order line(s) for ${cust.name} on this day to tag`}
                                        />
                                      ) : null;
                                    })()}
                                    <Users size={13} className="text-indigo-500" />
                                    <span className="text-xs font-semibold">{cust.name}</span>
                                    <span className="text-[10px] text-muted-foreground">({cust.code})</span>
                                    {locationSummary(cust.lines, locMap) && <span className="text-[11px] text-sky-700 font-medium" title="Location (edit on the customer profile)">· {locationSummary(cust.lines, locMap)}</span>}
                                  </div>
                                  <div className="flex items-center gap-3 text-[11px]">
                                    <span>{cust.lines.length} lines</span>
                                    <span className="font-semibold">{fmtNum(cust.totalOrdered)} ordered</span>
                                    <span>{fmtNum(cust.totalDispatched)} dispatched</span>
                                    {/* Single discount button per customer — opens a picker to
                                        choose WHICH of the customer's orders get the discount,
                                        instead of one pill per order crowding out the row. */}
                                    <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-0.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                                      onClick={() => openCustomerDiscount(cust, cust.lines[0]?.division || filterDivision)}
                                      title={`Apply a discount to selected orders of ${cust.name}`}>
                                      <IndianRupee size={11} className="mr-0.5" /> Discount…
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-[10px] text-indigo-700 hover:bg-indigo-100"
                                      onClick={() => {
                                        setReportTarget({ customer_code: cust.code, name: cust.name, dateKey: dateGroup.dateKey });
                                        setReportDialogOpen(true);
                                      }}
                                      title={`Customer Report for ${cust.name} — pick day, month, or FY`}
                                    >
                                      <FileText size={11} className="mr-1" /> Report…
                                    </Button>
                                  </div>
                                </div>

                                {/* Order Lines */}
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white">
                                        <TableHead className="text-[10px] font-medium w-[28px] text-center"></TableHead>
                                        <TableHead className="text-[10px] font-medium w-[110px]">Order No</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[90px]">Division</TableHead>
                                        <TableHead className="text-[10px] font-medium">Trade Name</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[90px]">Sort No</TableHead>
                                        {showForCol && <TableHead className="text-[10px] font-medium w-[110px]" title="Who the wholesaler placed this line for">For</TableHead>}
                                        <TableHead className="text-[10px] font-medium w-[100px]" title="Ship-to location (store) on the order copy">Store</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[55px]">Ord</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[55px]">Disp</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[75px]">Status</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[40px]">CTG</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[65px]">Rate</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[55px]">Disc%</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[65px]">Net Rate</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[50px]">Src</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[60px] text-center">Act</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {cust.lines.map((o, lIdx) => (
                                        <TableRow key={o.order_line_id || lIdx} className={`hover:bg-slate-50/50 ${selectedForTag.has(o.order_line_id) ? 'bg-indigo-50/60' : ''}`}>
                                          <TableCell className="py-1.5 text-center">
                                            {o.order_line_id && isWsDivision(o.division) ? (
                                              <input
                                                type="checkbox"
                                                className="h-3 w-3 accent-indigo-600 cursor-pointer"
                                                checked={selectedForTag.has(o.order_line_id)}
                                                onChange={() => toggleTagSelection(o.order_line_id)}
                                                title="Select to tag who this line is for"
                                              />
                                            ) : null}
                                          </TableCell>
                                          <TableCell className="text-xs font-mono py-1.5">{o.order_no}</TableCell>
                                          <TableCell className="text-[10px] py-1.5">{o.division}</TableCell>
                                          <TableCell className="text-xs py-1.5"><TradeName row={o} /></TableCell>
                                          <TableCell className="text-xs font-mono py-1.5">{o.product_code || '-'}</TableCell>
                                          {showForCol && <TableCell className="py-1.5">
                                            {o.order_line_id && isWsDivision(o.division) ? (
                                              <button type="button" onClick={() => openTagDialogFor(o)}
                                                className="group inline-flex items-center gap-1 rounded hover:bg-indigo-50 px-0.5 -mx-0.5"
                                                title="Change who this order is for (own shop, another retailer, or clear)">
                                                <ForBadge kind={o.end_customer_kind} name={o.end_customer_name} split={o.end_customer_split} />
                                                <Pencil size={9} className="text-slate-300 group-hover:text-indigo-500" />
                                              </button>
                                            ) : (
                                              isWsDivision(o.division)
                                                ? <ForBadge kind={o.end_customer_kind} name={o.end_customer_name} split={o.end_customer_split} />
                                                : <span className="text-[10px] text-slate-300">—</span>
                                            )}
                                          </TableCell>}
                                          <TableCell className="text-[10px] py-1.5 max-w-[120px] truncate" title={o.store_name ? `${o.store_name}${o.ship_to_code ? ` (${o.ship_to_code})` : ''}` : ''}>{o.store_name || '—'}</TableCell>
                                          <TableCell className="text-xs text-right font-semibold py-1.5">{fmtNum(o.units_ordered)}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{fmtNum(o.units_dispatched || 0)}</TableCell>
                                          <TableCell className="py-1.5">
                                            <Badge variant="outline" className={`text-[9px] ${
                                              o.dispatch_status === 'fulfilled' ? 'border-green-400 text-green-700 bg-green-50' :
                                              o.dispatch_status === 'partial' ? 'border-amber-400 text-amber-700 bg-amber-50' :
                                              o.dispatch_status === 'over_dispatched' ? 'border-blue-400 text-blue-700 bg-blue-50' :
                                              'border-gray-300 text-gray-600 bg-gray-50'
                                            }`}>
                                              {(o.dispatch_status || 'pending').replace(/_/g, ' ')}
                                            </Badge>
                                          </TableCell>
                                          <TableCell className="text-[10px] py-1.5">{o.category || '-'}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{o.ex_mill_price ? `₹${fmtNum(o.ex_mill_price)}` : '-'}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5" title={o.base_discount_pct ? `Base: ${o.base_discount_pct}% (profile) + Additional: ${o.additional_discount_pct || 0}% = Effective: ${o.discount_pct}%` : ''}>
                                            {o.base_discount_pct > 0 ? (
                                              <span>{o.additional_discount_pct || 0}%<span className="text-amber-500 text-[8px] ml-0.5" title={`Base ${o.base_discount_pct}% from profile`}>+{o.base_discount_pct}</span></span>
                                            ) : (o.discount_pct ? `${o.discount_pct}%` : '-')}
                                          </TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{o.net_price ? `₹${fmtNum(o.net_price)}` : '-'}</TableCell>
                                          <TableCell className="py-1.5">
                                            <Badge variant="outline" className={`text-[9px] ${o.source === 'manual' ? 'border-purple-300 text-purple-600' : 'border-gray-300 text-gray-600'}`}>
                                              {o.source || 'imp'}
                                            </Badge>
                                          </TableCell>
                                          <TableCell className="text-center py-1.5">
                                            <div className="flex items-center justify-center gap-0.5">
                                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditOrder(o)} title="Edit">
                                                <Pencil size={11} />
                                              </Button>
                                              {user?.role === 'admin' && (
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => {
                                                  // Count sibling lines that share the same order_no within
                                                  // the same division — surfaces the 'delete entire order'
                                                  // option in the confirmation dialog when relevant.
                                                  const siblings = ordersTableData.filter(x =>
                                                    x.order_no === o.order_no && x.division === o.division && !x.deleted
                                                  );
                                                  setDeleteTarget({ type: 'order', item: o, siblings: siblings.length });
                                                  setDeleteConfirmOpen(true);
                                                }} title="Delete">
                                                  <Trash2 size={11} />
                                                </Button>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {/* Customer Subtotal */}
                                      <TableRow className="bg-slate-50/80 border-t">
                                        <TableCell colSpan={showForCol ? 7 : 6} className="text-[10px] font-semibold text-right py-1">Subtotal ({cust.name}):</TableCell>
                                        <TableCell className="text-xs text-right font-bold py-1">{fmtNum(cust.totalOrdered)}</TableCell>
                                        <TableCell className="text-xs text-right font-bold py-1">{fmtNum(cust.totalDispatched)}</TableCell>
                                        <TableCell colSpan={7} className="py-1"></TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            ))}

                            {/* Day Total */}
                            <div className="flex items-center justify-between px-4 py-2 bg-indigo-100/70 border-t-2 border-indigo-300">
                              <span className="text-xs font-bold text-indigo-800">Day Total — {dateGroup.formattedDate}</span>
                              <div className="flex items-center gap-4 text-xs font-bold text-indigo-800">
                                <span>{dateGroup.dayLines} orders</span>
                                <span>{fmtNum(dateGroup.dayOrdered)} ordered</span>
                                <span>{fmtNum(dateGroup.dayDispatched)} dispatched</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">
                      Showing {ordersPage * ordersPageSize + 1}-{Math.min((ordersPage + 1) * ordersPageSize, ordersTableTotal)} of {fmtNum(ordersTableTotal)} orders
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={ordersPage === 0} onClick={() => setOrdersPage(p => p - 1)}>
                        <ChevronLeft size={14} className="mr-1" /> Prev
                      </Button>
                      <span className="text-xs text-muted-foreground">Page {ordersPage + 1} of {Math.ceil(ordersTableTotal / ordersPageSize) || 1}</span>
                      <Button variant="outline" size="sm" disabled={(ordersPage + 1) * ordersPageSize >= ordersTableTotal} onClick={() => setOrdersPage(p => p + 1)}>
                        Next <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* TAB 7: DISPATCHES DETAIL */}
        <TabsContent value="dispatches" className="space-y-4 mt-4">
          {/* View toggle */}
          <div className="flex items-center gap-2">
            <Button variant={dispatchView === 'all' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => setDispatchView('all')}>
              All Dispatches
            </Button>
            <Button variant={dispatchView === 'fy' ? 'default' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => setDispatchView('fy')}>
              FY / Month-wise
            </Button>
          </div>

          {/* FY / Month-wise dispatch summary */}
          {dispatchView === 'fy' && (
            <>
              {fyDispatchData.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-muted-foreground">No period-wise dispatch data</CardContent></Card>
              ) : fyDispatchData.map(fyG => {
                const fyTotPcs = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, d) => ss + (d.total_pieces || 0), 0), 0);
                const fyTotVal = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, d) => ss + (d.net_value || 0), 0), 0);
                const fyTotMtrs = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, d) => ss + (d.total_metres || 0), 0), 0);
                const fyTotCust = fyG.months.reduce((s, mo) => s + mo.items.reduce((ss, d) => ss + (d.customers || 0), 0), 0);
                return (
                  <Card key={fyG.fyLabel} className="border-2">
                    <CardHeader className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => toggleDispFY(fyG.fyLabel)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedDispFYs[fyG.fyLabel] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          <CardTitle className="text-lg font-bold text-indigo-700">{fyG.fyLabel}</CardTitle>
                          <Badge variant="outline">{fyG.months.length} months</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span><strong>{fmtNum(fyTotPcs)}</strong> pcs</span>
                          <span><strong>{fmtDec(fyTotMtrs)}</strong> mtrs</span>
                          <span><strong>{fmtLakhs(fyTotVal)}</strong></span>
                        </div>
                      </div>
                    </CardHeader>
                    {expandedDispFYs[fyG.fyLabel] && (
                      <CardContent className="pt-0 space-y-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-6"></TableHead>
                                <TableHead>Month</TableHead>
                                <TableHead className="text-right">Metres</TableHead>
                                <TableHead className="text-right">Pieces</TableHead>
                                <TableHead className="text-right">Net Value</TableHead>
                                <TableHead className="text-right">Customers</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {fyG.months.map(mo => {
                                const d = mo.items[0] || {};
                                const moDetail = expandedDispMonths[mo.monthKey];
                                return (
                                  <React.Fragment key={mo.monthKey}>
                                    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleDispMonth(mo.monthKey)}>
                                      <TableCell className="px-2">
                                        {moDetail?.data ? <ChevronDown size={14} className="text-indigo-600" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                                      </TableCell>
                                      <TableCell className="font-medium text-indigo-700">{mo.monthLabel}</TableCell>
                                      <TableCell className="text-right">{fmtDec(d.total_metres)}</TableCell>
                                      <TableCell className="text-right">{fmtNum(d.total_pieces)}</TableCell>
                                      <TableCell className="text-right font-semibold">{fmtLakhs(d.net_value)}</TableCell>
                                      <TableCell className="text-right">{d.customers}</TableCell>
                                    </TableRow>
                                    {moDetail?.loading && (
                                      <TableRow><TableCell colSpan={6} className="text-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
                                      </TableCell></TableRow>
                                    )}
                                    {moDetail?.data && (
                                      <TableRow><TableCell colSpan={6} className="p-0 bg-gray-50/50">
                                        <div className="px-4 py-3 space-y-2">
                                          {moDetail.data.map(dg => (
                                            <div key={dg.dateKey} className="border rounded-lg overflow-hidden bg-white">
                                              <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50/70">
                                                <span className="text-xs font-semibold">{dg.formattedDate} <span className="text-muted-foreground font-normal">({dg.dayName})</span></span>
                                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                                                  <span>{dg.dayLines} lines</span>
                                                  <span>{fmtDec(dg.dayMetres)} m</span>
                                                  <span>{fmtNum(dg.dayPieces)} pcs</span>
                                                  <span className="font-semibold text-foreground">{fmtLakhs(dg.dayNetValue)}</span>
                                                </div>
                                              </div>
                                              {dg.customers.map(cust => (
                                                <div key={cust.code} className="border-t">
                                                  <div className="flex items-center justify-between px-3 py-1 bg-muted/20">
                                                    <span className="text-xs font-medium">{cust.name} <span className="text-muted-foreground">({cust.code})</span></span>
                                                    <span className="text-[11px] text-muted-foreground">{fmtDec(cust.totalMetres)} m | {fmtNum(cust.totalPieces)} pcs | {fmtLakhs(cust.totalNetValue)}</span>
                                                  </div>
                                                  <Table>
                                                    <TableHeader>
                                                      <TableRow className="text-[11px]">
                                                        <TableHead className="py-1 h-7">Invoice</TableHead>
                                                        <TableHead className="py-1 h-7">Trade Name</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Pcs</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Metres</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Rate</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Disc%</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Net Rate</TableHead>
                                                        <TableHead className="py-1 h-7 text-right">Net Value</TableHead>
                                                      </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                      {cust.lines.map((l, li) => (
                                                        <TableRow key={li} className="text-xs">
                                                          <TableCell className="py-1">{l.plant_invoice}</TableCell>
                                                          <TableCell className="py-1"><TradeName row={l} /></TableCell>
                                                          <TableCell className="py-1 text-right">{l.no_of_pieces}</TableCell>
                                                          <TableCell className="py-1 text-right">{fmtDec(l.batch_qty_metres)}</TableCell>
                                                          <TableCell className="py-1 text-right">{fmtDec(l.so_price)}</TableCell>
                                                          <TableCell className="py-1 text-right">{(() => {
                                                            const eff = effectiveDiscPct(l.so_price, l.net_rate);
                                                            return (eff != null && eff > 0)
                                                              ? <span className="text-amber-600">{eff.toFixed(1)}%</span>
                                                              : '-';
                                                          })()}</TableCell>
                                                          <TableCell className="py-1 text-right">{fmtDec(l.net_rate)}</TableCell>
                                                          <TableCell className="py-1 text-right font-medium">{fmtLakhs(l.net_value)}</TableCell>
                                                        </TableRow>
                                                      ))}
                                                    </TableBody>
                                                  </Table>
                                                </div>
                                              ))}
                                            </div>
                                          ))}
                                          {moDetail.data.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No dispatch records for this month</p>}
                                        </div>
                                      </TableCell></TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                              <TableRow className="bg-indigo-50 font-bold">
                                <TableCell></TableCell>
                                <TableCell>FY Total</TableCell>
                                <TableCell className="text-right">{fmtNum(fyTotPcs)}</TableCell>
                                <TableCell className="text-right">{fmtDec(fyTotMtrs)}</TableCell>
                                <TableCell className="text-right">{fmtLakhs(fyTotVal)}</TableCell>
                                <TableCell className="text-right"></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </>
          )}

          {dispatchView === 'all' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg font-semibold">Dispatch Details</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={showMissingOrdersOnly ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowMissingOrdersOnly(v => !v)}
                    className={showMissingOrdersOnly ? 'bg-red-600 hover:bg-red-700' : ''}
                    title="Show only dispatches whose order copy hasn't been uploaded"
                  >
                    <AlertTriangle size={14} className="mr-1" />
                    {showMissingOrdersOnly ? `Missing Orders (${missingOrdersCount})` : `Missing Orders${missingOrdersCount ? ` (${missingOrdersCount})` : ''}`}
                  </Button>
                  {missingOrdersCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMissingOrdersExport}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      title="Download Excel of all missing-order dispatches on this page — share with marketing"
                    >
                      <Download size={14} className="mr-1" /> Missing List
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { resetDispatchForm(); setManualDispatchOpen(true); }}>
                    <Plus size={14} className="mr-1" /> Add Dispatch
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'accounts') && (
                    <label
                      className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none border rounded-md px-2 h-8"
                      title="Off (default): clean sales only — exports match the on-screen list. On: the PDF adds a separate Goods Returns / Credit Notes section with a net-of-returns summary (Excel lists returns as negative lines), so the customer sees actual dispatches and returns for the period. Admin/accounts only."
                    >
                      <input
                        type="checkbox"
                        className="h-3 w-3 accent-indigo-600 cursor-pointer"
                        checked={exportIncludeCN}
                        onChange={e => setExportIncludeCN(e.target.checked)}
                      />
                      Incl. credit notes
                    </label>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openOdExport('dispatches')} disabled={exporting}
                    title="Download dispatches — pick duration, region and customer first">
                    <Download size={14} className="mr-1" /> Download…
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtLakhs(ds.total_net_value)}</p>
                  <p className="text-[11px] text-muted-foreground">Net Sale Value</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(ds.total_invoices)}</p>
                  <p className="text-[11px] text-muted-foreground">Invoices</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtDec(ds.total_metres)}</p>
                  <p className="text-[11px] text-muted-foreground">Metres</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(ds.total_pieces)}</p>
                  <p className="text-[11px] text-muted-foreground">Pieces</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{fmtNum(ds.total_customers)}</p>
                  <p className="text-[11px] text-muted-foreground">Customers Billed</p>
                </div>
              </div>

              {/* Dispatches Table - Grouped by Date -> Customer */}
              {dispatchesTableLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                </div>
              ) : dispatchesTableData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Truck size={32} className="mx-auto mb-2 opacity-40" />
                  <p>No dispatches found. Import dispatch files or add manually.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {groupedDispatches.map((dateGroup) => (
                      <div key={dateGroup.dateKey} className="border rounded-lg overflow-hidden">
                        {/* Date Header */}
                        <div
                          className="flex items-center justify-between px-4 py-2.5 bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors"
                          onClick={() => toggleDateCollapse(dateGroup.dateKey)}
                        >
                          <div className="flex items-center gap-3">
                            {collapsedDates[dateGroup.dateKey] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                            <span className="font-semibold text-sm">{dateGroup.formattedDate}</span>
                            <span className="text-xs text-muted-foreground">({dateGroup.dayName})</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span>{dateGroup.dayLines} dispatches</span>
                            <span className="font-semibold">{fmtDec(dateGroup.dayMetres)} mtrs</span>
                            <span>{fmtNum(dateGroup.dayPieces)} pcs</span>
                            <span className="font-semibold text-emerald-700">{fmtLakhs(dateGroup.dayNetValue)}</span>
                          </div>
                        </div>

                        {/* Date Content (collapsible) */}
                        {!collapsedDates[dateGroup.dateKey] && (
                          <div>
                            {dateGroup.customers.map((cust, cIdx) => (
                              <div key={`${cust.code}-${cIdx}`}>
                                {/* Customer Header */}
                                {(() => {
                                  const grpSel = (user?.role === 'admin' || user?.role === 'accounts') ? groupSelectionState(cust.lines) : null;
                                  return (
                                    <div className="flex items-center justify-between px-4 py-1.5 bg-slate-50 border-t border-b">
                                      <div className="flex items-center gap-2">
                                        {grpSel && grpSel.total > 0 && (
                                          <input
                                            type="checkbox"
                                            className="h-3 w-3 accent-emerald-600 cursor-pointer"
                                            checked={grpSel.allSelected}
                                            ref={el => { if (el) el.indeterminate = grpSel.anySelected && !grpSel.allSelected; }}
                                            onChange={() => toggleGroupSelection(cust.lines)}
                                            title={`Select all ${grpSel.total} missing-order dispatch(es) for ${cust.name} on this day`}
                                          />
                                        )}
                                        <Users size={13} className="text-indigo-500" />
                                        <span className="text-xs font-semibold">{cust.name}</span>
                                        <span className="text-[10px] text-muted-foreground">({cust.code})</span>
                                    {locationSummary(cust.lines, locMap) && <span className="text-[11px] text-sky-700 font-medium" title="Location (edit on the customer profile)">· {locationSummary(cust.lines, locMap)}</span>}
                                        {grpSel && grpSel.total > 0 && (
                                          <span className="text-[10px] text-red-600 font-medium">· {grpSel.total} missing</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 text-[11px]">
                                        <span>{cust.lines.length} lines</span>
                                        <span className="font-semibold">{fmtDec(cust.totalMetres)} mtrs</span>
                                        <span>{fmtNum(cust.totalPieces)} pcs</span>
                                        <span className="font-semibold">{cust.totalNetValue ? `₹${fmtNum(cust.totalNetValue)}` : '-'}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 px-2 text-[10px] text-indigo-700 hover:bg-indigo-100"
                                          onClick={() => {
                                            setReportTarget({ customer_code: cust.code, name: cust.name, dateKey: dateGroup.dateKey });
                                            setReportDialogOpen(true);
                                          }}
                                          title={`Customer Report for ${cust.name} — pick day, month, or FY`}
                                        >
                                          <FileText size={11} className="mr-1" /> Report…
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Dispatch Lines */}
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white">
                                        {(user?.role === 'admin' || user?.role === 'accounts') && (
                                          <TableHead className="text-[10px] font-medium w-[28px] text-center"></TableHead>
                                        )}
                                        <TableHead className="text-[10px] font-medium w-[90px]">Invoice</TableHead>
                                        <TableHead className="text-[10px] font-medium">Trade Name</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[90px]">Sort No</TableHead>
                                        {showForCol && <TableHead className="text-[10px] font-medium w-[110px]" title="Who the matched order line was placed for">For</TableHead>}
                                        <TableHead className="text-[10px] font-medium w-[100px]" title="Ship-to location (store) on the dispatch report">Store</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[50px]">Pcs</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[60px]">Metres</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[60px]">Rate</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[50px]">Disc%</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[65px]">Net Rate</TableHead>
                                        <TableHead className="text-[10px] font-medium text-right w-[75px]">Net Value</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[70px]">Division</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[50px]">Src</TableHead>
                                        <TableHead className="text-[10px] font-medium w-[60px] text-center">Act</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {cust.lines.map((d, lIdx) => (
                                        <TableRow key={d.dispatch_id || lIdx} className={`hover:bg-slate-50/50 ${d.order_match_status === 'unmatched' ? 'bg-red-50/40' : ''} ${selectedForCertify.has(d.dispatch_id) ? 'bg-emerald-50/60' : ''}`}>
                                          {(user?.role === 'admin' || user?.role === 'accounts') && (
                                            <TableCell className="py-1.5 text-center">
                                              {d.order_match_status === 'unmatched' ? (
                                                <input
                                                  type="checkbox"
                                                  className="h-3 w-3 accent-emerald-600 cursor-pointer"
                                                  checked={selectedForCertify.has(d.dispatch_id)}
                                                  onChange={() => toggleCertifySelection(d.dispatch_id)}
                                                  title="Select to certify together with others"
                                                />
                                              ) : null}
                                            </TableCell>
                                          )}
                                          <TableCell className="text-xs font-mono py-1.5">
                                            {d.plant_invoice || '-'}
                                            {blockedDispatchIds.has(d.dispatch_id) && (
                                              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 text-[9px] font-sans font-semibold align-middle" title={blockedDispatchIds.get(d.dispatch_id)}>Dispatch after block</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-xs py-1.5">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span><TradeName row={d} /></span>
                                              {d.order_match_status === 'unmatched' && (
                                                <span
                                                  className="inline-flex items-center gap-0.5 text-[9px] leading-none font-semibold text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 whitespace-nowrap"
                                                  title="Order copy not uploaded — ask marketing or upload it"
                                                >
                                                  <AlertTriangle size={9} />
                                                  Missing Order
                                                </span>
                                              )}
                                              {d.order_certified && (
                                                <span
                                                  className="inline-flex items-center gap-0.5 text-[9px] leading-none font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 whitespace-nowrap"
                                                  title={`Was Missing Order — certified by ${d.order_certified_by_name || d.order_certified_by_email || 'admin'}${d.order_certified_at ? ' on ' + new Date(d.order_certified_at).toLocaleDateString() : ''}. Reason: ${d.order_certified_reason || '-'}${d.order_certified_note ? ' · ' + d.order_certified_note : ''}`}
                                                >
                                                  <ShieldCheck size={9} />
                                                  Certified
                                                </span>
                                              )}
                                              {d.order_match_status === 'matched_mix' && (
                                                <span
                                                  className="inline-flex items-center gap-0.5 text-[9px] leading-none font-semibold text-indigo-700 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5 whitespace-nowrap"
                                                  title={`Matched to a mix bale authorisation${d.mix_authorisation_id ? ' — ' + d.mix_authorisation_id : ''}`}
                                                >
                                                  Mix Auth
                                                </span>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs font-mono py-1.5">{d.customer_reference || '-'}</TableCell>
                                          {showForCol && <TableCell className="py-1.5">
                                            {isWsDivision(d.division)
                                              ? <ForBadge kind={d.end_customer_kind} name={d.end_customer_name} />
                                              : <span className="text-[10px] text-slate-300">—</span>}
                                          </TableCell>}
                                          <TableCell className="text-[10px] py-1.5 max-w-[120px] truncate" title={d.store_name ? `${d.store_name}${d.ship_to_code ? ` (${d.ship_to_code})` : ''}` : ''}>{d.store_name || '—'}</TableCell>
                                          <TableCell className="text-xs text-right font-semibold py-1.5">{fmtNum(d.no_of_pieces)}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{fmtDec(d.batch_qty_metres)}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{d.so_price ? `₹${fmtDec(d.so_price)}` : '-'}</TableCell>
                                          <TableCell className="text-xs text-right py-1.5">
                                            {(() => {
                                              const eff = effectiveDiscPct(d.so_price, d.net_rate);
                                              return (eff != null && eff > 0)
                                                ? <span className="text-amber-600 font-medium">{fmtDec(eff)}%</span>
                                                : '-';
                                            })()}
                                          </TableCell>
                                          <TableCell className="text-xs text-right py-1.5">{d.net_rate ? `₹${fmtDec(d.net_rate)}` : '-'}</TableCell>
                                          <TableCell className="text-xs text-right font-semibold py-1.5">{d.net_value ? `₹${fmtNum(d.net_value)}` : '-'}</TableCell>
                                          <TableCell className="text-[10px] py-1.5">{d.division}</TableCell>
                                          <TableCell className="py-1.5">
                                            <Badge variant="outline" className={`text-[9px] ${d.source === 'manual' ? 'border-purple-300 text-purple-600' : 'border-gray-300 text-gray-600'}`}>
                                              {d.source || 'imp'}
                                            </Badge>
                                          </TableCell>
                                          <TableCell className="text-center py-1.5">
                                            <div className="flex items-center justify-center gap-0.5">
                                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditDispatch(d)} title="Edit">
                                                <Pencil size={11} />
                                              </Button>
                                              {d.order_match_status === 'unmatched' && (user?.role === 'admin' || user?.role === 'accounts') && (
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700" onClick={() => openCertifyDialog(d)} title="Certify as direct (no order copy needed)">
                                                  <ShieldCheck size={11} />
                                                </Button>
                                              )}
                                              {d.order_certified && user?.role === 'admin' && (
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-amber-600 hover:text-amber-700" onClick={() => uncertifyDispatch(d)} title="Un-certify (revert to Missing Order)">
                                                  <ShieldOff size={11} />
                                                </Button>
                                              )}
                                              {user?.role === 'admin' && (
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => {
                                                  // Count sibling lines on the same invoice within the same
                                                  // division — surfaces the 'delete entire dispatch' option
                                                  // when an invoice spans multiple batch lines. Only group
                                                  // when the invoice number is present.
                                                  const inv = (d.plant_invoice || '').trim();
                                                  const siblings = inv
                                                    ? dispatchesTableData.filter(x =>
                                                        (x.plant_invoice || '').trim() === inv && x.division === d.division && !x.deleted
                                                      ).length
                                                    : 1;
                                                  setDeleteTarget({ type: 'dispatch', item: d, siblings });
                                                  setDeleteConfirmOpen(true);
                                                }} title="Delete">
                                                  <Trash2 size={11} />
                                                </Button>
                                              )}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {/* Customer Subtotal */}
                                      <TableRow className="bg-slate-50/80 border-t">
                                        {/* checkbox (admin/accounts) + Invoice + Trade Name + Sort No + For + Store */}
                                        <TableCell colSpan={((user?.role === 'admin' || user?.role === 'accounts') ? 6 : 5) - (showForCol ? 0 : 1)} className="text-[10px] font-semibold text-right py-1">Subtotal ({cust.name}):</TableCell>
                                        <TableCell className="text-xs text-right font-bold py-1">{fmtNum(cust.totalPieces)}</TableCell>
                                        <TableCell className="text-xs text-right font-bold py-1">{fmtDec(cust.totalMetres)}</TableCell>
                                        <TableCell colSpan={2} className="py-1"></TableCell>
                                        <TableCell className="py-1"></TableCell>
                                        <TableCell className="text-xs text-right font-bold py-1">{cust.totalNetValue ? `₹${fmtNum(cust.totalNetValue)}` : '-'}</TableCell>
                                        <TableCell colSpan={3} className="py-1"></TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            ))}

                            {/* Day Total */}
                            <div className="flex items-center justify-between px-4 py-2 bg-emerald-100/70 border-t-2 border-emerald-300">
                              <span className="text-xs font-bold text-emerald-800">Day Total — {dateGroup.formattedDate}</span>
                              <div className="flex items-center gap-4 text-xs font-bold text-emerald-800">
                                <span>{dateGroup.dayLines} dispatches</span>
                                <span>{fmtDec(dateGroup.dayMetres)} mtrs</span>
                                <span>{fmtNum(dateGroup.dayPieces)} pcs</span>
                                <span>{fmtLakhs(dateGroup.dayNetValue)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">
                      Showing {dispatchesPage * dispatchesPageSize + 1}-{Math.min((dispatchesPage + 1) * dispatchesPageSize, dispatchesTableTotal)} of {fmtNum(dispatchesTableTotal)} dispatches
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={dispatchesPage === 0} onClick={() => setDispatchesPage(p => p - 1)}>
                        <ChevronLeft size={14} className="mr-1" /> Prev
                      </Button>
                      <span className="text-xs text-muted-foreground">Page {dispatchesPage + 1} of {Math.ceil(dispatchesTableTotal / dispatchesPageSize) || 1}</span>
                      <Button variant="outline" size="sm" disabled={(dispatchesPage + 1) * dispatchesPageSize >= dispatchesTableTotal} onClick={() => setDispatchesPage(p => p + 1)}>
                        Next <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* TAB: MIX BALES (informal authorisations for mix dispatches) */}
        <TabsContent value="mix_auths" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Package className="text-indigo-600" size={18} /> Mix Bale Authorisations
                  </CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Informal bale orders (email/WhatsApp/call) for mix dispatches. Tracked in bales — each mix dispatch invoice = 1 bale against the oldest open authorisation for that customer + fabric type. Default expiry 60 days.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={mixStatusFilter} onValueChange={setMixStatusFilter}>
                    <SelectTrigger className="h-9 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="fulfilled">Fulfilled</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                  {(user?.role === 'admin' || user?.role === 'accounts') && (
                    <Button variant="outline" size="sm" onClick={reconcileMixAuths} title="Re-run FIFO matching against all mix dispatches">
                      <ArrowUpDown size={14} className="mr-1" /> Reconcile
                    </Button>
                  )}
                  {(user?.role === 'admin' || user?.role === 'accounts') && (
                    <Button size="sm" onClick={openMixDialog} className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus size={14} className="mr-1" /> Mix Authorisation
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Needs-fabric banner — mix dispatches the auto-reconciler couldn't classify */}
              {mixNeedsFabric.length > 0 && (user?.role === 'admin' || user?.role === 'accounts') && (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs">
                  <div className="flex items-center gap-2 text-amber-900">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200 text-[11px] font-bold">!</span>
                    <span>
                      <strong>{mixNeedsFabric.length}</strong> mix dispatch(es) need fabric assignment
                      <span className="text-amber-700"> — they won't appear in Mix Bale Shirting / Suiting buckets until tagged.</span>
                    </span>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300" onClick={openMixFabricDialog}>
                    Assign Fabric
                  </Button>
                </div>
              )}
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                {[
                  { label: 'Total', value: mixSummary.total || 0, color: 'bg-gray-50 text-gray-800' },
                  { label: 'Open Bales', value: mixSummary.open_bales || 0, color: 'bg-blue-50 text-blue-700' },
                  { label: 'Fulfilled', value: mixSummary.fulfilled || 0, color: 'bg-green-50 text-green-700' },
                  { label: 'Cancelled', value: mixSummary.cancelled || 0, color: 'bg-gray-50 text-gray-600' },
                  { label: 'Expired', value: mixSummary.expired || 0, color: 'bg-amber-50 text-amber-700' },
                ].map(s => (
                  <div key={s.label} className={`p-2 rounded-lg text-center ${s.color}`}>
                    <p className="text-lg font-bold leading-none">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-wide mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {mixLoading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
              ) : mixAuths.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No mix authorisations yet. Click "Mix Authorisation" to log an email / WhatsApp order.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-[10px]">Date</TableHead>
                        <TableHead className="text-[10px]">Customer</TableHead>
                        <TableHead className="text-[10px]">Fabric</TableHead>
                        <TableHead className="text-[10px] text-center">Bales</TableHead>
                        <TableHead className="text-[10px] text-right">Rate (₹)</TableHead>
                        <TableHead className="text-[10px]">Expires</TableHead>
                        <TableHead className="text-[10px]">Status</TableHead>
                        <TableHead className="text-[10px]">Source</TableHead>
                        <TableHead className="text-[10px] text-center">Act</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mixAuths.map(a => {
                        const balance = Math.max(0, (a.bales_authorised || 0) - (a.bales_dispatched || 0));
                        const pct = a.bales_authorised ? Math.min(100, Math.round(((a.bales_dispatched || 0) / a.bales_authorised) * 100)) : 0;
                        const statusColor = a.status === 'fulfilled' ? 'bg-green-100 text-green-800 border-green-200'
                          : a.status === 'cancelled' ? 'bg-gray-200 text-gray-600 border-gray-300'
                          : a.status === 'expired' ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : 'bg-blue-100 text-blue-800 border-blue-200';
                        return (
                          <TableRow key={a.auth_id} className="text-xs">
                            <TableCell className="py-1.5">{a.authorised_on || '-'}</TableCell>
                            <TableCell className="py-1.5">
                              <div className="font-medium">{a.customer_name || '-'}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">{a.customer_code}</div>
                            </TableCell>
                            <TableCell className="py-1.5 capitalize">{(a.fabric_type || '').replace('_', ' ')}</TableCell>
                            <TableCell className="py-1.5 text-center">
                              <div className="font-semibold">{a.bales_dispatched || 0} / {a.bales_authorised}</div>
                              <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                                <div className={`h-1 rounded-full ${pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                              </div>
                              {balance > 0 && a.status === 'open' && (
                                <div className="text-[10px] text-blue-700 mt-0.5">{balance} open</div>
                              )}
                            </TableCell>
                            <TableCell className="py-1.5 text-right">{a.flat_rate ? `₹${fmtDec(a.flat_rate)}` : '-'}</TableCell>
                            <TableCell className="py-1.5">{a.expires_on || '-'}</TableCell>
                            <TableCell className="py-1.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] border ${statusColor} capitalize`}>{a.status}</span>
                            </TableCell>
                            <TableCell className="py-1.5 capitalize">{a.source || '-'}</TableCell>
                            <TableCell className="py-1.5 text-center">
                              {a.status === 'open' && (user?.role === 'admin' || user?.role === 'accounts') && (
                                <Button variant="ghost" size="sm" className="h-6 px-2 text-red-600 text-[10px]" onClick={() => cancelMixAuth(a)}>
                                  Cancel
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 8: IMPORT */}
        <TabsContent value="import" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <FileSpreadsheet size={22} className="text-indigo-500" />
                Import Orders / Dispatches
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* All users can access import tab — GG PDF is always available, order import needs can_import_orders */}
              {(
              <>
              {/* Import Type */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Import Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ...(user?.role !== 'sales_member' || user?.can_import_orders ? [
                      { value: 'orders', label: 'Order Copy', desc: 'Import customer orders (Arvind format)', color: 'blue' },
                    ] : []),
                    ...(user?.role !== 'sales_member' ? [
                      { value: 'dispatches', label: 'Dispatch Report', desc: 'Import Arvind dispatches (Excel/TSV)', color: 'emerald' },
                    ] : []),
                    ...(user?.role !== 'sales_member' || user?.can_import_orders ? [
                      { value: 'orders_gg', label: 'GG Order Copy', desc: 'Import GG orders (Excel — flat or block format)', color: 'cyan' },
                    ] : []),
                    { value: 'dispatches_gg_pdf', label: 'GG Invoice PDF', desc: 'Import GG dispatch from Reliance PDF annexure', color: 'purple' },
                  ].map(t => (
                    <label key={t.value}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        importType === t.value
                          ? `border-${t.color}-500 bg-${t.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input type="radio" name="importTypeOD" value={t.value}
                        checked={importType === t.value}
                        onChange={e => setImportType(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-semibold">{t.label}</span>
                        <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Division */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Division *</Label>
                <Select value={importDivision} onValueChange={setImportDivision}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue placeholder="Select division..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(user?.role === 'sales_member' && user?.import_orders_scope === 'own'
                      ? DIVISIONS.filter(d => (user?.employee_divisions || []).includes(d))
                      : DIVISIONS
                    ).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Order metadata — taker + place (order imports only).
                  Applies to every line of the uploaded file(s). */}
              {(importType === 'orders' || importType === 'orders_gg') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Order taken by</Label>
                    <Select value={importTaker} onValueChange={setImportTaker}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {orderTakers.some(t => t.kind !== 'company') && (
                          <SelectGroup>
                            <SelectLabel className="text-[11px] font-bold text-indigo-600">Our team</SelectLabel>
                            {orderTakers.filter(t => t.kind !== 'company').map(t => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                          </SelectGroup>
                        )}
                        {orderTakers.some(t => t.kind === 'company') && (
                          <SelectGroup>
                            <SelectLabel className="text-[11px] font-bold text-amber-600">Company / principal employee</SelectLabel>
                            {orderTakers.filter(t => t.kind === 'company').map(t => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Place of order</Label>
                    <Select value={importPlace} onValueChange={setImportPlace}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Select place..." /></SelectTrigger>
                      <SelectContent>
                        {orderPlaces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">Applied to every order line in this upload. Editable later from Order Summary.</p>
                  </div>
                </div>
              )}

              {/* File */}
              <div>
                <Label className="text-sm font-medium mb-2 block">{importType === 'dispatches_gg_pdf' ? 'PDF File(s)' : 'Excel File(s)'}{importFiles.length > 1 ? ` — ${importFiles.length} files selected` : ''}</Label>
                <div className="flex flex-col md:flex-row gap-3">
                  <Input id="od-file-input" type="file" multiple accept={importType === 'dispatches_gg_pdf' ? '.pdf' : '.xlsx,.xls'}
                    onChange={e => { const fileList = Array.from(e.target.files || []); setImportFiles(fileList); setImportFile(fileList[0] || null); setImportResult(null); }}
                    className="flex-1"
                  />
                  <Button onClick={handleImport} disabled={!importFile || !importDivision || uploading}>
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Import
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Result */}
              {importResult && (
                <div className={`p-4 rounded-lg border-2 ${importResult.status === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.status === 'success' ? <CheckCircle size={20} className="text-green-600" /> : <AlertTriangle size={20} className="text-red-600" />}
                    <span className="font-semibold">{importResult.message}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {Object.entries(importResult.summary || {}).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span>
                        <span className="ml-1 font-semibold">{v}</span>
                      </div>
                    ))}
                  </div>
                  {(importResult.errors || []).length > 0 && (
                    <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700 max-h-24 overflow-y-auto">
                      {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                    </div>
                  )}
                </div>
              )}

              {/* Format Info */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-semibold mb-2">Expected Formats</h4>
                {importType === 'orders' ? (
                  <div className="text-sm space-y-1">
                    <p><strong>Order Copy (Arvind):</strong> Excel with columns: Date, Order No., Customer Code, Shop Name, Product Code, DTR Trade Name, Units, Ex Mill Price, Category, etc.</p>
                    <p className="text-xs text-muted-foreground">Each row = 1 SKU line in an order. Multiple rows per order number.</p>
                  </div>
                ) : importType === 'orders_gg' ? (
                  <div className="text-sm space-y-1">
                    <p><strong>GG Order Annexure:</strong> Excel with columns:</p>
                    <p className="text-xs text-muted-foreground">Payer, Customer, Order Date, Sort No/EAN, Material Description, Quantity (pcs), CTG, Rate, Disc%, Net Rate</p>
                    <p className="text-xs text-muted-foreground">Discount is in % terms (matched to absolute value in dispatch annexure). EAN codes normalized for piece-level matching.</p>
                    <p className="text-xs text-muted-foreground">Order number auto-generated as GG_{'{payer}'}_{'{date}'}. Legacy formats (F-1 flat, F-2 block) also supported.</p>
                  </div>
                ) : (
                  <div className="text-sm space-y-1">
                    <p><strong>Dispatch Report (Arvind):</strong> Excel with columns: PO No./Line, Delivery No./Line, Sold To Party, Product, Batch Qty., No. of Pieces, Plant Invoice, Net Value, etc.</p>
                    <p className="text-xs text-muted-foreground">Sheet1 = detailed transactions. Dispatches without matching orders will be marked as "NA/Direct".</p>
                  </div>
                )}
              </div>
              </>
              )}

              {/* Clear Dispatches */}
              {user?.role === 'admin' && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-red-800">Clear Imported Data</h4>
                      <p className="text-xs text-red-600 mt-1">Permanently delete all dispatches for a division or all divisions. Use before reimporting corrected data.</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => { setPurgeDivision('all'); setPurgeConfirmText(''); fetchPurgeCount('all'); setPurgeDialogOpen(true); }}>
                      <Trash2 size={14} className="mr-1" /> Clear Dispatches
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import History */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock size={18} className="text-indigo-500" />
                Import History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {importHistoryLoading ? (
                <div className="flex items-center justify-center h-16">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                </div>
              ) : importHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No imports yet.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold">Date</TableHead>
                        <TableHead className="text-xs font-semibold">Type</TableHead>
                        <TableHead className="text-xs font-semibold">Division</TableHead>
                        <TableHead className="text-xs font-semibold">File Name</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Records</TableHead>
                        <TableHead className="text-xs font-semibold">Date Range</TableHead>
                        <TableHead className="text-xs font-semibold">Uploaded By</TableHead>
                        <TableHead className="text-xs font-semibold">Status</TableHead>
                        {user?.role === 'admin' && <TableHead className="text-xs font-semibold text-center">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importHistory.map((imp, idx) => (
                        <TableRow key={imp.batch_id || idx}>
                          <TableCell className="text-xs">{imp.uploaded_at ? new Date(imp.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${imp.import_type === 'dispatches' ? 'border-emerald-300 text-emerald-700' : 'border-blue-300 text-blue-700'}`}>
                              {imp.import_type || imp.file_type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{imp.division || '-'}</TableCell>
                          <TableCell className="text-xs font-mono max-w-[200px] truncate" title={imp.original_name || imp.filename}>{imp.original_name || imp.filename || '-'}</TableCell>
                          <TableCell className="text-xs text-right font-semibold">{fmtNum(imp.records_imported || imp.imported || 0)}</TableCell>
                          <TableCell className="text-xs">
                            {imp.date_range_start && imp.date_range_end
                              ? `${imp.date_range_start} to ${imp.date_range_end}`
                              : '-'}
                          </TableCell>
                          <TableCell className="text-xs">{imp.uploaded_by || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${imp.status === 'completed' ? 'border-green-300 text-green-700' : imp.status === 'rolled_back' ? 'border-red-300 text-red-700' : 'border-gray-300 text-gray-600'}`}>
                              {imp.status || 'completed'}
                            </Badge>
                          </TableCell>
                          {user?.role === 'admin' && (  /* rollback is admin-only on the server (F-43) */
                            <TableCell className="text-center">
                              {imp.status !== 'rolled_back' ? (
                                <Button
                                  variant="ghost" size="sm"
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50 h-7 px-2 text-xs"
                                  disabled={rollbackLoading === imp.batch_id}
                                  onClick={() => handleRollback(imp)}
                                >
                                  {rollbackLoading === imp.batch_id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1" />
                                  ) : (
                                    <RotateCcw size={12} className="mr-1" />
                                  )}
                                  Rollback
                                </Button>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">Rolled back</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Standing footnote — so nobody has to remember what the figures are. */}
      <p className="text-[11px] text-muted-foreground border-t border-border/60 pt-3 mt-2">
        All values on this page exclude GST (net value after discounts, before tax) · rates are per metre ·
        goods returns are netted in the month they were raised · free samples are excluded.
      </p>

      {/* GR Create Dialog */}
      <Dialog open={grDialogOpen} onOpenChange={setGrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Goods Return</DialogTitle>
            <DialogDescription>
              Record a return for {grDispatch?.customer_name} - {grDispatch?.trade_name} ({grDispatch?.product_code})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Units to Return *</Label>
              <Input type="number" min={1} max={grDispatch?.no_of_pieces - (grDispatch?.gr_units || 0)}
                value={grUnits} onChange={e => setGrUnits(parseInt(e.target.value) || 1)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Available: {(grDispatch?.no_of_pieces || 0) - (grDispatch?.gr_units || 0)} of {grDispatch?.no_of_pieces} pieces
              </p>
            </div>
            <div>
              <Label>Return Reason *</Label>
              <Select value={grReason} onValueChange={setGrReason}>
                <SelectTrigger><SelectValue placeholder="Select reason..." /></SelectTrigger>
                <SelectContent>
                  {GR_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Return Type</Label>
              <Select value={grType} onValueChange={setGrType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GR_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea value={grRemarks} onChange={e => setGrRemarks(e.target.value)} placeholder="Optional remarks..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateGr} disabled={grSubmitting || !grReason}>
              {grSubmitting ? 'Creating...' : 'Create Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Report dialog — period-scoped PDF/Excel for a single customer */}
      <CustomerReportDialog
        open={reportDialogOpen}
        onOpenChange={(o) => { setReportDialogOpen(o); if (!o) setReportTarget(null); }}
        customer={reportTarget ? { customer_code: reportTarget.customer_code, name: reportTarget.name } : null}
        defaultPeriod="day"
        defaultPeriodValue={reportTarget?.dateKey}
        defaultDivision={filterDivision !== 'all' ? filterDivision : undefined}
      />

      {/* Goods Return statement dialog — period-scoped PDF for a customer or the whole view */}
      <GoodsReturnReportDialog
        open={grReportOpen}
        onOpenChange={(o) => { setGrReportOpen(o); if (!o) setGrReportTarget(null); }}
        customer={grReportTarget ? { customer_code: grReportTarget.customer_code, name: grReportTarget.name } : null}
        division={filterDivision !== 'all' ? filterDivision : undefined}
        defaultPeriod={grReportTarget?.dateKey ? 'day' : 'month'}
        defaultPeriodValue={grReportTarget?.dateKey}
      />

      {/* Certify Missing-Order Dialog — handles both single-row and bulk */}
      <Dialog open={certifyOpen} onOpenChange={(open) => { if (!open) { setCertifyOpen(false); setCertifyTarget(null); setBulkCertifyTargets([]); setCertifyBulkMode(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-600" />
              {certifyBulkMode
                ? `Certify ${bulkCertifyTargets.length} Dispatch${bulkCertifyTargets.length === 1 ? '' : 'es'} as Direct`
                : 'Certify as Direct Dispatch'}
            </DialogTitle>
            <DialogDescription>
              {certifyBulkMode
                ? 'All selected dispatches will be certified with the same reason and note. Your name will be recorded as the certifier.'
                : 'Mark this missing-order dispatch as intentionally direct (e.g. TAS auto-replenishment). It will drop out of the Missing Orders list. Your name will be recorded as the certifier.'}
            </DialogDescription>
          </DialogHeader>
          {!certifyBulkMode && certifyTarget && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                <div><span className="font-medium">Customer:</span> {certifyTarget.customer_name || '-'}</div>
                <div><span className="font-medium">Trade:</span> {certifyTarget.trade_name || '-'}</div>
                <div><span className="font-medium">Invoice:</span> {certifyTarget.plant_invoice || '-'}</div>
              </div>
            </div>
          )}
          {certifyBulkMode && bulkCertifyTargets.length > 0 && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 max-h-40 overflow-y-auto space-y-1">
              {bulkCertifyTargets.map((t) => (
                <div key={t.dispatch_id} className="flex items-center gap-2 border-b border-slate-100 last:border-0 pb-1 last:pb-0">
                  <span className="font-mono text-[10px] text-slate-500">{t.plant_invoice || '-'}</span>
                  <span className="truncate flex-1">{t.customer_name} · {t.trade_name}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
              <Input
                value={certifyReason}
                onChange={(e) => setCertifyReason(e.target.value)}
                placeholder="e.g. TAS auto-replenishment, Sample/no-charge, Manual approval"
                className="mt-1 text-sm"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                value={certifyNote}
                onChange={(e) => setCertifyNote(e.target.value)}
                placeholder="Additional context — e.g. approval chain, ticket reference"
                className="mt-1 text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCertifyOpen(false); setCertifyTarget(null); setBulkCertifyTargets([]); setCertifyBulkMode(false); }} disabled={certifySubmitting}>
              Cancel
            </Button>
            <Button onClick={submitCertify} disabled={certifySubmitting || !certifyReason.trim()}>
              {certifySubmitting
                ? 'Certifying...'
                : (certifyBulkMode ? `Certify ${bulkCertifyTargets.length}` : 'Certify')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag order lines to a retailer / own shop ("For") */}
      <ImportRetailerTagsDialog prompt={retailerPrompt?.data || null} fileName={retailerPrompt?.fileName} onDone={finishRetailerPrompt} />

      <Dialog open={tagDialogOpen} onOpenChange={(open) => { if (!open) closeTagDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <Tag size={16} /> Tag to…
            </DialogTitle>
            <DialogDescription>
              Record who the wholesaler placed these order lines for. Dispatches follow their matched order line, so the Customer Report can be read by retailer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {tagFromCell && (
              <RadioGroup value={tagScope} onValueChange={changeTagScope} className="grid grid-cols-2 gap-2">
                <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${tagScope === 'order' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value="order" /> Whole order {tagFromCell.orderNo}
                </Label>
                <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${tagScope === 'line' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value="line" /> Only this line
                </Label>
              </RadioGroup>
            )}
            {/* Selection summary */}
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
              <div>
                <span className="font-medium">Customer:</span>{' '}
                {tagSelection.codes.length === 1
                  ? <>{tagSelection.customerName || '-'} <span className="text-muted-foreground">({tagSelection.customerCode || '-'})</span></>
                  : <span className="text-red-600">{tagSelection.codes.length} different customers</span>}
              </div>
              <div><span className="font-medium">Lines:</span> {tagSelection.lines.length} · <span className="font-medium">Orders:</span> {tagSelection.orderNos.length ? tagSelection.orderNos.join(', ') : '-'}</div>
              {tagSelection.single && (
                <div><span className="font-medium">Sort:</span> {tagSelection.single.product_code || '-'} · {tagSelection.single.trade_name || '-'} · {fmtNum(tagSelection.single.units_ordered)} units</div>
              )}
            </div>
            {tagSelection.codes.length > 1 && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-700">
                The selected lines belong to more than one customer. Retailers hang off one wholesaler, so tag one customer at a time.
              </div>
            )}

            {!tagSplitOn && (
              <RadioGroup value={tagKind} onValueChange={(v) => { setTagKind(v); if (v !== 'retailer') setTagNewRetailerOpen(false); }} className="grid grid-cols-3 gap-2">
                <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${tagKind === 'own' ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value="own" /> Own shop
                </Label>
                <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${tagKind === 'retailer' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value="retailer" /> Retailer
                </Label>
                <Label className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${tagKind === 'clear' ? 'border-slate-500 bg-slate-100' : 'border-slate-200'}`}>
                  <RadioGroupItem value="clear" /> Clear tag
                </Label>
              </RadioGroup>
            )}

            {!tagSplitOn && tagKind === 'retailer' && (
              <div className="space-y-2">
                <Label className="text-xs">Retailer</Label>
                <Select
                  value={tagRetailerId}
                  onValueChange={(v) => { if (v === '__add__') { setTagNewRetailerOpen(true); } else { setTagRetailerId(v); } }}
                  disabled={tagRetailersLoading || tagSelection.codes.length !== 1}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={tagRetailersLoading ? 'Loading retailers…' : (tagRetailers.length ? 'Pick a retailer' : 'No retailers yet — add one')} />
                  </SelectTrigger>
                  <SelectContent>
                    {tagRetailers.map(r => (
                      <SelectItem key={r.retailer_id} value={r.retailer_id}>{r.name}{r.city ? ` · ${r.city}` : ''}</SelectItem>
                    ))}
                    <SelectItem value="__add__" className="text-indigo-700 font-medium">+ Add new retailer…</SelectItem>
                  </SelectContent>
                </Select>
                {tagNewRetailerOpen && (
                  <div className="flex items-center gap-2">
                    <Input
                      value={tagNewRetailerName}
                      onChange={(e) => setTagNewRetailerName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTagRetailer(); } }}
                      placeholder="Retailer name (a number is fine if the wholesaler withholds names)"
                      className="h-8 text-sm"
                      maxLength={120}
                      autoFocus
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={addTagRetailer} disabled={tagAddingRetailer || !tagNewRetailerName.trim()}>
                      {tagAddingRetailer ? 'Adding…' : 'Add'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTagNewRetailerOpen(false); setTagNewRetailerName(''); }} disabled={tagAddingRetailer}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Split — only when exactly one line is selected */}
            {tagSelection.single && tagSelection.codes.length === 1 && !(tagFromCell && tagScope === 'order') && (
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox checked={tagSplitOn} onCheckedChange={(v) => setTagSplitOn(!!v)} />
                Split this line between retailers
              </label>
            )}
            {tagSplitOn && tagSelection.single && (
              <div className="space-y-2 border rounded-lg p-2 bg-violet-50/40 border-violet-200">
                {tagSplitRows.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={row.who} onValueChange={(v) => {
                      if (v === '__add__') { setTagNewRetailerOpen(true); return; }
                      setTagSplitRows(rows => rows.map((r, i) => i === idx ? { ...r, who: v } : r));
                    }}>
                      <SelectTrigger className="h-8 text-xs flex-1">
                        <SelectValue placeholder="Own shop or retailer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own">Own shop</SelectItem>
                        {tagRetailers.map(r => (
                          <SelectItem key={r.retailer_id} value={r.retailer_id}>{r.name}{r.city ? ` · ${r.city}` : ''}</SelectItem>
                        ))}
                        <SelectItem value="__add__" className="text-indigo-700 font-medium">+ Add new retailer…</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" min={1} step={1}
                      value={row.units}
                      onChange={(e) => setTagSplitRows(rows => rows.map((r, i) => i === idx ? { ...r, units: e.target.value } : r))}
                      placeholder="units"
                      className="h-8 text-xs w-20 text-right"
                    />
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => setTagSplitRows(rows => rows.filter((_, i) => i !== idx))} disabled={tagSplitRows.length <= 2} title="Remove row">
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
                {tagNewRetailerOpen && (
                  <div className="flex items-center gap-2">
                    <Input
                      value={tagNewRetailerName}
                      onChange={(e) => setTagNewRetailerName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTagRetailer(); } }}
                      placeholder="New retailer name"
                      className="h-8 text-sm"
                      maxLength={120}
                      autoFocus
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={addTagRetailer} disabled={tagAddingRetailer || !tagNewRetailerName.trim()}>
                      {tagAddingRetailer ? 'Adding…' : 'Add'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTagNewRetailerOpen(false); setTagNewRetailerName(''); }} disabled={tagAddingRetailer}>
                      Cancel
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px]">
                  <button type="button" className="underline text-indigo-700 hover:text-indigo-900" onClick={() => setTagSplitRows(rows => [...rows, { who: '', units: '' }])}>
                    Add row
                  </button>
                  <span className={tagSplitTotal === Number(tagSelection.single.units_ordered || 0) ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                    {fmtNum(tagSplitTotal)} of {fmtNum(tagSelection.single.units_ordered)} units
                  </span>
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground">
              A line is tagged whole. Use Split when one sort in one order is for more than one retailer.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeTagDialog} disabled={tagSubmitting}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submitTag} disabled={!tagCanSubmit}>
              {tagSubmitting ? 'Saving…'
                : tagSplitOn ? 'Save split'
                : tagFromCell && tagScope === 'order' ? (tagKind === 'clear' ? `Clear tag on order ${tagFromCell.orderNo}` : `Tag order ${tagFromCell.orderNo}`)
                : tagKind === 'clear' ? `Clear tag on ${tagSelection.lines.length} line(s)`
                : `Tag ${tagSelection.lines.length} line(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GR Status Update Dialog */}
      <Dialog open={grStatusDialogOpen} onOpenChange={setGrStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Return Status</DialogTitle>
            <DialogDescription>
              Update status for {grStatusItem?.customer_name} - {grStatusItem?.product_code} ({grStatusItem?.return_units} units)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={grNewStatus} onValueChange={setGrNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sent_to_mill">Sent to Mill</SelectItem>
                  <SelectItem value="credit_note_issued">Credit Note Issued</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {grNewStatus === 'credit_note_issued' && (
              <>
                <div>
                  <Label>Credit Note Number</Label>
                  <Input value={grCreditNoteNumber} onChange={e => setGrCreditNoteNumber(e.target.value)} placeholder="CN-12345" />
                </div>
                <div>
                  <Label>Credit Note Amount</Label>
                  <Input type="number" value={grCreditNoteAmount} onChange={e => setGrCreditNoteAmount(parseFloat(e.target.value) || 0)} />
                </div>
              </>
            )}
            <div>
              <Label>Remarks</Label>
              <Textarea value={grStatusRemarks} onChange={e => setGrStatusRemarks(e.target.value)} placeholder="Optional remarks..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateGrStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Order Entry Dialog (Create + Edit mode) */}
      <Dialog open={manualOrderOpen} onOpenChange={(open) => { if (!open) { setEditOrderMode(false); setEditOrderId(null); } setManualOrderOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editOrderMode ? 'Edit Order' : 'Add Manual Order'}</DialogTitle>
            <DialogDescription>
              {editOrderMode ? `Editing order ${manualOrder.order_no}` : "Manually enter an order when the principal's order copy is not available."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Row 1: Order No + Division */}
            <div>
              <Label>Order No. *</Label>
              <Input
                value={manualOrder.order_no}
                onChange={e => updateManualField('order_no', e.target.value)}
                placeholder="e.g. DMDH251260216002"
              />
            </div>
            <div>
              <Label>Division *</Label>
              <Select value={manualOrder.division} onValueChange={v => updateManualField('division', v)}>
                <SelectTrigger><SelectValue placeholder="Select division..." /></SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Customer Code (drives name lookup) + resolved name */}
            <div>
              <Label>Customer Code *</Label>
              <Input
                list="manual-order-customer-codes"
                value={manualOrder.customer_code}
                onChange={e => {
                  const code = e.target.value;
                  updateManualField('customer_code', code);
                  // Auto-fill name/state/city from the directory when the code
                  // resolves to a known customer. The user can't freely type
                  // a name — this prevents spelling drift.
                  const match = customerOptions.find(c => c.customer_code === code || String(c.customer_code).trim() === String(code).trim());
                  if (match) {
                    updateManualField('shop_name', match.name);
                    if (match.state) updateManualField('state', match.state);
                    if (match.city) updateManualField('city', match.city);
                  } else {
                    updateManualField('shop_name', '');
                  }
                }}
                placeholder={manualOrder.division ? 'Type or pick a code…' : 'Select a division first'}
                disabled={!manualOrder.division}
              />
              <datalist id="manual-order-customer-codes">
                {customerOptions.map(c => (
                  <option key={c.customer_id || c.customer_code} value={c.customer_code}>
                    {c.name}{c.city ? ` — ${c.city}` : ''}
                  </option>
                ))}
              </datalist>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {customerOptions.length ? `${customerOptions.length} customers in ${manualOrder.division}` : 'Customers load after division is selected'}
              </p>
            </div>
            <div>
              <Label>Shop Name (auto-filled from code)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualOrder.shop_name
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualOrder.shop_name || 'Auto-filled when code matches'}
              </div>
              {manualOrder.customer_code && !manualOrder.shop_name && (
                <p className="text-[10px] text-amber-600 mt-0.5">Code not recognised for this division — check and re-enter</p>
              )}
            </div>

            {manualForShown && (
              <div className="col-span-full">
                <Label>For (who the wholesaler placed this order for)</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <Select value={manualFor || 'untagged'} onValueChange={(v) => setManualFor(v === 'untagged' ? '' : v)}
                    disabled={!manualOrder.shop_name}>
                    <SelectTrigger className="sm:w-64">
                      <SelectValue placeholder="Not tagged" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="untagged">Not tagged (decide later)</SelectItem>
                      <SelectItem value="own">Own shop</SelectItem>
                      {manualRetailers.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-[11px] font-bold text-sky-700">Retailers</SelectLabel>
                          {manualRetailers.map(r => (
                            <SelectItem key={r.retailer_id} value={`ret:${r.retailer_id}`}>{r.name}{r.alias ? ` · ${r.alias}` : ''}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      <SelectItem value="new">+ New retailer…</SelectItem>
                    </SelectContent>
                  </Select>
                  {manualFor === 'new' && (
                    <Input maxLength={120} placeholder="Retailer name or number" value={manualForName}
                      onChange={e => setManualForName(e.target.value)} className="flex-1" />
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {manualOrder.shop_name ? 'Tags this order line. Change it later by clicking its For cell on the Orders tab.' : 'Enter a recognised customer code first.'}
                </p>
              </div>
            )}

            {manualStoreShown && manualStores.length > 0 && (
              <div className="col-span-full">
                <Label>Store (ship-to location)</Label>
                <Select value={manualShipTo || '__none__'} onValueChange={(v) => setManualShipTo(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="sm:w-64 mt-1">
                    <SelectValue placeholder="Not specified" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    {manualStores.map(s => (
                      <SelectItem key={s.store_id || s.ship_to_code} value={s.ship_to_code}>
                        {s.display_name || s.name || s.ship_to_code}{s.city ? ` · ${s.city}` : ''}{s.is_head ? '' : ` (${s.ship_to_code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-0.5">Optional. Billing stays on the SAP code; the store only splits orders, dispatches and targets.</p>
              </div>
            )}

            {/* Row 3: Trade Name + Product Code */}
            <div>
              <Label>Trade Name *</Label>
              <Input
                value={manualOrder.trade_name}
                onChange={e => updateManualField('trade_name', e.target.value)}
                placeholder="e.g. TRESCA COTTON LINEN"
              />
            </div>
            <div>
              <Label>Product Code</Label>
              <Input
                value={manualOrder.product_code}
                onChange={e => updateManualField('product_code', e.target.value)}
                placeholder="e.g. 0043003578"
              />
            </div>

            {/* Row 4: Units + Order Date */}
            <div>
              <Label>Units Ordered *</Label>
              <Input
                type="number"
                min={1}
                value={manualOrder.units_ordered}
                onChange={e => updateManualField('units_ordered', parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <Label>Order Date</Label>
              <Input
                type="date"
                value={manualOrder.order_date}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => updateManualField('order_date', e.target.value)}
              />
            </div>

            {/* Row 5: State + City — auto-filled from customer profile */}
            <div>
              <Label>State (from customer profile)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualOrder.state
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualOrder.state || 'Auto-filled when code matches'}
              </div>
            </div>
            <div>
              <Label>City (from customer profile)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualOrder.city
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualOrder.city || 'Auto-filled when code matches'}
              </div>
            </div>

            {/* Row 6: Category + Composition */}
            <div>
              <Label>Category</Label>
              {categoryOptions.length > 0 ? (
                <Select
                  value={manualOrder.category}
                  onValueChange={v => updateManualField('category', v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select category..." /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={manualOrder.category}
                  onChange={e => updateManualField('category', (e.target.value || '').toUpperCase())}
                  placeholder={manualOrder.division ? 'No categories yet — type to create one' : 'Select a division first'}
                  disabled={!manualOrder.division}
                />
              )}
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {categoryOptions.length > 0
                  ? `${categoryOptions.length} existing categor${categoryOptions.length === 1 ? 'y' : 'ies'} in ${manualOrder.division || 'this division'}`
                  : 'First category for this division — will appear in the dropdown next time'}
              </p>
            </div>
            <div>
              <Label>Ex-Mill Price (₹)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={manualOrder.ex_mill_price}
                onChange={e => updateManualField('ex_mill_price', parseFloat(e.target.value) || 0)}
              />
            </div>

            {/* Row 7: Discount + Net Price */}
            <div>
              <Label>Discount %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={manualOrder.discount_pct}
                onChange={e => updateManualField('discount_pct', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Net Price (₹) <span className="text-muted-foreground text-xs">(auto-calculated)</span></Label>
              <Input
                type="number"
                readOnly
                className="bg-gray-50"
                value={manualOrder.ex_mill_price > 0 ? (manualOrder.ex_mill_price * (1 - (manualOrder.discount_pct || 0) / 100)).toFixed(2) : 0}
              />
            </div>

            {/* Row 8: Remark (full width) */}
            <div className="md:col-span-2">
              <Label>Remark</Label>
              <Textarea
                value={manualOrder.remark}
                onChange={e => updateManualField('remark', e.target.value)}
                placeholder="Any special instructions or notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setManualOrderOpen(false)}>Cancel</Button>
            <Button onClick={handleManualOrderSubmit} disabled={manualSubmitting}>
              {manualSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {editOrderMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  {editOrderMode ? <><Save size={16} className="mr-1" /> Save Changes</> : <><Plus size={16} className="mr-1" /> Create Order</>}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variance Detail Dialog (side-by-side order vs dispatch) */}
      <Dialog open={varianceDialogOpen} onOpenChange={setVarianceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Variance Detail:
              <Badge variant="outline" className={`text-xs ${
                selectedVariance?.type === 'order_missing' ? 'border-red-400 text-red-700 bg-red-50' :
                selectedVariance?.type === 'excess_dispatch' ? 'border-orange-400 text-orange-700 bg-orange-50' :
                selectedVariance?.type === 'short_dispatch' ? 'border-amber-400 text-amber-700 bg-amber-50' :
                selectedVariance?.type === 'cross_customer' ? 'border-cyan-400 text-cyan-700 bg-cyan-50' :
                selectedVariance?.type === 'substitution' ? 'border-purple-400 text-purple-700 bg-purple-50' :
                selectedVariance?.type === 'price_variance' ? 'border-amber-500 text-amber-800 bg-amber-50' :
                'border-blue-400 text-blue-700 bg-blue-50'
              }`}>
                {selectedVariance?.type?.replace(/_/g, ' ')}
              </Badge>
              {selectedVariance?.dispatch?.match_type && (
                <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-600 bg-indigo-50 ml-1">
                  {selectedVariance.dispatch.match_type.replace(/_/g, ' ')}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Side-by-side comparison of order vs dispatch data
            </DialogDescription>
          </DialogHeader>
          {selectedVariance && (
            <div className="space-y-4">
              {/* Side by side comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Order side */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-indigo-600 border-b pb-1">ORDER</h4>
                  {selectedVariance.order ? (
                    <>
                      <div className="text-xs space-y-1">
                        <p><span className="text-muted-foreground">Order No:</span> <span className="font-medium">{selectedVariance.order.order_no}</span></p>
                        <p><span className="text-muted-foreground">Product:</span> <span className="font-medium">{selectedVariance.order.product_code || '-'}</span></p>
                        <p className={selectedVariance.order.trade_name !== selectedVariance.dispatch?.trade_name ? 'bg-red-50 px-1 rounded' : ''}>
                          <span className="text-muted-foreground">Trade Name:</span> <span className="font-medium">{selectedVariance.order.trade_name || '-'}</span>
                        </p>
                        <p className={selectedVariance.order.units_ordered !== selectedVariance.dispatch?.no_of_pieces ? 'bg-amber-50 px-1 rounded' : ''}>
                          <span className="text-muted-foreground">Units:</span> <span className="font-medium">{selectedVariance.order.units_ordered}</span>
                        </p>
                        <p className={selectedVariance.order.ex_mill_price !== selectedVariance.dispatch?.so_price ? 'bg-amber-50 px-1 rounded' : ''}>
                          <span className="text-muted-foreground">Ex-Mill:</span> <span className="font-medium">₹{selectedVariance.order.ex_mill_price}</span>
                        </p>
                        <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedVariance.order.shop_name}</span></p>
                        <p><span className="text-muted-foreground">Date:</span> <span className="font-medium">{(selectedVariance.order.order_date || '').slice(0, 10)}</span></p>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No matching order in system</p>
                  )}
                </div>

                {/* Dispatch side */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-emerald-600 border-b pb-1">DISPATCH</h4>
                  <div className="text-xs space-y-1">
                    <p><span className="text-muted-foreground">Invoice:</span> <span className="font-medium">{selectedVariance.dispatch?.plant_invoice}</span></p>
                    <p><span className="text-muted-foreground">Product:</span> <span className="font-medium">{selectedVariance.dispatch?.product_code || '-'}</span></p>
                    <p className={selectedVariance.order?.trade_name !== selectedVariance.dispatch?.trade_name ? 'bg-red-50 px-1 rounded' : ''}>
                      <span className="text-muted-foreground">Trade Name:</span> <span className="font-medium">{selectedVariance.dispatch?.trade_name || '-'}</span>
                    </p>
                    <p className={selectedVariance.order?.units_ordered !== selectedVariance.dispatch?.no_of_pieces ? 'bg-amber-50 px-1 rounded' : ''}>
                      <span className="text-muted-foreground">Pieces:</span> <span className="font-medium">{selectedVariance.dispatch?.no_of_pieces}</span>
                    </p>
                    <p className={selectedVariance.order?.ex_mill_price !== selectedVariance.dispatch?.so_price ? 'bg-amber-50 px-1 rounded' : ''}>
                      <span className="text-muted-foreground">SO Price:</span> <span className="font-medium">₹{selectedVariance.dispatch?.so_price}</span>
                    </p>
                    <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedVariance.dispatch?.customer_name}</span></p>
                    <p><span className="text-muted-foreground">Delivery:</span> <span className="font-medium">{(selectedVariance.dispatch?.delivery_date || '').slice(0, 10)}</span></p>
                    <p><span className="text-muted-foreground">Net Rate:</span> <span className="font-medium">₹{selectedVariance.dispatch?.net_rate}</span></p>
                    <p><span className="text-muted-foreground">Metres:</span> <span className="font-medium">{selectedVariance.dispatch?.batch_qty_metres}</span></p>
                    <p><span className="text-muted-foreground">Net Value:</span> <span className="font-medium">₹{fmtNum(selectedVariance.dispatch?.net_value)}</span></p>
                  </div>
                </div>
              </div>

              {/* Variance Analysis */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h4 className="font-semibold text-sm mb-2">Variance Analysis</h4>
                <div className="space-y-1 text-xs">
                  <p>
                    <span className="text-muted-foreground">Quantity:</span>{' '}
                    {selectedVariance.quantity_variance !== 0 ? (
                      <span className={selectedVariance.quantity_variance > 0 ? 'text-orange-600 font-semibold' : 'text-amber-600 font-semibold'}>
                        {selectedVariance.quantity_variance > 0 ? '+' : ''}{selectedVariance.quantity_variance} units ({selectedVariance.quantity_variance_pct > 0 ? '+' : ''}{selectedVariance.quantity_variance_pct}%)
                        {Math.abs(selectedVariance.quantity_variance_pct) > (reconData?.tolerance_settings?.quantity_tolerance_pct || 5)
                          ? <Badge variant="destructive" className="ml-2 text-[10px]">EXCEEDS {reconData?.tolerance_settings?.quantity_tolerance_pct || 5}% TOLERANCE</Badge>
                          : null}
                      </span>
                    ) : <span className="text-green-600">No variance</span>}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Price:</span>{' '}
                    {selectedVariance.price_variance_pct !== 0 ? (
                      <span className={selectedVariance.price_variance_pct < 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                        {selectedVariance.price_variance_pct > 0 ? '+' : ''}{selectedVariance.price_variance_pct}% (₹{selectedVariance.price_variance_amount > 0 ? '+' : ''}{selectedVariance.price_variance_amount}/unit)
                        {Math.abs(selectedVariance.price_variance_pct) > (reconData?.tolerance_settings?.price_tolerance_pct || 2)
                          ? <Badge variant="destructive" className="ml-2 text-[10px]">EXCEEDS {reconData?.tolerance_settings?.price_tolerance_pct || 2}% TOLERANCE</Badge>
                          : null}
                      </span>
                    ) : <span className="text-green-600">No variance</span>}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVarianceDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tolerance Settings Dialog */}
      <Dialog open={toleranceDialogOpen} onOpenChange={setToleranceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reconciliation Settings</DialogTitle>
            <DialogDescription>
              Configure tolerance thresholds for variance detection. Changes apply on next import or manual re-run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity Tolerance (%)</Label>
              <Input
                type="number" min={0} max={50} step={0.5}
                value={toleranceForm.quantity_tolerance_pct}
                onChange={e => setToleranceForm(prev => ({ ...prev, quantity_tolerance_pct: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Dispatches within ±{toleranceForm.quantity_tolerance_pct}% of ordered qty are treated as fulfilled</p>
            </div>
            <div>
              <Label>Price Tolerance (%)</Label>
              <Input
                type="number" min={0} max={50} step={0.5}
                value={toleranceForm.price_tolerance_pct}
                onChange={e => setToleranceForm(prev => ({ ...prev, price_tolerance_pct: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Price differences within ±{toleranceForm.price_tolerance_pct}% are not flagged</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Aging Warning (days)</Label>
                <Input
                  type="number" min={1} max={365}
                  value={toleranceForm.aging_warning_days}
                  onChange={e => setToleranceForm(prev => ({ ...prev, aging_warning_days: parseInt(e.target.value) || 30 }))}
                />
              </div>
              <div>
                <Label>Aging Critical (days)</Label>
                <Input
                  type="number" min={1} max={365}
                  value={toleranceForm.aging_critical_days}
                  onChange={e => setToleranceForm(prev => ({ ...prev, aging_critical_days: parseInt(e.target.value) || 60 }))}
                />
              </div>
            </div>
            <div>
              <Label>Delivery Grace Period (days)</Label>
              <Input
                type="number" min={0} max={90}
                value={toleranceForm.delivery_grace_days}
                onChange={e => setToleranceForm(prev => ({ ...prev, delivery_grace_days: parseInt(e.target.value) || 7 }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Extra days allowed after expected delivery before flagging as missed</p>
            </div>
            <div className="p-3 rounded border border-sky-200 bg-sky-50">
              <Label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-sky-600"
                  checked={toleranceForm.store_aware_matching !== false}
                  onChange={e => setToleranceForm(prev => ({ ...prev, store_aware_matching: e.target.checked }))} />
                <span>
                  <span className="font-medium">Store-aware matching</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    A dispatch fills order lines of its own store first, then lines that named no store; only when nothing else is open does it fill another store's line, and both rows are then flagged "Store swap" (TAS multi-store accounts). Re-Run reconciliation after changing this.
                  </span>
                </span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToleranceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTolerances} disabled={toleranceSaving}>
              {toleranceSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Dispatch Entry Dialog (Create + Edit mode) */}
      <Dialog open={manualDispatchOpen} onOpenChange={(open) => { if (!open) { resetDispatchForm(); } setManualDispatchOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDispatchMode ? 'Edit Dispatch' : 'Add Manual Dispatch'}</DialogTitle>
            <DialogDescription>
              {editDispatchMode ? `Editing dispatch ${manualDispatch.plant_invoice || editDispatchId}` : 'Manually enter a dispatch record.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Row 1: Division + Plant Invoice */}
            <div>
              <Label>Division *</Label>
              <Select value={manualDispatch.division} onValueChange={v => updateDispatchField('division', v)}>
                <SelectTrigger><SelectValue placeholder="Select division..." /></SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plant Invoice</Label>
              <Input value={manualDispatch.plant_invoice} onChange={e => updateDispatchField('plant_invoice', e.target.value)} placeholder="e.g. 5901234567" />
            </div>

            {/* Row 2: Customer Code (drives name lookup) + resolved name */}
            <div>
              <Label>Customer Code *</Label>
              <Input
                list="manual-dispatch-customer-codes"
                value={manualDispatch.customer_code}
                onChange={e => {
                  const code = e.target.value;
                  updateDispatchField('customer_code', code);
                  const match = customerOptions.find(c => c.customer_code === code || String(c.customer_code).trim() === String(code).trim());
                  if (match) {
                    updateDispatchField('customer_name', match.name);
                    if (match.state) updateDispatchField('state', match.state);
                    if (match.city) updateDispatchField('city', match.city);
                  } else {
                    updateDispatchField('customer_name', '');
                  }
                }}
                placeholder={manualDispatch.division ? 'Type or pick a code…' : 'Select a division first'}
                disabled={!manualDispatch.division}
              />
              <datalist id="manual-dispatch-customer-codes">
                {customerOptions.map(c => (
                  <option key={c.customer_id || c.customer_code} value={c.customer_code}>
                    {c.name}{c.city ? ` — ${c.city}` : ''}
                  </option>
                ))}
              </datalist>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {customerOptions.length ? `${customerOptions.length} customers in ${manualDispatch.division}` : 'Customers load after division is selected'}
              </p>
            </div>
            <div>
              <Label>Customer Name (auto-filled from code)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualDispatch.customer_name
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualDispatch.customer_name || 'Auto-filled when code matches'}
              </div>
              {manualDispatch.customer_code && !manualDispatch.customer_name && (
                <p className="text-[10px] text-amber-600 mt-0.5">Code not recognised for this division — check and re-enter</p>
              )}
            </div>

            {/* Row 3: Trade Name + Product Code */}
            <div>
              <Label>Trade Name *</Label>
              <Input value={manualDispatch.trade_name} onChange={e => updateDispatchField('trade_name', e.target.value)} placeholder="e.g. TRESCA COTTON LINEN" />
            </div>
            <div>
              <Label>Product Code</Label>
              <Input value={manualDispatch.product_code} onChange={e => updateDispatchField('product_code', e.target.value)} placeholder="e.g. 0043003578" />
            </div>

            {/* Row 4: Customer Reference (Sort No) + Batch No */}
            <div>
              <Label>Customer Ref / Sort No</Label>
              <Input value={manualDispatch.customer_reference} onChange={e => updateDispatchField('customer_reference', e.target.value)} placeholder="e.g. 0043003578" />
            </div>
            <div>
              <Label>Batch No</Label>
              <Input value={manualDispatch.batch_no} onChange={e => updateDispatchField('batch_no', e.target.value)} placeholder="e.g. 12345" />
            </div>

            {/* Row 5: Pieces + Metres */}
            <div>
              <Label>No. of Pieces *</Label>
              <Input type="number" min={1} value={manualDispatch.no_of_pieces} onChange={e => updateDispatchField('no_of_pieces', parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <Label>Batch Qty (Metres)</Label>
              <Input type="number" min={0} step={0.01} value={manualDispatch.batch_qty_metres} onChange={e => updateDispatchField('batch_qty_metres', parseFloat(e.target.value) || 0)} />
            </div>

            {/* Row 6: Delivery Date + PO No */}
            <div>
              <Label>Delivery Date *</Label>
              <Input type="date" value={manualDispatch.delivery_date} onChange={e => updateDispatchField('delivery_date', e.target.value)} />
            </div>
            <div>
              <Label>PO No</Label>
              <Input value={manualDispatch.po_no} onChange={e => updateDispatchField('po_no', e.target.value)} placeholder="e.g. 4500123456" />
            </div>

            {/* Row 7: SO Price + Net Rate */}
            <div>
              <Label>SO Price (₹)</Label>
              <Input type="number" min={0} step={0.01} value={manualDispatch.so_price} onChange={e => updateDispatchField('so_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Net Rate (₹)</Label>
              <Input type="number" min={0} step={0.01} value={manualDispatch.net_rate} onChange={e => updateDispatchField('net_rate', parseFloat(e.target.value) || 0)} />
            </div>

            {/* Row 8: Net Value + Discounts */}
            <div>
              <Label>Net Value (₹)</Label>
              <Input type="number" min={0} step={0.01} value={manualDispatch.net_value} onChange={e => updateDispatchField('net_value', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Trade Disc %</Label>
                <Input type="number" min={0} max={100} step={0.1} value={manualDispatch.trade_discount_pct} onChange={e => updateDispatchField('trade_discount_pct', parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <Label className="text-xs">Other Disc %</Label>
                <Input type="number" min={0} max={100} step={0.1} value={manualDispatch.other_discount_pct} onChange={e => updateDispatchField('other_discount_pct', parseFloat(e.target.value) || 0)} />
              </div>
            </div>

            {/* Row 9: State + City — auto-filled from customer profile */}
            <div>
              <Label>State (from customer profile)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualDispatch.state
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualDispatch.state || 'Auto-filled when code matches'}
              </div>
            </div>
            <div>
              <Label>City (from customer profile)</Label>
              <div
                className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm shadow-sm ${
                  manualDispatch.city
                    ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                    : 'bg-gray-50 border-input text-muted-foreground italic'
                }`}
              >
                {manualDispatch.city || 'Auto-filled when code matches'}
              </div>
            </div>

            {/* Row 10: Remarks (full width) */}
            <div className="md:col-span-2">
              <Label>Remarks</Label>
              <Textarea value={manualDispatch.remarks} onChange={e => updateDispatchField('remarks', e.target.value)} placeholder="Optional remarks..." rows={2} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setManualDispatchOpen(false)}>Cancel</Button>
            <Button onClick={handleManualDispatchSubmit} disabled={dispatchSubmitting}>
              {dispatchSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {editDispatchMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  {editDispatchMode ? <><Save size={16} className="mr-1" /> Save Changes</> : <><Plus size={16} className="mr-1" /> Create Dispatch</>}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={18} /> Confirm Delete
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === 'order'
                ? `Delete order ${deleteTarget.item?.order_no} (${deleteTarget.item?.trade_name}, ${deleteTarget.item?.units_ordered} units)?`
                : `Delete dispatch ${deleteTarget?.item?.plant_invoice || deleteTarget?.item?.dispatch_id} (${deleteTarget?.item?.customer_name}, ${deleteTarget?.item?.no_of_pieces} pcs)?`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <p className="text-amber-800">This record will be soft-deleted. It will no longer appear in reports or analytics but can be recovered if needed.</p>
          </div>

          {/* When the user deletes a line that has MORE than one sibling
              line — for orders this is lines sharing an order_no, for
              dispatches it's batch lines sharing a plant_invoice — surface
              the option to nuke the whole order / dispatch in one click.
              Single-line records show only the regular delete button. */}
          {(deleteTarget?.type === 'order' || deleteTarget?.type === 'dispatch') && (deleteTarget?.siblings || 1) > 1 ? (
            <DialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2 sm:items-stretch">
              <div className="text-[12px] text-gray-600 px-1 -mt-1">
                {deleteTarget?.type === 'order' ? 'Order ' : 'Invoice '}
                <span className="font-mono font-semibold">{deleteTarget?.type === 'order' ? deleteTarget.item?.order_no : deleteTarget.item?.plant_invoice}</span> has{' '}
                <span className="font-semibold">{deleteTarget.siblings} line items</span>. Choose what to delete:
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteConfirm(false)}
                  disabled={deleting}
                  className="justify-start"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} className="mr-2" />
                      Delete this line only ({deleteTarget.item?.trade_name})
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteConfirm(true)}
                  disabled={deleting}
                  className="justify-start bg-red-700 hover:bg-red-800"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} className="mr-2" />
                      {deleteTarget?.type === 'order'
                        ? `Delete entire order (${deleteTarget.siblings} lines)`
                        : `Delete entire dispatch (${deleteTarget.siblings} lines)`}
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }} disabled={deleting}>
                  Cancel
                </Button>
              </div>
            </DialogFooter>
          ) : (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleDeleteConfirm(false)} disabled={deleting}>
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-1" /> Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Discount Dialog */}
      <Dialog open={bulkDiscountOpen} onOpenChange={setBulkDiscountOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <IndianRupee size={18} /> Apply Discount to Order
            </DialogTitle>
            <DialogDescription>
              Apply a discount % to all lines of order <strong>{bulkDiscountOrderNo}</strong> ({bulkDiscountDivision}).
              You can edit individual lines afterwards if some items shouldn't have the discount.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {bulkDiscountBaseInfo.base_discount_pct > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="font-medium text-amber-800">Base Discount: {bulkDiscountBaseInfo.base_discount_pct}%</p>
                <p className="text-amber-600 text-xs mt-1">This customer has a base discount of {bulkDiscountBaseInfo.base_discount_pct}% on {bulkDiscountDivision}. Any additional discount entered below will be stacked on top.</p>
              </div>
            )}
            <div>
              <Label>{bulkDiscountBaseInfo.base_discount_pct > 0 ? 'Additional Discount %' : 'Discount %'}</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={bulkDiscountPct}
                onChange={e => setBulkDiscountPct(parseFloat(e.target.value) || 0)}
                placeholder="e.g. 10"
                className="text-lg font-bold"
                autoFocus
              />
            </div>
            {bulkDiscountBaseInfo.base_discount_pct > 0 && bulkDiscountPct > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <p className="font-medium">Effective Discount: {Math.min(bulkDiscountBaseInfo.base_discount_pct + bulkDiscountPct, 100)}%</p>
                <p className="text-xs mt-1">Base {bulkDiscountBaseInfo.base_discount_pct}% + Additional {bulkDiscountPct}% = {Math.min(bulkDiscountBaseInfo.base_discount_pct + bulkDiscountPct, 100)}% on ex-mill price</p>
              </div>
            )}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              {bulkDiscountBaseInfo.base_discount_pct > 0
                ? `This will apply ${Math.min(bulkDiscountBaseInfo.base_discount_pct + bulkDiscountPct, 100)}% effective discount (base ${bulkDiscountBaseInfo.base_discount_pct}% + additional ${bulkDiscountPct}%) on all lines and auto-calculate net prices.`
                : `This will set ${bulkDiscountPct}% discount on all lines and auto-calculate net prices. You can still edit individual lines to override.`
              }
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDiscountOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkDiscount} disabled={bulkDiscountSubmitting} className="bg-amber-600 hover:bg-amber-700">
              {bulkDiscountSubmitting ? 'Applying...' : `Apply ${bulkDiscountPct}% ${bulkDiscountBaseInfo.base_discount_pct > 0 ? 'Additional ' : ''}Discount`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer discount picker — choose which of the customer's orders get
          the discount, then apply to all lines of the selected orders. */}
      <Dialog open={!!custDiscount} onOpenChange={(o) => { if (!o) setCustDiscount(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <IndianRupee size={18} /> Apply discount — {custDiscount?.name}
            </DialogTitle>
            <DialogDescription>
              Pick the order(s) to discount ({custDiscount?.division}). The % applies to every line of each selected order; you can still edit individual lines afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{(custDiscount?.orders || []).length} order(s)</span>
              <div className="flex gap-2">
                <button className="underline hover:text-foreground"
                  onClick={() => setCustDiscountSel(Object.fromEntries((custDiscount?.orders || []).map(o => [o.order_no, true])))}>Select all</button>
                <button className="underline hover:text-foreground"
                  onClick={() => setCustDiscountSel({})}>Clear</button>
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto border rounded-lg divide-y">
              {(custDiscount?.orders || []).map((o) => (
                <label key={o.order_no} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={!!custDiscountSel[o.order_no]}
                    onCheckedChange={(v) => setCustDiscountSel(s => ({ ...s, [o.order_no]: !!v }))} />
                  <span className="font-mono flex-1">{o.order_no}</span>
                  <span className="text-muted-foreground">{fmtNum(o.units)} units · {o.lines} lines</span>
                </label>
              ))}
            </div>
            {custDiscountBase > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                Base discount {custDiscountBase}% for this customer — the % below stacks on top (effective {Math.min(custDiscountBase + (custDiscountPct || 0), 100)}%).
              </div>
            )}
            <div>
              <Label className="text-xs">{custDiscountBase > 0 ? 'Additional discount %' : 'Discount %'}</Label>
              <Input type="number" min={0} max={100} step={0.5} value={custDiscountPct}
                onChange={(e) => setCustDiscountPct(parseFloat(e.target.value) || 0)}
                placeholder="e.g. 10" className="text-lg font-bold mt-1" autoFocus />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCustDiscount(null)} disabled={custDiscountBusy}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleCustomerDiscount} disabled={custDiscountBusy}>
              {custDiscountBusy ? 'Applying…'
                : `Apply to ${Object.values(custDiscountSel).filter(Boolean).length} order(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge Dispatches Confirmation Dialog */}
      <Dialog open={purgeDialogOpen} onOpenChange={(open) => { setPurgeDialogOpen(open); if (!open) { setPurgeConfirmText(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">Clear Dispatches</DialogTitle>
            <DialogDescription>
              This will permanently delete dispatches. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Division to clear</Label>
              <Select value={purgeDivision} onValueChange={(v) => { setPurgeDivision(v); fetchPurgeCount(v); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select division..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-800">
                {purgeCount > 0
                  ? `This will permanently delete ${fmtNum(purgeCount)} dispatches${purgeDivision !== 'all' ? ` from ${purgeDivision}` : ''}.`
                  : 'No dispatches to delete.'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Type DELETE to confirm</Label>
              <Input
                value={purgeConfirmText}
                onChange={e => setPurgeConfirmText(e.target.value)}
                placeholder="Type DELETE..."
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handlePurge} disabled={purgeConfirmText !== 'DELETE' || purgeCount === 0 || purging}>
              {purging ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Purging...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-1" /> Delete {fmtNum(purgeCount)} Dispatches
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mix Authorisation dialog — log an informal bale order */}
      <Dialog open={mixDialogOpen} onOpenChange={setMixDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={18} className="text-indigo-600" /> Log Mix Bale Authorisation
            </DialogTitle>
            <DialogDescription>
              Informal order for mix-dispatch bales (shared via email/WhatsApp/call). System tracks bales and FIFO-matches incoming mix dispatches for this customer + fabric.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Division *</Label>
              <Select value={mixForm.division} onValueChange={v => setMixForm(f => ({ ...f, division: v, customer_code: '' }))}>
                <SelectTrigger><SelectValue placeholder="Select division..." /></SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fabric Type *</Label>
              <Select value={mixForm.fabric_type} onValueChange={v => setMixForm(f => ({ ...f, fabric_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shirting">Shirting</SelectItem>
                  <SelectItem value="suiting">Suiting</SelectItem>
                  <SelectItem value="combo_pack">Combo Pack (RTS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer Code *</Label>
              <Input
                list="mix-auth-customer-codes"
                value={mixForm.customer_code}
                onChange={e => {
                  const code = e.target.value;
                  const match = customerOptions.find(c => c.customer_code === code || String(c.customer_code).trim() === String(code).trim());
                  setMixForm(f => ({ ...f, customer_code: code }));
                  // Prime customer directory if needed — trigger via dialog's own effect below
                }}
                placeholder={mixForm.division ? 'Type or pick a code…' : 'Select division first'}
                disabled={!mixForm.division}
              />
              <datalist id="mix-auth-customer-codes">
                {customerOptions.map(c => (
                  <option key={c.customer_id || c.customer_code} value={c.customer_code}>
                    {c.name}{c.city ? ` — ${c.city}` : ''}
                  </option>
                ))}
              </datalist>
            </div>
            <div>
              <Label>Customer (auto)</Label>
              <div className={`flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm ${
                customerOptions.find(c => c.customer_code === mixForm.customer_code)
                  ? 'bg-green-50 border-green-200 text-green-900 font-medium'
                  : 'bg-gray-50 border-input text-muted-foreground italic'
              }`}>
                {customerOptions.find(c => c.customer_code === mixForm.customer_code)?.name || 'Auto-filled when code matches'}
              </div>
            </div>
            <div>
              <Label>Bales Authorised *</Label>
              <Input type="number" min={1} value={mixForm.bales_authorised} onChange={e => setMixForm(f => ({ ...f, bales_authorised: parseInt(e.target.value) || 1 }))} />
            </div>
            <div>
              <Label>Flat Rate (₹ / piece) *</Label>
              <Input type="number" min={0} step={0.01} value={mixForm.flat_rate} onChange={e => setMixForm(f => ({ ...f, flat_rate: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 299.00" />
            </div>
            <div>
              <Label>Communication Date *</Label>
              <Input type="date" value={mixForm.authorised_on} onChange={e => {
                const d = e.target.value;
                // Auto-shift expiry by 60 days when comms date changes
                let exp = mixForm.expires_on;
                try {
                  const dt = new Date(d);
                  dt.setDate(dt.getDate() + 60);
                  exp = dt.toISOString().slice(0, 10);
                } catch {}
                setMixForm(f => ({ ...f, authorised_on: d, expires_on: exp }));
              }} />
            </div>
            <div>
              <Label>Expires On (default +60 days)</Label>
              <Input type="date" value={mixForm.expires_on} onChange={e => setMixForm(f => ({ ...f, expires_on: e.target.value }))} />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={mixForm.source} onValueChange={v => setMixForm(f => ({ ...f, source: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Reference / Message Text</Label>
              <Textarea value={mixForm.reference_text} onChange={e => setMixForm(f => ({ ...f, reference_text: e.target.value }))} placeholder="Paste the email / WhatsApp text for audit trail…" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMixDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveMixAuth} disabled={savingMixAuth} className="bg-indigo-600 hover:bg-indigo-700">
              {savingMixAuth ? 'Saving…' : 'Save Authorisation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign fabric to mix dispatches — admin/accounts tag rows whose fabric
          type couldn't be inferred from text or auto-resolved via a single open
          auth. Once tagged, the dispatch flows into the correct Mix Bale
          Shirting / Suiting / Combo Pack bucket in the product breakdown. */}
      <Dialog open={mixFabricDialogOpen} onOpenChange={setMixFabricDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={18} className="text-amber-600" /> Assign Fabric to Mix Dispatches
            </DialogTitle>
            <DialogDescription>
              These dispatches are detected as mix bales but the fabric type (shirting / suiting / combo pack) isn't on the row and couldn't be auto-resolved. Pick a fabric per row and save — the dispatch will attach to the matching authorisation and flow into Mix Bale revenue buckets.
            </DialogDescription>
          </DialogHeader>
          {mixNeedsFabric.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nothing pending. All mix dispatches are classified.</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-[10px]">Date</TableHead>
                    <TableHead className="text-[10px]">Division</TableHead>
                    <TableHead className="text-[10px]">Customer</TableHead>
                    <TableHead className="text-[10px]">Invoice / Qty</TableHead>
                    <TableHead className="text-[10px]">Fabric *</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mixNeedsFabric.map(d => (
                    <TableRow key={d.dispatch_id} className="text-xs">
                      <TableCell className="py-1.5 whitespace-nowrap">{(d.delivery_date || '').slice(0, 10) || '-'}</TableCell>
                      <TableCell className="py-1.5 whitespace-nowrap">{d.division || '-'}</TableCell>
                      <TableCell className="py-1.5">
                        <div className="font-medium">{d.customer_name || '-'}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{d.customer_code}</div>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div className="font-mono text-[11px]">
                          {d.plant_invoice || '-'}
                          {blockedDispatchIds.has(d.dispatch_id) && (
                            <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 text-[9px] font-sans font-semibold align-middle" title={blockedDispatchIds.get(d.dispatch_id)}>Dispatch after block</span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {d.no_of_pieces || 0} pcs · {d.batch_qty_metres ? `${d.batch_qty_metres} m` : ''}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Select
                          value={mixFabricChoices[d.dispatch_id] || ''}
                          onValueChange={v => setMixFabricChoices(prev => ({ ...prev, [d.dispatch_id]: v }))}
                        >
                          <SelectTrigger className="h-8 w-[140px] text-xs">
                            <SelectValue placeholder={d.suggested_fabric ? `Suggested: ${d.suggested_fabric}` : 'Pick…'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shirting">Shirting</SelectItem>
                            <SelectItem value="suiting">Suiting</SelectItem>
                            <SelectItem value="combo_pack">Combo Pack (RTS)</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMixFabricDialogOpen(false)}>Close</Button>
            <Button
              onClick={assignMixFabrics}
              disabled={mixFabricSaving || Object.values(mixFabricChoices).filter(Boolean).length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {mixFabricSaving ? 'Saving…' : `Assign ${Object.values(mixFabricChoices).filter(Boolean).length} Row(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orders / Dispatches export dialog — pick the scope BEFORE building the
          file, so a PDF is never attempted over the whole dataset (which used
          to fail with a 413 only after the attempt). */}
      <Dialog open={!!odExportKind} onOpenChange={(o) => { if (!o) setOdExportKind(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download size={16} className="text-indigo-600" />
              Download {odExportKind === 'dispatches' ? 'dispatches' : 'orders'}
            </DialogTitle>
            <DialogDescription>
              Pick the scope first. Division follows the page filter
              ({filterDivision && filterDivision !== 'all' ? filterDivision : 'All divisions'}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-16">Duration</span>
              <Select value={odExMonth} onValueChange={setOdExMonth}>
                <SelectTrigger className="h-9 w-[170px] text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months (full history)</SelectItem>
                  {(data?.filters?.months || []).filter(m => m).map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-16">Region</span>
              <Select value={odExState} onValueChange={setOdExState}>
                <SelectTrigger className="h-9 w-[170px] text-sm"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {(data?.filters?.states || []).filter(s => s).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-16">Customer</span>
              <Select value={odExCustomer} onValueChange={setOdExCustomer}>
                <SelectTrigger className="h-9 w-[260px] text-sm"><SelectValue placeholder="Customer" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="all">All customers</SelectItem>
                  {(data?.filters?.all_customers || data?.filters?.customers || [])
                    .filter(c => c.name && c.code)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((c, i) => (
                      <SelectItem key={c.code || `c-${i}`} value={c.code}>
                        {c.name} ({c.code}){c.location ? ` · ${c.location}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <p className={`text-xs rounded p-2 border ${odExportScoped() ? 'text-muted-foreground bg-slate-50 border-slate-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
              {odExportScoped() ? (
                <>Scope: {filterDivision && filterDivision !== 'all' ? filterDivision : 'All divisions'}
                  {odExMonth !== 'all' && <> · {odExMonth}</>}
                  {odExState !== 'all' && <> · {odExState}</>}
                  {odExCustomer !== 'all' && <> · selected customer</>}
                </>
              ) : (
                <>No duration/region/customer picked — this exports the <strong>full history</strong>. PDF is capped
                  (large sets are rejected); pick a month or region above, or use Excel which handles large data.</>
              )}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOdExportKind(null)} disabled={exporting}>Cancel</Button>
            <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              disabled={exporting}
              onClick={() => (odExportKind === 'dispatches' ? handleDispatchesExport('excel') : handleOrdersExport('excel'))}>
              <Download size={14} className="mr-1" /> {exporting ? 'Building…' : 'Excel'}
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={exporting}
              onClick={() => (odExportKind === 'dispatches' ? handleDispatchesExport('pdf') : handleOrdersExport('pdf'))}>
              <Download size={14} className="mr-1" /> {exporting ? 'Building…' : 'PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Format mismatch dialog — shown when an upload is blocked by the format lock */}
      <Dialog open={!!formatMismatch} onOpenChange={(open) => { if (!open) setFormatMismatch(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Upload Blocked — Format Mismatch
            </DialogTitle>
            <DialogDescription>
              The file <span className="font-mono font-semibold">{formatMismatch?.file_name}</span> does not match the accepted <strong>{formatMismatch?.upload_type_label || formatMismatch?.upload_type}</strong> format.
              {formatMismatch?.reference_file && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Expected format reference: <span className="font-mono">{formatMismatch.reference_file}</span>
                  {formatMismatch?.reference_set_at && <> · set {new Date(formatMismatch.reference_set_at).toLocaleDateString('en-IN')}</>}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {formatMismatch?.missing_columns?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
              <p className="text-xs font-semibold text-red-700 mb-1.5">Missing required columns ({formatMismatch.missing_columns.length})</p>
              <div className="flex flex-wrap gap-1">
                {formatMismatch.missing_columns.map((c, i) => (
                  <span key={i} className="text-[11px] bg-white border border-red-300 text-red-700 rounded px-1.5 py-0.5 font-mono">{c}</span>
                ))}
              </div>
            </div>
          )}
          {formatMismatch?.extra_columns?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-2">
              <p className="text-xs font-semibold text-amber-700 mb-1.5">Unexpected columns in file ({formatMismatch.extra_columns.length})</p>
              <div className="flex flex-wrap gap-1">
                {formatMismatch.extra_columns.map((c, i) => (
                  <span key={i} className="text-[10px] bg-white border border-amber-300 text-amber-800 rounded px-1.5 py-0.5 font-mono">{c}</span>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            If this file is correct and the principal has updated their template, an admin can refresh the reference file from <span className="font-semibold">Settings → Format Locks</span>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormatMismatch(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate-order decision dialog — the upload wrote NOTHING; the user
          chooses whether the file's quantities replace the stored orders or
          the existing orders stay untouched. */}
      <Dialog open={!!dupConflict && !retailerPrompt} onOpenChange={(open) => { if (!open && !retailerPrompt) setDupConflict(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Order numbers already in the system
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-semibold">{dupConflict?.fileName}</span> contains{' '}
              <strong>{dupConflict?.conflicts?.length}</strong> order number(s) that already exist in{' '}
              <strong>{dupConflict?.division}</strong>. Nothing has been imported yet — compare the
              quantities and choose what to do.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2">Order No</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2 text-right">Existing qty</th>
                  <th className="px-3 py-2 text-right">In this file</th>
                </tr>
              </thead>
              <tbody>
                {(dupConflict?.conflicts || []).map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-1.5 font-mono">{c.order_no}</td>
                    <td className="px-3 py-1.5 max-w-[180px] truncate" title={c.shop_name}>{c.shop_name || '—'}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${c.existing_units !== c.incoming_units ? 'text-red-600' : ''}`}>
                      {c.existing_units?.toLocaleString('en-IN')} <span className="text-muted-foreground font-normal">({c.existing_lines} lines)</span>
                    </td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${c.existing_units !== c.incoming_units ? 'text-emerald-700' : ''}`}>
                      {c.incoming_units?.toLocaleString('en-IN')} <span className="text-muted-foreground font-normal">({c.incoming_lines} lines)</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">
            <strong>Override</strong> replaces the stored quantities with this file's (other orders in the file import normally).{' '}
            <strong>Keep existing</strong> leaves the listed orders untouched and imports only the new order numbers from the file.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDupConflict(null)} disabled={uploading}>Cancel — import nothing</Button>
            <Button
              variant="outline"
              className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
              onClick={() => resolveDupConflict('skip')}
              disabled={uploading}
            >
              Keep existing, import new only
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => resolveDupConflict('override')}
              disabled={uploading}
            >
              {uploading ? 'Working…' : 'Override with this file'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
